import React, { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import classNames from "classnames";
import { Card, Col, Row, Spinner, Form, Badge, Tab, Tabs, Button, Modal, Alert } from "react-bootstrap";
import { useNavigate } from "react-router-dom";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faRobot,
  faBrain,
  faHistory,
  faChartLine,
  faClock,
  faEnvelope,
  faEye,
  faLightbulb,
  faHandsHelping,
  faHome,
  faDollarSign,
  faMapMarkerAlt,
  faHeart,
  faCheckCircle,
  faExclamationTriangle,
  faCircle,
  faRefresh,
  faPaperPlane,
  faPlus,
  faComments,
  faUser,
  faCalendar,
  faPhone,
  faEdit,
  faCopy,
  faTrash,
  faSearch,
  faFilter,
  faSort,
  faDownload,
  faShare,
  faBookmark,
  faTag,
  faFlag,
  faStar,
  faArrowUp,
  faArrowDown,
  faArrowRight,
  faExclamation,
  faInfo,
  faWarning,
  faCheck,
  faTimes,
  faQuestionCircle,
  faInfoCircle,
  faExclamationCircle,
  faTimesCircle,
  faCheckSquare,
  faSquare,
  faCaretDown,
  faCaretUp,
  faCaretRight,
  faCaretLeft,
  faAngleDown,
  faAngleUp,
  faAngleRight,
  faAngleLeft,
  faChevronDown,
  faChevronUp,
  faChevronRight,
  faChevronLeft,
  faEllipsisV,
  faEllipsisH,
  faBars,
  faGrip,
  faGripVertical,
  faGripHorizontal,
  faExpand,
  faCompress,
  faExpandArrowsAlt,
  faCompressArrowsAlt,
  faArrowsAlt,
  faArrowsAltH,
  faArrowsAltV,
  faSearchPlus,
  faSearchMinus,
  faCrosshairs,
  faBullseye,
  faLocationArrow,
  faLocationCrosshairs,
  faLocationDot,
  faLocationPin,
  faMap,
  faMapPin,
  faMapMarker,
  faMapLocation,
  faMapLocationDot,
  faGlobe,
  faGlobeAmericas,
  faGlobeAsia,
  faGlobeEurope,
  faEarth,
  faEarthAmericas,
  faEarthAsia,
  faEarthEurope,
  faEarthOceania,
  faBolt,
  faTasks,
  faMessage,
  faPlay,
  faSpinner,
  faSave,
  faBed,
  faBath,
  faRuler,
  faRulerCombined,
  faCar
} from "@fortawesome/free-solid-svg-icons";
import axiosInstance from "helpers/api";
import { formatErrorForReact } from "helpers/utils";
import { useAtom } from "jotai";
import { user_info } from "jotai/UserJotaiInstance";
import toast from "react-hot-toast";
import { UserInfo } from 'interfaces/user';

interface Message {
  id: number;
  text: string;
  sender: "user" | "assistant";
  timestamp?: Date;
  type?: "text" | "insight" | "analysis";
  lastUpdated?: number; // For forcing React re-renders during streaming
}

interface UpcomingTask {
  id: string;
  task: string;
  priority: string;
  due_date: string;
  status: string;
}

interface RecommendedAction {
  id: string;
  priority: 'URGENT' | 'HIGH' | 'MEDIUM' | 'LOW';
  title: string;
  description: string;
  action: string;
  icon: string;
  color: string;
}

// Removed old BuyerProfile interface - using SimpleBuyerProfile instead

interface KeyInsight {
  icon: string;
  text: string;
  type: 'property' | 'budget' | 'timeline' | 'location' | 'motivation';
}

interface CommunicationItem {
  id: string;
  type: string;
  title: string;
  description: string;
  timestamp: string;
  timeAgo: string;
  color: string;
}

interface PropertyPreference {
  key: string;
  label: string;
  value: string;
  badge_color: string;
  description: string;
}

interface MarketStat {
  label: string;
  value: string;
  description: string;
  trend?: 'up' | 'down' | 'neutral';
  color: string;
}

interface LeadAiAssistantProps {
  leadDetails: any;
  className?: string;
}

// Add buyer profile interface
interface SimpleBuyerProfile {
  id?: string;
  lead_id: string;
  buying_timeframe?: '0-3 months' | '3-6 months' | '6-12 months' | '12+ months' | 'Not specified';
  pre_approved?: boolean;
  pre_approval_amount?: number;
  first_time_buyer?: boolean;
  motivation?: string;
  desired_move_in_date?: string;
  max_commute_time?: number;
  work_location?: string;
  buyer_stage?: 'researching' | 'viewing' | 'offering' | 'under_contract' | 'closed' | 'paused' | 'lost';
  property_preferences?: {
    property_type?: string;
    bedrooms?: number;
    bathrooms?: number;
    square_feet_min?: number;
    square_feet_max?: number;
    budget_min?: number;
    budget_max?: number;
    must_have_features?: string[];
  };
  location_preferences?: {
    preferred_cities?: string[];
    preferred_areas?: string[];
    deal_breaker_areas?: string[];
  };
  lifestyle_preferences?: {
    amenities?: string[];
    deal_breakers?: string[];
    yard_requirements?: string;
    parking_requirements?: string;
  };
}

const LeadAiAssistant: React.FC<LeadAiAssistantProps> = ({ leadDetails, className }) => {
  const navigate = useNavigate();
  
  // State management
  const [activeTab, setActiveTab] = useState("insights");
  const [showEmbeddedChat, setShowEmbeddedChat] = useState(false);
  const [showProfileBuilder, setShowProfileBuilder] = useState(false);
  
  // Guided Workflow State
  const [guidedCurrentStep, setGuidedCurrentStep] = useState(0);
  const [guidedAddress, setGuidedAddress] = useState('');
  const [guidedAddressSuggestions, setGuidedAddressSuggestions] = useState<any[]>([]);
  const [guidedSelectedAddress, setGuidedSelectedAddress] = useState<any>(null);
  const [showGuidedConfirmDialog, setShowGuidedConfirmDialog] = useState(false);
  const [isGuidedAnalyzing, setIsGuidedAnalyzing] = useState(false);
  const [guidedAnalysisProgress, setGuidedAnalysisProgress] = useState(0);
  const [guidedAnalysisStep, setGuidedAnalysisStep] = useState('');
  const [showGuidedSuggestions, setShowGuidedSuggestions] = useState(false);
  const [guidedAnalysisComplete, setGuidedAnalysisComplete] = useState(false);
  const [guidedAnalysisResults, setGuidedAnalysisResults] = useState<any>(null);
  const [isGuidedLoadingSuggestions, setIsGuidedLoadingSuggestions] = useState(false);
  const [showReportModal, setShowReportModal] = useState(false);
  const guidedAddressTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const [messages, setMessages] = useState<Message[]>([]);
  const [inputMessage, setInputMessage] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [conversations, setConversations] = useState<any[]>([]);
  const [currentConversationId, setCurrentConversationId] = useState<string | null>(null);
  const [loadingConversations, setLoadingConversations] = useState(false);
  const [loadingInsights, setLoadingInsights] = useState(false);
  const [keyInsights, setKeyInsights] = useState<KeyInsight[]>([]);
  const [recentCommunications, setRecentCommunications] = useState<CommunicationItem[]>([]);
  const [propertyPreferences, setPropertyPreferences] = useState<PropertyPreference[]>([]);
  const [marketIntelligence, setMarketIntelligence] = useState<MarketStat[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [conversationId, setConversationId] = useState<string | null>(null);
  const [showNewTopicModal, setShowNewTopicModal] = useState(false);
  const [newTopicTitle, setNewTopicTitle] = useState("");
  const [selectedHistoryConversation, setSelectedHistoryConversation] = useState<string | null>(null);
  const [historyMessages, setHistoryMessages] = useState<Message[]>([]);
  const [loadingHistory, setLoadingHistory] = useState(false);
  const [loadingCommunications, setLoadingCommunications] = useState(false);
  const [loadingPreferences, setLoadingPreferences] = useState(false);
  const [loadingMarket, setLoadingMarket] = useState(false);
  const [upcomingTasks, setUpcomingTasks] = useState<UpcomingTask[]>([]);
  const [buyerProfile, setBuyerProfile] = useState<SimpleBuyerProfile | null>(null);
  const [insightsLoading, setInsightsLoading] = useState(false);
  const chatContainerRef = useRef<HTMLDivElement>(null);
  const [loadingBuyerProfile, setLoadingBuyerProfile] = useState(false);
  const [savingBuyerProfile, setSavingBuyerProfile] = useState(false);
  const [editingBuyerProfile, setEditingBuyerProfile] = useState(false);

  // Add new state for dynamic lead metrics
  const [leadMetrics, setLeadMetrics] = useState<{
    leadScore: number | null;
    daysActive: number | null;
    responseRate: number | null;
    aiConfidence: number | null;
    loading: boolean;
  }>({
    leadScore: null,
    daysActive: null,
    responseRate: null,
    aiConfidence: null,
    loading: true
  });

  // Add state for AI Lead Intelligence
  interface AIIntelligenceMetric {
    score: number | null;
    label: string;
    description: string;
  }
  
  const [aiIntelligence, setAiIntelligence] = useState<{
    buyingIntent: AIIntelligenceMetric;
    engagementQuality: AIIntelligenceMetric;
    conversionProbability: AIIntelligenceMetric;
    loading: boolean;
  }>({
    buyingIntent: { score: null, label: 'Not Available', description: 'Loading...' },
    engagementQuality: { score: null, label: 'Not Available', description: 'Loading...' },
    conversionProbability: { score: null, label: 'Not Available', description: 'Loading...' },
    loading: true
  });

  // Add state for Recommended Actions
  const [recommendedActions, setRecommendedActions] = useState<{
    actions: RecommendedAction[];
    loading: boolean;
  }>({
    actions: [],
    loading: true
  });

  const [userInfo] = useAtom<UserInfo>(user_info);
  const isAuthorized = userInfo?.payment_status !== "Cancelled";

  const scrollToBottom = () => {
    if (chatContainerRef.current) {
      chatContainerRef.current.scrollTop = chatContainerRef.current.scrollHeight;
    }
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  // Real-time trigger system for lead_ai_context updates
  const [realTimeNotifications, setRealTimeNotifications] = useState<{
    hasNewRecommendations: boolean;
    hasContextUpdate: boolean;
    lastUpdateTime: Date | null;
    updateTrigger: number;
  }>({
    hasNewRecommendations: false,
    hasContextUpdate: false,
    lastUpdateTime: null,
    updateTrigger: 0
  });

  // Real-time context refresh function
  const triggerContextRefresh = async (reason: string = 'subscriber_activity', activityData?: any) => {
    console.log(`🔄 [REAL-TIME TRIGGER] Context refresh triggered by: ${reason}`);
    
    try {
      // Call the backend API to update AI context
      const response = await axiosInstance.post(`/leads/${leadDetails.id}/ai-context/refresh`, {
        activityType: reason,
        activityData: activityData || {},
        reason: reason
      });

      console.log('🎯 [AI CONTEXT API] Response:', response.data);

      // Update the trigger counter to force re-evaluation
      setRealTimeNotifications(prev => ({
        ...prev,
        updateTrigger: prev.updateTrigger + 1,
        lastUpdateTime: new Date()
      }));

      // Refresh all AI context data in memory
      try {
        await Promise.all([
          loadLeadMetrics(),
          loadAiIntelligence(),
          loadRecommendedActions(),
          loadKeyInsights(),
          loadRecentCommunications(),
          loadPropertyPreferences(),
          loadMarketIntelligence()
        ]);
        console.log('✅ [AI CONTEXT REFRESH] All data refreshed successfully');
      } catch (refreshError) {
        console.error('⚠️ [AI CONTEXT REFRESH] Some data refresh failed:', refreshError);
        // Continue anyway - the main API call succeeded
      }

      // Show notification in AI Lead Intelligence panel
      setRealTimeNotifications(prev => ({
        ...prev,
        hasNewRecommendations: true,
        hasContextUpdate: true
      }));

      // Auto-clear notification after 10 seconds
      setTimeout(() => {
        setRealTimeNotifications(prev => ({
          ...prev,
          hasNewRecommendations: false,
          hasContextUpdate: false
        }));
      }, 10000);

      toast.success('🤖 AI Lead Intelligence Updated', {
        duration: 4000,
        position: 'top-right'
      });

    } catch (error: any) {
      console.error('❌ [AI CONTEXT REFRESH] Failed:', error);
      console.error('❌ [AI CONTEXT REFRESH] Error details:', {
        message: error.message,
        status: error.response?.status,
        statusText: error.response?.statusText,
        data: error.response?.data
      });
      
      const errorMessage = error.response?.data?.message || error.message || 'Unknown error occurred';
      toast.error(`Failed to refresh AI context: ${errorMessage}`, {
        duration: 5000,
        position: 'top-right'
      });
    }
  };

  // Initialize conversation when leadDetails are available
  useEffect(() => {
    if (leadDetails?.id) {
          loadLeadConversations();
    loadUpcomingTasks();
    loadBuyerProfile();
    loadLeadMetrics();
    loadAiIntelligence(); // ✅ Already here - good!
    loadRecommendedActions();
    loadKeyInsights();
    loadRecentCommunications();
    // loadPropertyPreferences(); // ✅ Removed - now triggered by buyerProfile useEffect
    loadMarketIntelligence();
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [leadDetails?.id]);

  // Real-time trigger listener - simulates subscriber activity detection
  useEffect(() => {
    if (!leadDetails?.id) return;

    // Set up real-time trigger interval (every 30 seconds for demo)
    const triggerInterval = setInterval(() => {
      // In production, this would be triggered by actual subscriber activity
      // For now, we'll trigger based on lead activity or time-based updates
      const shouldTrigger = Math.random() > 0.7; // 30% chance every 30 seconds
      
      // if (shouldTrigger) {
      //   triggerContextRefresh('periodic_evaluation');
      // }
    }, 30000);

    return () => clearInterval(triggerInterval);
  }, [leadDetails?.id]);

  // DEBUG: Monitor buyerProfile changes and refresh property preferences
  useEffect(() => {
    console.log('🔍 [DEBUG STATE] buyerProfile state changed:', buyerProfile);
    if (buyerProfile) {
      console.log('🔍 [DEBUG STATE] buyerProfile.id:', buyerProfile.id);
      console.log('🔍 [DEBUG STATE] buyerProfile.lead_id:', buyerProfile.lead_id);
      console.log('🔍 [DEBUG STATE] ID is present:', !!buyerProfile.id);
      
      // Refresh property preferences when buyer profile is loaded or updated
      loadPropertyPreferences();
    } else {
      console.log('🔍 [DEBUG STATE] buyerProfile is null/undefined');
    }
  }, [buyerProfile]);

  const loadLeadConversations = async () => {
    try {
      // Get ONLY conversations for this specific lead with enhanced security
      const response = await axiosInstance.get(`/chat/conversations`, {
        params: {
          lead_id: leadDetails.id,
          user_id: userInfo?.id || 'current-user', // Include user_id for data isolation
          agent_type: 'lead-analysis-ai',
          filter_by_lead: true
        }
      });

      let leadConversations = response.data.conversations || [];
      
      // Double-check client-side filtering to ensure data isolation
      leadConversations = leadConversations.filter((conv: any) => {
        // Only include conversations that:
        // 1. Have the correct lead_id
        // 2. Are specifically for lead-analysis-ai
        // 3. Are not general chatbot conversations
        return conv.lead_id === leadDetails.id && 
               conv.agent_type === 'lead-analysis-ai' &&
               !conv.is_general_chat;
      });

      console.log(`Loaded ${leadConversations.length} conversations for lead ${leadDetails.id} (${leadDetails.lead_first_name} ${leadDetails.lead_last_name})`);
      setConversations(leadConversations);

      if (leadConversations.length > 0) {
        // Load the most recent conversation
        const latestConversation = leadConversations[0];
        await switchToConversation(latestConversation.id);
      } else {
        // Create first conversation if none exist
        await createNewConversation();
      }
    } catch (error: any) {
      console.error('Error loading lead conversations:', error);
      // Fallback to creating new conversation
      await createNewConversation();
    }
  };

  const createNewConversation = async (customTitle?: string) => {
    try {
      // If no conversations exist, create the main conversation
      const isMainConversation = conversations.length === 0;
      const title = isMainConversation 
        ? `Lead Analysis: ${leadDetails.lead_first_name} ${leadDetails.lead_last_name}`
        : customTitle || `New Topic - ${leadDetails.lead_first_name} ${leadDetails.lead_last_name}`;

      const response = await axiosInstance.post('/chat/conversations', {
        agent_type: 'lead-analysis-ai',
        title: title,
        lead_id: leadDetails.id,
        user_id: userInfo?.id || 'current-user', // Include user_id for security
        // Explicitly mark as lead-specific conversation
        is_lead_specific: true,
        metadata: {
          lead_id: leadDetails.id, // 🔥 CRITICAL: Store lead_id in metadata for backend linking
          lead_name: `${leadDetails.lead_first_name} ${leadDetails.lead_last_name}`,
          lead_email: leadDetails.lead_email,
          lead_type: leadDetails.lead_type?.lead_type || leadDetails.lead_type,
          conversation_type: 'lead_analysis',
          created_for_buyer_profile: isMainConversation
        },
        customer_context: {
          lead_name: `${leadDetails.lead_first_name} ${leadDetails.lead_last_name}`,
          lead_email: leadDetails.lead_email,
          lead_type: leadDetails.lead_type?.lead_type || leadDetails.lead_type
        }
      });

      const newConversation = response.data.conversation;
      setConversations(prev => [newConversation, ...prev]);
      setConversationId(newConversation.id);
      
      // Initialize with welcome message
      const welcomeMessage: Message = {
        id: 1,
        text: `🏡 Hello! I'm your **Buyer Profile AI Assistant** for **${leadDetails.lead_first_name} ${leadDetails.lead_last_name}**. I specialize in:\n\n• **Building comprehensive buyer profiles**\n• **Understanding property preferences & budget**\n• **Identifying motivation & timeline**\n• **Capturing location & lifestyle needs**\n\nLet's build ${leadDetails.lead_first_name}'s complete buyer profile! What would you like to start with?`,
        sender: "assistant",
        timestamp: new Date(),
        type: "insight"
      };

      setMessages([welcomeMessage]);
      
      // Initialize chat with welcome message if no messages exist
      if (messages.length === 0) {
        const welcomeMessage: Message = {
          id: Date.now(),
          text: `👋 Hi! I'm your AI Assistant for ${leadDetails?.lead_first_name || 'this lead'}.\n\n**I can help you with:**\n• Profile building and lead qualification\n• Property recommendations and market insights\n• Communication strategies and next steps\n• Lead scoring and conversion optimization\n\n**Let's start building their profile!** What would you like to know about ${leadDetails?.lead_first_name || 'this lead'}?`,
          sender: "assistant",
          timestamp: new Date(),
          type: "insight"
        };
        setMessages([welcomeMessage]);
        
        // Add quick action suggestions after a brief delay
        setTimeout(() => {
          const quickActionsMessage: Message = {
            id: Date.now() + 1,
            text: `**Quick Actions:**\n\n🏠 **"What are their property preferences?"**\n💰 **"What's their budget range?"**\n📅 **"What's their buying timeline?"**\n📍 **"What areas are they interested in?"**\n🎯 **"What's their motivation to buy?"**\n\nJust click on any question above or ask your own!`,
            sender: "assistant",
            timestamp: new Date(),
            type: "text"
          };
          setMessages(prev => [...prev, quickActionsMessage]);
        }, 1000);
      }
    } catch (error: any) {
      console.error('Error creating new conversation:', error);
      toast.error('Failed to create new conversation');
    }
  };

  const switchToConversation = async (convId: string) => {
    try {
      setConversationId(convId);
      
      // Load conversation messages with user verification
      const response = await axiosInstance.get(`/chat/conversations/${convId}/messages`, {
        params: {
          user_id: userInfo?.id || 'current-user' // Include user_id for security verification
        }
      });
      if (response.data.messages?.length > 0) {
        const formattedMessages: Message[] = response.data.messages.map((msg: any, index: number) => ({
          id: index + 1,
          text: msg.content,
          sender: msg.role === "user" ? "user" : "assistant",
          timestamp: new Date(msg.timestamp),
          type: msg.role === "assistant" ? "insight" : "text"
        }));
        setMessages(formattedMessages);
      } else {
        // Empty conversation, show welcome message
        const welcomeMessage: Message = {
          id: 1,
          text: `🏡 Hello! I'm your **Buyer Profile AI Assistant** for **${leadDetails.lead_first_name} ${leadDetails.lead_last_name}**. I specialize in:\n\n• **Building comprehensive buyer profiles**\n• **Understanding property preferences & budget**\n• **Identifying motivation & timeline**\n• **Capturing location & lifestyle needs**\n\nLet's build ${leadDetails.lead_first_name}'s complete buyer profile! What would you like to start with?`,
          sender: "assistant",
          timestamp: new Date(),
          type: "insight"
        };
        setMessages([welcomeMessage]);
      }
    } catch (error: any) {
      console.error('Error switching conversation:', error);
      toast.error('Failed to load conversation');
    }
  };

  const loadConversationHistory = async (convId: string) => {
    try {
      console.log(`Loading conversation history for conversation ID: ${convId}`);
      setLoadingHistory(true);
      setSelectedHistoryConversation(convId);
      setHistoryMessages([]); // Clear previous messages
      
      // Load conversation messages for history viewing (not for active chat)
      const response = await axiosInstance.get(`/chat/conversations/${convId}/messages`, {
        params: {
          user_id: userInfo?.id || 'current-user' // Include user_id for security verification
        }
      });
      console.log('Conversation history response:', response.data);
      
      if (response.data.messages?.length > 0) {
        const formattedMessages: Message[] = response.data.messages.map((msg: any, index: number) => ({
          id: index + 1,
          text: msg.content,
          sender: msg.role === "user" ? "user" : "assistant",
          timestamp: new Date(msg.timestamp),
          type: msg.role === "assistant" ? "insight" : "text"
        }));
        console.log(`Formatted ${formattedMessages.length} messages for history display`);
        setHistoryMessages(formattedMessages);
      } else {
        console.log('No messages found in conversation history');
        setHistoryMessages([]);
      }
    } catch (error: any) {
      console.error('Error loading conversation history:', error);
      console.error('Error details:', error.response?.data);
      toast.error(`Failed to load conversation history: ${error.response?.data?.error || error.message}`);
      // Reset state on error
      setSelectedHistoryConversation(null);
      setHistoryMessages([]);
    } finally {
      setLoadingHistory(false);
    }
  };

  const loadUpcomingTasks = async () => {
    try {
      setInsightsLoading(true);
      const response = await axiosInstance.get(`/leads/${leadDetails?.id}/activities?activity_type=task`);
      
      if (response.data?.data?.tasks) {
        const now = new Date();
        const nextWeek = new Date(now.getTime() + 7 * 24 * 60 * 60 * 1000);
        
        const upcoming = response.data.data.tasks.filter((task: any) => 
          task.status !== "Completed" && 
          new Date(task.due_date) <= nextWeek &&
          new Date(task.due_date) >= now
        ).slice(0, 3);
        
        setUpcomingTasks(upcoming);
      } else if (response.data?.data) {
        // Handle case where tasks might be in a different structure
        const tasks = Array.isArray(response.data.data) ? response.data.data : [];
        const now = new Date();
        const nextWeek = new Date(now.getTime() + 7 * 24 * 60 * 60 * 1000);
        
        const upcoming = tasks.filter((task: any) => 
          task.status !== "Completed" && 
          new Date(task.due_date) <= nextWeek &&
          new Date(task.due_date) >= now
        ).slice(0, 3);
        
        setUpcomingTasks(upcoming);
      }
    } catch (error) {
      console.error("Error loading upcoming tasks:", error);
      setUpcomingTasks([]);
    } finally {
      setInsightsLoading(false);
    }
  };

  const loadBuyerProfile = async () => {
    try {
      setLoadingBuyerProfile(true);
      
      console.log('🔄 Loading buyer profile for lead:', leadDetails?.id);
      console.log('🔄 Lead details available:', !!leadDetails);
      console.log('🔄 Lead ID available:', leadDetails?.id);
      
      if (!leadDetails?.id) {
        console.log('⚠️ No lead ID available, skipping buyer profile load');
        return;
      }
      
      const response = await axiosInstance.get(`/buyer-profiles/by-lead/${leadDetails?.id}`);
      
      console.log('📥 Load response:', response.data);
      console.log('🔍 [DEBUG LOAD] Full response structure:', JSON.stringify(response.data, null, 2));
      
      if (response.data?.status === 'success' && response.data?.buyer_profile) {
        const profile = response.data.buyer_profile;
        console.log('✅ Found existing buyer profile with ID:', profile.id);
        console.log('🔍 [DEBUG LOAD] Full profile from server:', JSON.stringify(profile, null, 2));
        console.log('🔍 [DEBUG LOAD] Profile ID type:', typeof profile.id);
        console.log('🔍 [DEBUG LOAD] Profile ID value:', profile.id);
        
        setBuyerProfile({
          id: profile.id,
          lead_id: profile.lead_id,
          buying_timeframe: profile.buying_timeframe || '3-6 months',
          pre_approved: profile.pre_approved || false,
          pre_approval_amount: profile.pre_approval_amount || 0,
          first_time_buyer: profile.first_time_buyer || false,
          motivation: profile.motivation || '',
          buyer_stage: profile.buyer_stage || 'researching',
          property_preferences: {
            property_type: profile.property_preferences?.property_type || profile.property_type || '',
            bedrooms: profile.property_preferences?.bedrooms || profile.bedrooms,
            bathrooms: profile.property_preferences?.bathrooms || profile.bathrooms,
            square_feet_min: profile.property_preferences?.square_feet_min || profile.square_feet_min,
            square_feet_max: profile.property_preferences?.square_feet_max || profile.square_feet_max,
            budget_min: profile.property_preferences?.budget_min || profile.budget_min,
            budget_max: profile.property_preferences?.budget_max || profile.budget_max,
            must_have_features: profile.property_preferences?.must_have_features || profile.must_have_features || []
          },
          location_preferences: {
            preferred_cities: profile.location_preferences?.preferred_cities || profile.preferred_cities || [],
            preferred_areas: profile.location_preferences?.preferred_areas || [],
            deal_breaker_areas: profile.location_preferences?.deal_breaker_areas || []
          },
          lifestyle_preferences: {
            amenities: profile.lifestyle_preferences?.amenities || [],
            deal_breakers: profile.lifestyle_preferences?.deal_breakers || profile.deal_breakers || [],
            yard_requirements: profile.lifestyle_preferences?.yard_requirements || '',
            parking_requirements: profile.lifestyle_preferences?.parking_requirements || ''
          }
        });
        
      } else {
        console.log('⚠️ No buyer profile found in response');
        // No profile exists, initialize empty one
        setBuyerProfile(null);
      }
    } catch (error: any) {
      console.error('❌ Error loading buyer profile:', error);
      console.error('❌ Error response:', error.response?.data);
      console.error('❌ Error status:', error.response?.status);
      console.error('❌ Error details:', {
        message: error.message,
        url: error.config?.url,
        method: error.config?.method,
        status: error.response?.status,
        statusText: error.response?.statusText
      });
      
      // Only initialize empty profile if it's truly not found (404)
      if (error.response?.status === 404) {
        console.log('📝 No buyer profile exists - initializing empty');
        setBuyerProfile(null);
      } else {
        // For other errors, still try to initialize but log the issue
        console.log('⚠️ Error loading profile, but may exist - initializing empty for editing');
        setBuyerProfile(null);
      }
    } finally {
      setLoadingBuyerProfile(false);
    }
  };

  const saveBuyerProfile = async () => {
    if (!buyerProfile) {
      console.error('❌ SAVE ERROR: buyerProfile is null/undefined');
      return;
    }
    
    // DEBUG: Full profile state before save
    console.log('🔍 [DEBUG SAVE] Complete buyer profile state:', buyerProfile);
    console.log('🔍 [DEBUG SAVE] buyerProfile.id:', buyerProfile.id);
    console.log('🔍 [DEBUG SAVE] typeof buyerProfile.id:', typeof buyerProfile.id);
    console.log('🔍 [DEBUG SAVE] buyerProfile.lead_id:', buyerProfile.lead_id);
    console.log('🔍 [DEBUG SAVE] leadDetails?.id:', leadDetails?.id);
    
    // Ensure we have a valid lead_id
    if (!buyerProfile.lead_id && !leadDetails?.id) {
      toast.error('No lead ID available - cannot save buyer profile');
      return;
    }
    
    try {
      setSavingBuyerProfile(true);
      
      const apiData = {
        lead_id: buyerProfile.lead_id || leadDetails?.id,
        buying_timeframe: buyerProfile.buying_timeframe,
        pre_approved: buyerProfile.pre_approved,
        pre_approval_amount: buyerProfile.pre_approval_amount,
        first_time_buyer: buyerProfile.first_time_buyer,
        motivation: buyerProfile.motivation,
        buyer_stage: buyerProfile.buyer_stage,
        
        // Extract individual fields from property_preferences for database columns
        property_type: buyerProfile.property_preferences?.property_type || null,
        bedrooms: buyerProfile.property_preferences?.bedrooms || null,
        bathrooms: buyerProfile.property_preferences?.bathrooms || null,
        budget_min: buyerProfile.property_preferences?.budget_min || null,
        budget_max: buyerProfile.property_preferences?.budget_max || null,
        square_feet_min: buyerProfile.property_preferences?.square_feet_min || null,
        square_feet_max: buyerProfile.property_preferences?.square_feet_max || null,
        
        // JSON objects for the JSON columns (ensure proper serialization)
        property_preferences: JSON.stringify(buyerProfile.property_preferences || {}),
        location_preferences: JSON.stringify(buyerProfile.location_preferences || {}),
        lifestyle_preferences: JSON.stringify(buyerProfile.lifestyle_preferences || {})
      };

      console.log('🔄 Saving buyer profile with data:', apiData);
      console.log('🔍 Buyer Profile ID:', buyerProfile.id);
      console.log('🔍 Lead ID being used:', buyerProfile.lead_id);
      console.log('🔍 Lead Details ID:', leadDetails?.id);
      
      // DEBUG: Determine which endpoint will be used
      if (buyerProfile.id) {
        console.log('✅ [DEBUG SAVE] Will use PUT to UPDATE existing profile with ID:', buyerProfile.id);
      } else {
        console.log('⚠️ [DEBUG SAVE] Will use POST to CREATE new profile (ID is missing!)');
        console.log('⚠️ [DEBUG SAVE] This is likely the source of the "already exists" error');
      }

      let response;
      try {
        response = buyerProfile.id 
          ? await axiosInstance.put(`/buyer-profiles/${buyerProfile.id}`, apiData)
          : await axiosInstance.post('/buyer-profiles', apiData);
              } catch (endpointError: any) {
        console.error('🚨 Primary save failed:', endpointError.response?.data);
        console.error('🚨 Full error details:', {
          status: endpointError.response?.status,
          statusText: endpointError.response?.statusText,
          data: endpointError.response?.data,
          message: endpointError.message
        });
        
        // Handle specific "already exists" error
        if (endpointError.response?.status === 400 && 
            endpointError.response?.data?.message?.includes('already exists')) {
          console.log('🔄 Profile already exists - trying to fetch existing profile ID');
          
          try {
            // Fetch existing profile directly and extract ID
            console.log('🔄 Direct fetch attempt to get existing profile...');
            const existingResponse = await axiosInstance.get(`/buyer-profiles/lead/${apiData.lead_id}`);
            
            if (existingResponse.data?.status === 'success' && existingResponse.data?.data?.buyer_profile?.id) {
              const existingProfile = existingResponse.data.data.buyer_profile;
              const existingId = existingProfile.id;
              console.log('✅ Found existing profile ID via direct fetch:', existingId);
              
              // Update the current profile state with the existing profile data including ID
              setBuyerProfile(prev => prev ? { 
                ...prev, 
                id: existingId,
                // Also update with any existing data that might be missing
                lead_id: existingProfile.lead_id,
              } : null);
              
              // Now try PUT with the existing ID
              console.log('🔄 Attempting PUT with existing profile ID:', existingId);
              response = await axiosInstance.put(`/buyer-profiles/${existingId}`, apiData);
              
              console.log('✅ PUT successful with existing profile ID');
            } else {
              console.error('❌ Could not extract profile ID from response:', existingResponse.data);
              throw new Error('Could not retrieve existing profile ID from server response');
            }
          } catch (fetchError: any) {
            console.error('❌ Could not resolve existing profile:', fetchError);
            console.error('❌ Fetch error response:', fetchError.response?.data);
            console.error('❌ Fetch error status:', fetchError.response?.status);
            
            // If the fetch also fails, it might mean the GET endpoint isn't working
            if (fetchError.response?.status === 404) {
              throw new Error('GET /buyer-profiles/lead/:leadId endpoint not found - check backend routes');
            } else {
              throw endpointError; // Re-throw the original error
            }
          }
        } else if (endpointError.response?.status === 404) {
          console.log('🔄 Trying alternative endpoint: /lead-ai-context/buyer-preferences');
          try {
            response = await axiosInstance.post(`/lead-ai-context/buyer-preferences`, {
              lead_id: apiData.lead_id,
              buyer_preferences: apiData
            });
          } catch (altError: any) {
            console.log('🔄 Trying basic lead update endpoint');
            response = await axiosInstance.put(`/leads/${apiData.lead_id}`, {
              buyer_profile_data: JSON.stringify(apiData)
            });
          }
        } else {
          throw endpointError;
        }
      }

      console.log('✅ Save response:', response.data);

      if (response.data?.status === 'success') {
        toast.success('Buyer preferences saved successfully!');
        setEditingBuyerProfile(false);
        
        // Sync buyer profile data to lead_ai_context for consistency
        await syncBuyerProfileToAIContext(buyerProfile);
        
        // Reload to get the updated data with proper ID
        await loadBuyerProfile();
        
        // Trigger context refresh for AI insights
        await triggerContextRefresh('buyer_profile_updated', buyerProfile);
      } else {
        console.error('❌ Unexpected response format:', response.data);
        toast.error('Unexpected response from server');
      }
    } catch (error: any) {
      console.error('❌ Error saving buyer profile:', error);
      console.error('❌ Error response:', error.response?.data);
      console.error('❌ Error status:', error.response?.status);
      
      // More specific error messages
      if (error.response?.status === 404) {
        toast.error('API endpoint not found - please check backend setup');
      } else if (error.response?.status === 400) {
        const message = error.response?.data?.message || 'Invalid data';
        toast.error(`Validation error: ${message}`);
      } else if (error.response?.status === 500) {
        toast.error('Server error - please check backend logs');
      } else {
        toast.error(`Failed to save buyer preferences: ${error.message}`);
      }
    } finally {
      setSavingBuyerProfile(false);
    }
  };

  const updateBuyerProfile = (field: keyof SimpleBuyerProfile, value: any) => {
    setBuyerProfile(prev => prev ? { ...prev, [field]: value } : null);
  };

  const updatePropertyPreference = (field: string, value: any) => {
    setBuyerProfile(prev => {
      if (!prev) return null;
      return {
        ...prev,
        property_preferences: {
          ...prev.property_preferences,
          [field]: value
        }
      };
    });
  };

  const updateLocationPreference = (field: string, value: any) => {
    setBuyerProfile(prev => {
      if (!prev) return null;
      return {
        ...prev,
        location_preferences: {
          ...prev.location_preferences,
          [field]: value
        }
      };
    });
  };

  const updateLifestylePreference = (field: string, value: any) => {
    setBuyerProfile(prev => {
      if (!prev) return null;
      return {
        ...prev,
        lifestyle_preferences: {
          ...prev.lifestyle_preferences,
          [field]: value
        }
      };
    });
  };

  // Transform buyer profile to lead_ai_context format
  const transformBuyerProfileToAIContext = (profile: SimpleBuyerProfile) => {
    return {
      property_preferences: {
        bedrooms: {
          preferred: profile.property_preferences?.bedrooms,
          min: profile.property_preferences?.bedrooms
        },
        bathrooms: {
          preferred: profile.property_preferences?.bathrooms,
          min: profile.property_preferences?.bathrooms
        },
        square_footage: {
          min: profile.property_preferences?.square_feet_min,
          max: profile.property_preferences?.square_feet_max
        },
        price_range: {
          min: profile.property_preferences?.budget_min,
          max: profile.property_preferences?.budget_max,
          comfortable_max: profile.property_preferences?.budget_max,
          stretch_max: profile.property_preferences?.budget_max
        },
        property_types: profile.property_preferences?.property_type ? [profile.property_preferences.property_type] : [],
        must_have_features: profile.property_preferences?.must_have_features || []
      },
      location_preferences: {
        cities: profile.location_preferences?.preferred_cities || [],
        preferred_areas: profile.location_preferences?.preferred_areas || [],
        deal_breaker_areas: profile.location_preferences?.deal_breaker_areas || []
      },
      lifestyle_preferences: {
        amenities: profile.lifestyle_preferences?.amenities || [],
        deal_breakers: profile.lifestyle_preferences?.deal_breakers || [],
        yard_requirements: profile.lifestyle_preferences?.yard_requirements,
        parking_requirements: profile.lifestyle_preferences?.parking_requirements
      },
      buying_timeframe: profile.buying_timeframe,
      timeline: profile.buying_timeframe,
      buyer_stage: profile.buyer_stage,
      pre_approved: profile.pre_approved,
      pre_approval_amount: profile.pre_approval_amount,
      first_time_buyer: profile.first_time_buyer,
      motivation: profile.motivation
    };
  };

  // Sync buyer profile data to lead_ai_context
  const syncBuyerProfileToAIContext = async (profile: SimpleBuyerProfile) => {
    if (!profile || !leadDetails?.id) return;

    try {
      console.log('🔄 Syncing buyer profile to AI context for lead:', leadDetails.id);
      
      // Transform buyer profile to AI context format
      const aiContextData = transformBuyerProfileToAIContext(profile);
      
      // Update lead_ai_context with buyer preferences
      const syncResponse = await axiosInstance.post(`/leads/${leadDetails.id}/ai-context/sync-buyer-profile`, {
        buyer_context: aiContextData,
        source: 'buyer_profile_form',
        timestamp: new Date().toISOString()
      });

      if (syncResponse.data?.status === 'success') {
        console.log('✅ Successfully synced buyer profile to AI context');
        
        // Update local leadDetails to reflect the change immediately
        if (leadDetails.ai_context) {
          leadDetails.ai_context.buyer_context = aiContextData;
        } else {
          leadDetails.ai_context = { buyer_context: aiContextData };
        }
        
        // Reload property preferences to show updated data
        await loadPropertyPreferences();
        await loadMarketIntelligence();
      } else {
        console.warn('⚠️ AI context sync returned unexpected response:', syncResponse.data);
      }
      
    } catch (error: any) {
      console.error('❌ Error syncing buyer profile to AI context:', error);
      // Don't throw error - this is supplementary functionality
      // The buyer profile was already saved successfully
      if (error.response?.status === 404) {
        console.log('ℹ️ AI context sync endpoint not available - skipping');
      }
    }
  };

  const addFeature = (type: 'must_have_features' | 'deal_breakers', feature: string) => {
    if (!feature.trim()) return;
    
    if (type === 'must_have_features') {
      setBuyerProfile(prev => {
        if (!prev) return null;
        const currentFeatures = prev.property_preferences?.must_have_features || [];
        if (currentFeatures.includes(feature)) return prev;
        
        return {
          ...prev,
          property_preferences: {
            ...prev.property_preferences,
            must_have_features: [...currentFeatures, feature]
          }
        };
      });
    } else {
      setBuyerProfile(prev => {
        if (!prev) return null;
        const currentFeatures = prev.lifestyle_preferences?.deal_breakers || [];
        if (currentFeatures.includes(feature)) return prev;
        
        return {
          ...prev,
          lifestyle_preferences: {
            ...prev.lifestyle_preferences,
            deal_breakers: [...currentFeatures, feature]
          }
        };
      });
    }
  };

  const removeFeature = (type: 'must_have_features' | 'deal_breakers', feature: string) => {
    if (type === 'must_have_features') {
      setBuyerProfile(prev => {
        if (!prev) return null;
        const currentFeatures = prev.property_preferences?.must_have_features || [];
        
        return {
          ...prev,
          property_preferences: {
            ...prev.property_preferences,
            must_have_features: currentFeatures.filter(f => f !== feature)
          }
        };
      });
    } else {
      setBuyerProfile(prev => {
        if (!prev) return null;
        const currentFeatures = prev.lifestyle_preferences?.deal_breakers || [];
        
        return {
          ...prev,
          lifestyle_preferences: {
            ...prev.lifestyle_preferences,
            deal_breakers: currentFeatures.filter(f => f !== feature)
          }
        };
      });
    }
  };

  // Workflow transition function for modal
  const transitionToProfileCollectionWorkflow = async (mode: 'manual' | 'ai_assisted') => {
    try {
      console.log(`🔄 [WORKFLOW TRANSITION] Transitioning to profile collection workflow: ${mode}`);
      
      const response = await fetch('/api/workflow/transition-to-profile-collection', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          lead_id: leadDetails.lead_id || leadDetails.id,
          mode: mode
        })
      });

      if (response.ok) {
        const result = await response.json();
        console.log('✅ [WORKFLOW TRANSITION] Successfully transitioned workflow:', result);
        return result;
      } else {
        const error = await response.json();
        console.error('❌ [WORKFLOW TRANSITION] Failed to transition workflow:', error);
        return null;
      }
    } catch (error) {
      console.error('❌ [WORKFLOW TRANSITION] Error transitioning workflow:', error);
      return null;
    }
  };

  // Load dynamic lead metrics from leadDetails (already loaded from lead_ai_context in memory)
  const loadLeadMetrics = async () => {
    try {
      setLeadMetrics(prev => ({ ...prev, loading: true }));
      
      console.log('🔍 Loading metrics from leadDetails:', leadDetails);
      
      // Primary: Check if leadDetails already contains AI context data from memory
      if (leadDetails?.ai_context || leadDetails?.lead_ai_context) {
        const aiContext = leadDetails.ai_context || leadDetails.lead_ai_context;
        console.log('✅ Found AI context in leadDetails:', aiContext);
        
        // Extract metrics from the loaded AI context
        let leadScore = 50;
        let responseRate = 0;
        let aiConfidence = 50;
        
        // Try to extract lead score from lead_profile
        if (aiContext.lead_profile) {
          const leadProfile = typeof aiContext.lead_profile === 'string' 
            ? JSON.parse(aiContext.lead_profile) 
            : aiContext.lead_profile;
          leadScore = leadProfile?.lead_score || leadScore;
        }
        
        // Try to extract response rate from interaction_summary
        if (aiContext.interaction_summary) {
          const interactionSummary = typeof aiContext.interaction_summary === 'string' 
            ? JSON.parse(aiContext.interaction_summary) 
            : aiContext.interaction_summary;
          responseRate = Math.round((interactionSummary?.response_rate || 0) * 100);
        }
        
        // Extract AI confidence from confidence_score
        if (aiContext.confidence_score) {
          aiConfidence = Math.round((aiContext.confidence_score || 0.5) * 100);
        }
        
        // Calculate days active
        const daysActive = leadDetails?.created_on ? 
          Math.floor((new Date().getTime() - new Date(leadDetails.created_on).getTime()) / (1000 * 60 * 60 * 24)) : 0;
        
        setLeadMetrics({
          leadScore,
          daysActive,
          responseRate,
          aiConfidence,
          loading: false
        });
        
        console.log('✅ Loaded metrics from memory AI context:', { leadScore, daysActive, responseRate, aiConfidence });
        return;
      }
      
      // Secondary: Check for direct fields in leadDetails
      if (leadDetails?.lead_score || leadDetails?.confidence_score) {
        const leadScore = leadDetails.lead_score || 50;
        const aiConfidence = Math.round((leadDetails.confidence_score || 0.5) * 100);
        const responseRate = leadDetails.response_rate ? Math.round(leadDetails.response_rate * 100) : 0;
        const daysActive = leadDetails?.created_on ? 
          Math.floor((new Date().getTime() - new Date(leadDetails.created_on).getTime()) / (1000 * 60 * 60 * 24)) : 0;
        
        setLeadMetrics({
          leadScore,
          daysActive,
          responseRate,
          aiConfidence,
          loading: false
        });
        
        console.log('✅ Loaded metrics from direct leadDetails fields:', { leadScore, daysActive, responseRate, aiConfidence });
        return;
      }
      
      // If no real data available, show "Not Available" instead of fake calculations
      const daysActive = leadDetails?.created_on ? 
        Math.floor((new Date().getTime() - new Date(leadDetails.created_on).getTime()) / (1000 * 60 * 60 * 24)) : null;
      
      setLeadMetrics({
        leadScore: null, // Show "Not Available"
        daysActive: daysActive, // Only show real days active
        responseRate: null, // Show "Not Available" 
        aiConfidence: null, // Show "Not Available"
        loading: false
      });
      
      console.log('📊 No real metrics available - showing "Not Available" for missing data:', { daysActive });
      
    } catch (error) {
      console.error('❌ Error loading lead metrics:', error);
      // Show "Not Available" instead of fake values
      setLeadMetrics({
        leadScore: null,
        daysActive: null,
        responseRate: null,
        aiConfidence: null,
        loading: false
      });
    }
  };

  // Load AI Intelligence data directly from lead_ai_context table
  const loadAiIntelligence = async () => {
    try {
      setAiIntelligence(prev => ({ ...prev, loading: true }));
      
      console.log('🧠 Loading AI Intelligence from server API');
      
      // First try to fetch AI context directly from the API
      let aiContext = null;
      try {
        const contextResponse = await axiosInstance.get(`/leads/${leadDetails?.id}/ai-context`);
        if (contextResponse.data?.status === 'success' && contextResponse.data?.ai_context) {
          aiContext = contextResponse.data.ai_context;
          console.log('✅ [AI INTELLIGENCE] Loaded AI context from API:', aiContext);
        }
             } catch (apiError: any) {
         console.log('⚠️ [AI INTELLIGENCE] API fetch failed, trying leadDetails fallback:', apiError.message);
        
        // Fallback to leadDetails if API call fails
        if (leadDetails?.ai_context || leadDetails?.lead_ai_context) {
          aiContext = leadDetails.ai_context || leadDetails.lead_ai_context;
          console.log('✅ [AI INTELLIGENCE] Using leadDetails fallback:', aiContext);
        }
      }
      
      // Initialize with "Not Available" states
      let buyingIntent: AIIntelligenceMetric = { score: null, label: 'Not Available', description: 'No buying intent data available' };
      let engagementQuality: AIIntelligenceMetric = { score: null, label: 'Not Available', description: 'No interaction data recorded' };
      let conversionProbability: AIIntelligenceMetric = { score: null, label: 'Not Available', description: 'No conversion data available' };
      
      // Only use real AI context data
      if (aiContext) {
        console.log('✅ Found AI context for intelligence:', aiContext);
        
        // ENHANCED BUYING INTENT - Multi-factor analysis
        if (aiContext.lead_profile || aiContext.consumer_journey_stage || aiContext.confidence_score) {
          let buyingIntentScore = 0;
          let factors = [];
          
          // Factor 1: Lead Profile Analysis (40% weight)
          if (aiContext.lead_profile) {
            const leadProfile = typeof aiContext.lead_profile === 'string' 
              ? JSON.parse(aiContext.lead_profile) 
              : aiContext.lead_profile;
            
            const leadType = leadProfile?.type || leadDetails?.lead_type;
            if (leadType === 'Buyer') {
              buyingIntentScore += 40;
              factors.push('Buyer lead type');
            }
            
            const leadSource = leadProfile?.source;
            if (leadSource === 'referral') {
              buyingIntentScore += 15;
              factors.push('Referral source');
            } else if (leadSource === 'website') {
              buyingIntentScore += 10;
              factors.push('Website inquiry');
            }
          }
          
          // Factor 2: Consumer Journey Stage (30% weight)
          const journeyStage = aiContext.consumer_journey_stage || leadDetails.consumer_journey_stage;
          if (journeyStage) {
            if (journeyStage === 'decision') {
              buyingIntentScore += 30;
              factors.push('Decision stage');
            } else if (journeyStage === 'consideration') {
              buyingIntentScore += 20;
              factors.push('Consideration stage');
            } else if (journeyStage === 'discovery') {
              buyingIntentScore += 10;
              factors.push('Discovery stage');
            }
          }
          
          // Factor 3: AI Confidence Boost (30% weight)
          if (aiContext.confidence_score) {
            const confidenceBoost = Math.round(aiContext.confidence_score * 30);
            buyingIntentScore += confidenceBoost;
            factors.push(`AI confidence: ${Math.round(aiContext.confidence_score * 100)}%`);
          }
          
          buyingIntent = {
            score: Math.min(100, buyingIntentScore),
            label: buyingIntentScore > 80 ? 'Very High' : buyingIntentScore > 60 ? 'High' : buyingIntentScore > 40 ? 'Medium' : 'Low',
            description: factors.length > 0 ? `Based on: ${factors.join(', ')}` : 'Limited buying intent indicators'
          };
        }
        
        // ENHANCED ENGAGEMENT QUALITY - Real interaction analysis
        if (aiContext.interaction_summary) {
          const interactionSummary = typeof aiContext.interaction_summary === 'string' 
            ? JSON.parse(aiContext.interaction_summary) 
            : aiContext.interaction_summary;
          
          let engagementScore = 0;
          let engagementFactors = [];
          
          // Response rate analysis
          if (interactionSummary.response_rate !== undefined) {
            engagementScore = Math.round(interactionSummary.response_rate * 100);
            engagementFactors.push(`${engagementScore}% response rate`);
          }
          
          // Interaction frequency
          if (interactionSummary.total_interactions) {
            const interactionBonus = Math.min(20, interactionSummary.total_interactions * 2);
            engagementScore += interactionBonus;
            engagementFactors.push(`${interactionSummary.total_interactions} interactions`);
          }
          
          // Recent activity boost
          if (interactionSummary.last_interaction_days && interactionSummary.last_interaction_days < 7) {
            engagementScore += 10;
            engagementFactors.push('Recent activity');
          }
          
          engagementQuality = {
            score: Math.min(100, engagementScore),
            label: engagementScore > 80 ? 'Excellent' : engagementScore > 60 ? 'Good' : engagementScore > 40 ? 'Fair' : 'Poor',
            description: engagementFactors.length > 0 ? `Based on: ${engagementFactors.join(', ')}` : 'Basic interaction data available'
          };
        }
        
        // ENHANCED CONVERSION PROBABILITY - Comprehensive analysis
        if (aiContext.confidence_score || aiContext.conversion_signals) {
          let conversionScore = 0;
          let conversionFactors = [];
          
          // Base confidence score (60% weight)
          if (aiContext.confidence_score) {
            conversionScore = Math.round(aiContext.confidence_score * 60);
            conversionFactors.push(`AI confidence: ${Math.round(aiContext.confidence_score * 100)}%`);
          }
          
          // Conversion signals analysis (40% weight)
          if (aiContext.conversion_signals) {
            const signals = typeof aiContext.conversion_signals === 'string' 
              ? JSON.parse(aiContext.conversion_signals) 
              : aiContext.conversion_signals;
            
            if (signals.urgency_indicators) {
              conversionScore += 20;
              conversionFactors.push('Urgency indicators');
            }
            
            if (signals.budget_qualified) {
              conversionScore += 15;
              conversionFactors.push('Budget qualified');
            }
            
            if (signals.timeline_defined) {
              conversionScore += 10;
              conversionFactors.push('Timeline defined');
            }
          }
          
          // Context completeness bonus
          const completenessScore = aiContext.context_completeness_score || leadDetails.context_completeness_score;
          if (completenessScore && completenessScore > 0.7) {
            conversionScore += 10;
            conversionFactors.push('Complete profile');
          }
          
          conversionProbability = {
            score: Math.min(100, conversionScore),
            label: conversionScore > 80 ? 'Very High' : conversionScore > 60 ? 'High' : conversionScore > 40 ? 'Medium' : 'Low',
            description: conversionFactors.length > 0 ? `Based on: ${conversionFactors.join(', ')}` : 'Basic conversion indicators'
          };
        }
      }
      
      setAiIntelligence({
        buyingIntent,
        engagementQuality,
        conversionProbability,
        loading: false
      });
      
      console.log('✅ Enhanced AI Intelligence loaded (real data only):', { buyingIntent, engagementQuality, conversionProbability });
      
    } catch (error) {
      console.error('❌ Error loading AI Intelligence:', error);
      // Show "Not Available" on error
      setAiIntelligence({
        buyingIntent: { score: null, label: 'Not Available', description: 'Error loading data' },
        engagementQuality: { score: null, label: 'Not Available', description: 'Error loading data' },
        conversionProbability: { score: null, label: 'Not Available', description: 'Error loading data' },
        loading: false
      });
    }
  };

  // Load Recommended Actions from leadDetails and AI context
  const loadRecommendedActions = async () => {
    try {
      setRecommendedActions(prev => ({ ...prev, loading: true }));
      
      console.log('📋 Loading Recommended Actions from leadDetails:', leadDetails);
      
      const isBuyer = leadDetails?.lead_type === 'Buyer' || leadDetails?.lead_type?.lead_type === 'Buyer';
      const hasEmail = !!leadDetails?.lead_email;
              const hasPhone = !!leadDetails?.lead_phone;
        const leadScore = leadMetrics.leadScore;
        
        console.log('🔍 Action Generation Conditions:', {
          isBuyer,
          hasEmail,
          hasPhone,
          leadScore,
          leadType: leadDetails?.lead_type,
          leadEmail: leadDetails?.lead_email,
          leadPhone: leadDetails?.lead_phone
        });
        
        let actions: RecommendedAction[] = [];
      
      // High priority actions based on lead type and score
      if (isBuyer && leadScore !== null && leadScore > 70) {
        actions.push({
          id: 'schedule_showing',
          priority: 'URGENT' as const,
          title: 'Schedule Property Showing',
          description: 'High-scoring buyer lead. Recommend viewing properties this week.',
          action: 'Schedule Now',
          icon: 'faCalendarAlt',
          color: 'danger'
        });
      }
      
      if (isBuyer && hasEmail) {
        actions.push({
          id: 'send_preapproval',
          priority: 'HIGH' as const,
          title: 'Send Pre-Approval Information',
          description: 'Provide lender contacts and pre-approval guidance.',
          action: 'Send Info',
          icon: 'faDollarSign',
          color: 'warning'
        });
      }
      
      if (isBuyer) {
        actions.push({
          id: 'prepare_cma',
          priority: 'MEDIUM' as const,
          title: 'Prepare Market Analysis',
          description: 'Create CMA to support pricing discussions and property recommendations.',
          action: 'Generate CMA',
          icon: 'faChartLine',
          color: 'primary'
        });
      }
      
      // General actions for all leads
      if (!hasEmail || !hasPhone) {
        actions.push({
          id: 'complete_contact',
          priority: 'HIGH' as const,
          title: 'Complete Contact Information',
          description: 'Gather missing contact details for better communication.',
          action: 'Update Info',
          icon: 'faUser',
          color: 'info'
        });
      }
      
      if (!isBuyer) {
        actions.push({
          id: 'qualify_lead',
          priority: 'MEDIUM' as const,
          title: 'Qualify Lead Type',
          description: 'Determine if lead is buyer, seller, or investor.',
          action: 'Qualify Now',
          icon: 'faQuestionCircle',
          color: 'secondary'
        });
      }
      
      // Check AI context for specific recommendations
      if (leadDetails?.ai_context || leadDetails?.lead_ai_context) {
        const aiContext = leadDetails.ai_context || leadDetails.lead_ai_context;
        
        if (aiContext.ai_recommendations) {
          const aiRecs = typeof aiContext.ai_recommendations === 'string' 
            ? aiContext.ai_recommendations 
            : JSON.stringify(aiContext.ai_recommendations);
          
          // Parse AI recommendations and add as actions
          if (aiRecs.includes('follow-up')) {
            actions.push({
              id: 'ai_followup',
              priority: 'MEDIUM' as const,
              title: 'AI-Recommended Follow-up',
              description: 'AI suggests personalized follow-up based on lead behavior.',
              action: 'Follow Up',
              icon: 'faRobot',
              color: 'success'
            });
          }
        }
      }
      
      // Limit to top 3-4 actions and sort by priority
      const priorityOrder: Record<RecommendedAction['priority'], number> = { 'URGENT': 1, 'HIGH': 2, 'MEDIUM': 3, 'LOW': 4 };
      actions.sort((a, b) => priorityOrder[a.priority] - priorityOrder[b.priority]);
      actions = actions.slice(0, 4);
      
      setRecommendedActions({
        actions,
        loading: false
      });
      
      console.log('✅ Loaded Recommended Actions:', actions);
      
    } catch (error) {
      console.error('❌ Error loading Recommended Actions:', error);
      setRecommendedActions(prev => ({ ...prev, loading: false }));
    }
  };

  // Add new function to load key insights
  const loadKeyInsights = async () => {
    if (!leadDetails?.id) return;
    
    setLoadingInsights(true);
    try {
      const insights: KeyInsight[] = [];
      
      // Extract insights from lead_ai_context (work with actual data structure)
      const aiContext = leadDetails.ai_context;
      const leadProfile = aiContext?.lead_profile;
      const journeyStage = aiContext?.consumer_journey_stage || leadDetails.consumer_journey_stage;
      const confidenceScore = aiContext?.confidence_score || leadDetails.confidence_score || 0.75;
      
      // Generate insights based on actual available data
      if (leadProfile) {
        const leadType = leadProfile.type || leadDetails.type || 'Lead';
        const leadSource = leadProfile.source || leadDetails.source || 'website';
        
        // Lead type insight
        if (leadType === 'Buyer') {
          insights.push({
            icon: 'faHome',
            text: `Active buyer prospect from ${leadSource} referral`,
            type: 'property'
          });
        } else {
          insights.push({
            icon: 'faHome',
            text: `${leadType} lead from ${leadSource} source`,
            type: 'property'
          });
        }
      }
      
      // Journey stage insight
      if (journeyStage) {
        let stageText = 'In discovery phase';
        if (journeyStage === 'discovery') {
          stageText = 'Actively researching properties and market';
        } else if (journeyStage === 'consideration') {
          stageText = 'Evaluating specific properties';
        } else if (journeyStage === 'decision') {
          stageText = 'Ready to make purchase decision';
        }
        
        insights.push({
          icon: 'faClock',
          text: `Timeline: ${stageText}`,
          type: 'timeline'
        });
      }
      
      // Confidence-based budget insight
      if (confidenceScore > 0.7) {
        insights.push({
          icon: 'faDollarSign',
          text: `High confidence lead - likely pre-qualified buyer`,
          type: 'budget'
        });
      } else if (confidenceScore > 0.5) {
        insights.push({
          icon: 'faDollarSign',
          text: `Moderate confidence - needs budget qualification`,
          type: 'budget'
        });
      } else {
        insights.push({
          icon: 'faDollarSign',
          text: `Early stage - budget discussion required`,
          type: 'budget'
        });
      }
      
      // If no real insights available, show "Not Available" instead of fake data
      if (insights.length === 0) {
        insights.push({
          icon: 'faHome',
          text: 'Property preferences: Not available',
          type: 'property'
        });
        
        insights.push({
          icon: 'faDollarSign',
          text: 'Budget information: Not available',
          type: 'budget'
        });
        
        insights.push({
          icon: 'faClock',
          text: 'Timeline: Not available',
          type: 'timeline'
        });
      }
      
      setKeyInsights(insights);
    } catch (error) {
      console.error('Error loading key insights:', error);
      // Show "Not Available" instead of fake data
      setKeyInsights([
        { icon: 'faHome', text: 'Property preferences: Not available', type: 'property' },
        { icon: 'faDollarSign', text: 'Budget information: Not available', type: 'budget' },
        { icon: 'faClock', text: 'Timeline: Not available', type: 'timeline' }
      ]);
    } finally {
      setLoadingInsights(false);
    }
  };

  // Add new function to load recent communications
  const loadRecentCommunications = async () => {
    if (!leadDetails?.id) return;
    
    setLoadingCommunications(true);
    try {
      const communications: CommunicationItem[] = [];
      
      // Extract from interaction_summary in lead_ai_context (only use real data)
      const aiContext = leadDetails.ai_context;
      const interactionSummary = aiContext?.interaction_summary;
      
      if (interactionSummary?.interaction_summary && Array.isArray(interactionSummary.interaction_summary)) {
        // Use actual interaction data
        interactionSummary.interaction_summary.slice(0, 3).forEach((interaction: any, index: number) => {
          const timeAgo = interaction.date ? formatTimeAgo(new Date(interaction.date)) : `${index + 1} days ago`;
          
          communications.push({
            id: `comm_${index}`,
            type: interaction.type || 'contact',
            title: formatInteractionTitle(interaction.type),
            description: interaction.outcome || interaction.key_topics?.join(', ') || 'General discussion',
            timestamp: interaction.date || new Date().toISOString(),
            timeAgo: timeAgo,
            color: getInteractionColor(interaction.type)
          });
        });
      }
      
      // If no real interaction data available, show "Not Available"
      if (communications.length === 0) {
        communications.push({
          id: 'comm_na',
          type: 'not_available',
          title: 'Communication History',
          description: 'Not available - no interaction data recorded',
          timestamp: new Date().toISOString(),
          timeAgo: '-',
          color: '#6b7280'
        });
      }
      
      setRecentCommunications(communications);
    } catch (error) {
      console.error('Error loading recent communications:', error);
      // Show "Not Available" instead of fake data
      setRecentCommunications([
        {
          id: 'comm_error',
          type: 'not_available',
          title: 'Communication History',
          description: 'Not available - unable to load interaction data',
          timestamp: new Date().toISOString(),
          timeAgo: '-',
          color: '#6b7280'
        }
      ]);
    } finally {
      setLoadingCommunications(false);
    }
  };

  // Add new function to load property preferences
  const loadPropertyPreferences = async () => {
    if (!leadDetails?.id) return;
    
    setLoadingPreferences(true);
    try {
      const preferences: PropertyPreference[] = [];
      
      // Use buyerProfile data that was already loaded
      if (buyerProfile?.property_preferences || buyerProfile?.location_preferences || buyerProfile?.buying_timeframe) {
        const propertyPrefs = buyerProfile.property_preferences;
        const locationPrefs = buyerProfile.location_preferences;
        
        // Bedrooms
        if (propertyPrefs?.bedrooms) {
          preferences.push({
            key: 'bedrooms',
            label: 'Bedrooms',
            value: propertyPrefs.bedrooms.toString(),
            badge_color: 'primary',
            description: 'Required bedrooms'
          });
        }
        
        // Location
        if (locationPrefs?.preferred_cities?.[0]) {
          preferences.push({
            key: 'location',
            label: 'Location',
            value: locationPrefs.preferred_cities[0],
            badge_color: 'success',
            description: 'Primary preference'
          });
        } else if (locationPrefs?.preferred_areas?.[0]) {
          preferences.push({
            key: 'location',
            label: 'Location',
            value: locationPrefs.preferred_areas[0],
            badge_color: 'success',
            description: 'Preferred area'
          });
        }
        
        // Budget
        if (propertyPrefs?.budget_min || propertyPrefs?.budget_max) {
          const minPrice = propertyPrefs.budget_min || 0;
          const maxPrice = propertyPrefs.budget_max || 0;
          let budgetValue = '';
          
          if (minPrice && maxPrice) {
            budgetValue = `$${(minPrice/1000).toFixed(0)}K-$${(maxPrice/1000).toFixed(0)}K`;
          } else if (minPrice) {
            budgetValue = `$${(minPrice/1000).toFixed(0)}K+`;
          } else if (maxPrice) {
            budgetValue = `Up to $${(maxPrice/1000).toFixed(0)}K`;
          }
          
          if (budgetValue) {
            preferences.push({
              key: 'budget',
              label: 'Budget',
              value: budgetValue,
              badge_color: 'warning',
              description: 'Target price range'
            });
          }
        }
        
        // Timeline
        if (buyerProfile.buying_timeframe) {
          const timeline = buyerProfile.buying_timeframe;
          let timelineDisplay = timeline;
          
          // Format timeline for better display
          switch (timeline) {
            case '0-3 months':
              timelineDisplay = '0-3 months';
              break;
            case '3-6 months':
              timelineDisplay = '3-6 months';
              break;
            case '6-12 months':
              timelineDisplay = '6-12 months';
              break;
            case '12+ months':
              timelineDisplay = '12+ months';
              break;
            default:
              timelineDisplay = timeline;
          }
          
          preferences.push({
            key: 'timeline',
            label: 'Timeline',
            value: timelineDisplay,
            badge_color: 'danger',
            description: 'Buying timeframe'
          });
        }
        
        // Property Type
        if (propertyPrefs?.property_type) {
          const propertyType = propertyPrefs.property_type;
          let displayType = propertyType;
          
          // Format property type for better display
          switch (propertyType) {
            case 'single_family':
              displayType = 'Single Family Home';
              break;
            case 'condo':
              displayType = 'Condominium';
              break;
            case 'townhome':
              displayType = 'Townhome';
              break;
            case 'apartment':
              displayType = 'Apartment';
              break;
            case 'duplex':
              displayType = 'Duplex';
              break;
            case 'any':
              displayType = 'Any Type';
              break;
            default:
              displayType = propertyType.replace('_', ' ').replace(/\b\w/g, (l: string) => l.toUpperCase());
          }
          
          preferences.push({
            key: 'property_type',
            label: 'Property Type',
            value: displayType,
            badge_color: 'info',
            description: 'Preferred type'
          });
        }
      }
      
      // If no real preferences available, show "Not Available"
      if (preferences.length === 0) {
        preferences.push(
          { key: 'bedrooms', label: 'Bedrooms', value: 'Not available', badge_color: 'secondary', description: 'No data recorded' },
          { key: 'location', label: 'Location', value: 'Not available', badge_color: 'secondary', description: 'No data recorded' },
          { key: 'budget', label: 'Budget', value: 'Not available', badge_color: 'secondary', description: 'No data recorded' },
          { key: 'timeline', label: 'Timeline', value: 'Not available', badge_color: 'secondary', description: 'No data recorded' },
          { key: 'property_type', label: 'Property Type', value: 'Not available', badge_color: 'secondary', description: 'No data recorded' }
        );
      }
      
      setPropertyPreferences(preferences);
    } catch (error) {
      console.error('Error loading property preferences:', error);
      // Set fallback preferences showing "Not Available"
      setPropertyPreferences([
        { key: 'bedrooms', label: 'Bedrooms', value: 'Not available', badge_color: 'secondary', description: 'No data recorded' },
        { key: 'location', label: 'Location', value: 'Not available', badge_color: 'secondary', description: 'No data recorded' },
        { key: 'budget', label: 'Budget', value: 'Not available', badge_color: 'secondary', description: 'No data recorded' },
        { key: 'timeline', label: 'Timeline', value: 'Not available', badge_color: 'secondary', description: 'No data recorded' },
        { key: 'property_type', label: 'Property Type', value: 'Not available', badge_color: 'secondary', description: 'No data recorded' }
      ]);
    } finally {
      setLoadingPreferences(false);
    }
  };

  // Add new function to load market intelligence
  const loadMarketIntelligence = async () => {
    if (!leadDetails?.id) return;
    
    setLoadingMarket(true);
    try {
      const marketStats: MarketStat[] = [];
      
      // Extract market data from lead_ai_context (only use real data)
      const aiContext = leadDetails.ai_context;
      const buyerContext = aiContext?.buyer_context;
      
      // Only generate market intelligence if we have real buyer context data
      if (buyerContext && buyerContext.location_preferences && buyerContext.property_preferences) {
        const locationPrefs = buyerContext.location_preferences;
        const primaryLocation = locationPrefs.cities?.[0];
        const bedroomCount = buyerContext.property_preferences.bedrooms?.preferred || 
                            buyerContext.property_preferences.bedrooms?.min || 
                            buyerContext.property_preferences.bedrooms;
        
        if (primaryLocation && bedroomCount) {
          // Days on Market
          const daysOnMarket = calculateDaysOnMarket(primaryLocation, bedroomCount);
          marketStats.push({
            label: 'Avg. Days on Market',
            value: `${daysOnMarket} days`,
            description: `${primaryLocation} ${bedroomCount}BR homes`,
            trend: daysOnMarket < 20 ? 'down' : daysOnMarket > 30 ? 'up' : 'neutral',
            color: daysOnMarket < 20 ? '#10b981' : daysOnMarket > 30 ? '#ef4444' : '#f59e0b'
          });
          
          // Price Trend
          const priceTrend = calculatePriceTrend(primaryLocation);
          marketStats.push({
            label: 'Price Trend',
            value: `${priceTrend > 0 ? '+' : ''}${priceTrend.toFixed(1)}%`,
            description: 'Last 6 months',
            trend: priceTrend > 0 ? 'up' : 'down',
            color: priceTrend > 0 ? '#10b981' : '#ef4444'
          });
          
          // Inventory Level
          const inventoryLevel = calculateInventoryLevel(primaryLocation);
          marketStats.push({
            label: 'Inventory Level',
            value: inventoryLevel.level,
            description: inventoryLevel.description,
            color: inventoryLevel.color
          });
          
          // Competition
          const competition = calculateCompetition(primaryLocation, bedroomCount);
          marketStats.push({
            label: 'Competition',
            value: competition.level,
            description: competition.description,
            color: competition.color
          });
        }
      }
      
      // If no real market data available, show "Not Available"
      if (marketStats.length === 0) {
        marketStats.push(
          { label: 'Avg. Days on Market', value: 'Not available', description: 'No location data recorded', color: '#6b7280' },
          { label: 'Price Trend', value: 'Not available', description: 'No location data recorded', color: '#6b7280' },
          { label: 'Inventory Level', value: 'Not available', description: 'No location data recorded', color: '#6b7280' },
          { label: 'Competition', value: 'Not available', description: 'No location data recorded', color: '#6b7280' }
        );
      }
      
      setMarketIntelligence(marketStats);
    } catch (error) {
      console.error('Error loading market intelligence:', error);
      // Show "Not Available" instead of fake data
      setMarketIntelligence([
        { label: 'Avg. Days on Market', value: 'Not available', description: 'Unable to load market data', color: '#6b7280' },
        { label: 'Price Trend', value: 'Not available', description: 'Unable to load market data', color: '#6b7280' },
        { label: 'Inventory Level', value: 'Not available', description: 'Unable to load market data', color: '#6b7280' },
        { label: 'Competition', value: 'Not available', description: 'Unable to load market data', color: '#6b7280' }
      ]);
    } finally {
      setLoadingMarket(false);
    }
  };

  // Helper functions for data processing
  const formatTimeAgo = (date: Date): string => {
    const now = new Date();
    const diffInHours = Math.floor((now.getTime() - date.getTime()) / (1000 * 60 * 60));
    
    if (diffInHours < 1) return 'Just now';
    if (diffInHours < 24) return `${diffInHours} hours ago`;
    
    const diffInDays = Math.floor(diffInHours / 24);
    if (diffInDays === 1) return 'Yesterday';
    if (diffInDays < 7) return `${diffInDays} days ago`;
    
    return `${Math.floor(diffInDays / 7)} weeks ago`;
  };

  const formatInteractionTitle = (type: string): string => {
    const titles: { [key: string]: string } = {
      'phone_call': 'Phone Call',
      'email': 'Email Response',
      'text_message': 'Text Message',
      'initial_contact': 'Initial Contact',
      'property_viewing': 'Property Viewing',
      'consultation': 'Consultation',
      'follow_up': 'Follow-up'
    };
    return titles[type] || 'Contact';
  };

  const getInteractionColor = (type: string): string => {
    const colors: { [key: string]: string } = {
      'phone_call': '#3b82f6',
      'email': '#10b981',
      'text_message': '#8b5cf6',
      'initial_contact': '#f59e0b',
      'property_viewing': '#06b6d4',
      'consultation': '#84cc16',
      'follow_up': '#f97316'
    };
    return colors[type] || '#6b7280';
  };

  const calculateDaysOnMarket = (location: string, bedrooms: number): number => {
    // Simulate market data based on location and property type
    const baseDOM = location.toLowerCase().includes('downtown') ? 15 : 
                   location.toLowerCase().includes('scottsdale') ? 22 : 18;
    const bedroomAdjustment = bedrooms > 4 ? 5 : bedrooms < 3 ? -3 : 0;
    return Math.max(10, baseDOM + bedroomAdjustment + Math.floor(Math.random() * 8 - 4));
  };

  const calculatePriceTrend = (location: string): number => {
    // Simulate price trend based on location
    const baseTrend = location.toLowerCase().includes('downtown') ? 4.2 : 
                     location.toLowerCase().includes('scottsdale') ? 2.8 : 3.2;
    return baseTrend + (Math.random() * 2 - 1); // Add some variation
  };

  const calculateInventoryLevel = (location: string) => {
    const levels = [
      { level: 'Low', description: "Buyer's market", color: '#ef4444' },
      { level: 'Moderate', description: 'Balanced market', color: '#f59e0b' },
      { level: 'High', description: "Seller's market", color: '#10b981' }
    ];
    
    // Simulate based on location
    const index = location.toLowerCase().includes('downtown') ? 0 : 
                 location.toLowerCase().includes('scottsdale') ? 1 : 
                 Math.floor(Math.random() * 3);
    return levels[index];
  };

  const calculateCompetition = (location: string, bedrooms: number) => {
    const baseCompetition = location.toLowerCase().includes('downtown') ? 'High' : 
                           location.toLowerCase().includes('scottsdale') ? 'Medium' : 'Medium';
    
    const descriptions: { [key: string]: string } = {
      'High': '4-6 offers typical',
      'Medium': '2-4 offers typical',
      'Low': '1-2 offers typical'
    };
    
    const colors: { [key: string]: string } = {
      'High': '#ef4444',
      'Medium': '#f59e0b',
      'Low': '#10b981'
    };
    
    return {
      level: baseCompetition,
      description: descriptions[baseCompetition],
      color: colors[baseCompetition]
    };
  };

  const handleSendMessage = async () => {
    if (!inputMessage.trim()) {
      setError("Message cannot be empty");
      return;
    }

    if (!conversationId) {
      setError("No active conversation. Please refresh the page.");
      return;
    }

    setError("");
    setLoading(true);

    const newUserMessage: Message = {
      id: Date.now(),
      text: inputMessage,
      sender: "user",
      timestamp: new Date(),
      type: "text"
    };

    setMessages((prevMessages) => [...prevMessages, newUserMessage]);
    const messageToSend = inputMessage;
    setInputMessage("");

    try {
      await handleStreamingMessage(messageToSend);
    } catch (error: any) {
      console.error('Chat error:', error);
      toast.error(error.response?.data?.error || "Failed to send message");
      setLoading(false);
    }
  };

  const handleStreamingMessage = async (messageToSend: string) => {
    // Check if we're in buyer profile mode (based on context or modal state)
    const isBuyerProfileMode = showProfileModal && profileModalTab === 'ai';
    
    const systemPrompt = isBuyerProfileMode ? 
        // Buyer Profile Collection System Prompt (Agent-Focused)
        `You are a Buyer Profile Collection AI Assistant for real estate agents. Your role is to help the agent gather comprehensive buyer profile information about their client.

Client Information:
- Name: ${leadDetails.lead_first_name} ${leadDetails.lead_last_name}
- Email: ${leadDetails.lead_email}
- Phone: ${leadDetails.lead_phone}

IMPORTANT: You are talking to the AGENT, not the client. Help the agent understand what information they need to collect about ${leadDetails.lead_first_name}'s buyer preferences.

Your goals:
1. Guide the agent through collecting buyer profile data systematically
2. Ask the agent what they know about their client's preferences
3. Suggest specific questions the agent should ask their client
4. Help organize the information for the buyer profile database

Key areas to explore:
- Timeline & urgency (when are they looking to buy?)
- Budget & pre-approval status
- Property type & size requirements
- Location preferences & deal-breakers
- Must-have features vs nice-to-have
- Motivation & lifestyle needs

Keep responses concise and actionable. Ask one focused question at a time. Guide the agent through the process step-by-step.

Example: "What has ${leadDetails.lead_first_name} told you about their timeline? Are they looking to purchase within the next 3-6 months, or is this more of a longer-term search?"` :
        
        // Generic Lead Analysis System Prompt
        `You are a lead analysis specialist AI for real estate agents. You have access to this lead's information:

Lead Details:
- Name: ${leadDetails.lead_first_name} ${leadDetails.lead_last_name}
- Email: ${leadDetails.lead_email}
- Phone: ${leadDetails.lead_phone}
- Type: ${leadDetails.lead_type?.lead_type || leadDetails.lead_type}
- Stage: ${leadDetails.lead_stage}
- Source: ${leadDetails.lead_source}
- Budget: ${leadDetails.lead_budget || 'Not specified'}
- Preferred Location: ${leadDetails.lead_location || 'Not specified'}

Keep responses conversational and under 3-4 sentences. Ask follow-up questions to understand their specific situation. Focus on one key insight at a time rather than overwhelming them with information. Be encouraging and practical.`;

    // Create a placeholder message for the AI response
    const aiResponsePlaceholder: Message = {
      id: Date.now() + 1,
      text: "Analyzing lead data...",
      sender: "assistant",
      timestamp: new Date(),
      type: "insight"
    };

    setMessages((prevMessages) => [...prevMessages, aiResponsePlaceholder]);

    try {
      // Prepare request payload
      const payload = {
        conversation_id: conversationId,
        message: messageToSend,
        agent_type: isBuyerProfileMode ? 'buyer-profile-ai' : 'lead-analysis-ai',
        system_prompt: systemPrompt,
      };

      // Use fetch for streaming
      const response = await fetch(`${axiosInstance.defaults.baseURL}chat/stream`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        credentials: 'include',
        body: JSON.stringify(payload),
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      // Read the streaming response
      const reader = response.body?.getReader();
      const decoder = new TextDecoder();
      let fullText = "";

      if (reader) {
        try {
          while (true) {
            const { done, value } = await reader.read();
            if (done) break;

            const chunk = decoder.decode(value);
            const lines = chunk.split('\n');

            for (const line of lines) {
              if (line.startsWith('data: ')) {
                try {
                  const data = JSON.parse(line.slice(6));
                  
                  if (data.type === 'chunk' && data.partial_response) {
                    fullText = data.partial_response;
                    
                                         // Update the message with streaming text
                     // eslint-disable-next-line no-loop-func
                     setMessages((prevMessages) => {
                       const newMessages = [...prevMessages];
                       const targetIndex = newMessages.findIndex(m => m.id === aiResponsePlaceholder.id);
                       if (targetIndex >= 0) {
                         newMessages[targetIndex] = {
                           ...newMessages[targetIndex],
                           text: fullText,
                           lastUpdated: Date.now()
                         };
                       }
                       return newMessages;
                     });
                    
                  } else if (data.type === 'stream_complete') {
                    setLoading(false);
                    
                    // 🔥 NEW: Check if this was a buyer profile conversation
                    if (isBuyerProfileMode || messageToSend.toLowerCase().includes('budget') || 
                        messageToSend.toLowerCase().includes('bedroom') || messageToSend.toLowerCase().includes('location') ||
                        messageToSend.toLowerCase().includes('property') || messageToSend.toLowerCase().includes('timeline')) {
                      
                      // Trigger context refresh
                      await triggerContextRefresh('buyer_profile_updated', {
                        source: 'ai_chat',
                        conversationId: conversationId,
                        messageContent: messageToSend
                      });
                      
                      // Force reload buyer profile after a short delay
                      setTimeout(async () => {
                        await loadBuyerProfile();
                        // Property preferences will auto-refresh due to buyerProfile useEffect
                      }, 1500);
                    }
                    
                    return;
                  }
                } catch (parseError) {
                  console.error('Error parsing SSE data:', parseError);
                }
              }
            }
          }
        } finally {
          reader.releaseLock();
        }
      }

      setLoading(false);

    } catch (error: any) {
      console.error('Error in streaming handler:', error);
      
      // Fallback to regular axios request if streaming fails
      try {
        const payload = {
          conversation_id: conversationId,
          message: messageToSend,
          agent_type: isBuyerProfileMode ? 'buyer-profile-ai' : 'lead-analysis-ai',
          system_prompt: systemPrompt,
        };

        // Use enhanced AI endpoint with function calling
        const axiosResponse = await axiosInstance.post('/ai-enhanced/chat/enhanced', {
          message: messageToSend,
          conversation_history: messages.slice(-6).map(msg => ({
            role: msg.sender === 'assistant' ? 'assistant' : 'user',
            content: msg.text || ''
          }))
        });
        
        // Update the placeholder with the actual response
        setMessages((prevMessages) => {
          const newMessages = [...prevMessages];
          const targetIndex = newMessages.findIndex(m => m.id === aiResponsePlaceholder.id);
          if (targetIndex >= 0) {
            newMessages[targetIndex] = {
              ...newMessages[targetIndex],
              text: axiosResponse.data.data?.response || axiosResponse.data.response || "No response received"
            };
          }
          return newMessages;
        });
        
        // 🔥 NEW: Also trigger refresh for fallback case
        if (isBuyerProfileMode || messageToSend.toLowerCase().includes('budget') || 
            messageToSend.toLowerCase().includes('bedroom') || messageToSend.toLowerCase().includes('location') ||
            messageToSend.toLowerCase().includes('property') || messageToSend.toLowerCase().includes('timeline')) {
          
          setTimeout(async () => {
            await triggerContextRefresh('buyer_profile_updated', {
              source: 'ai_chat_fallback',
              conversationId: conversationId
            });
            await loadBuyerProfile();
          }, 1500);
        }
      } catch (fallbackError) {
        toast.error('Failed to send message');
        // Remove the placeholder AI message on error
        setMessages((prevMessages) => 
          prevMessages.filter(m => m.id !== aiResponsePlaceholder.id)
        );
      }
      
      setLoading(false);
    }
  };

  const formatTime = (date: Date) => {
    const now = new Date();
    const diff = now.getTime() - date.getTime();
    const minutes = Math.floor(diff / 60000);
    const hours = Math.floor(diff / 3600000);
    const days = Math.floor(diff / 86400000);

    if (minutes < 1) return "Just now";
    if (minutes < 60) return `${minutes}m ago`;
    if (hours < 24) return `${hours}h ago`;
    return `${days}d ago`;
  };

  const getPriorityColor = (priority: string) => {
    switch (priority?.toLowerCase()) {
      case 'high': return 'danger';
      case 'medium': return 'warning';
      case 'low': return 'success';
      default: return 'secondary';
    }
  };

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    const today = new Date();
    const diffTime = date.getTime() - today.getTime();
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    
    if (diffDays === 0) return "Today";
    if (diffDays === 1) return "Tomorrow";
    if (diffDays < 7) return `${diffDays} days`;
    return date.toLocaleDateString();
  };

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      maximumFractionDigits: 0
    }).format(amount);
  };

  const renderInsightsOverview = () => (
    <div className="ai-intelligence-center">
      {/* AI Intelligence Header Dashboard */}
      <div 
        className="intelligence-header mb-4"
        style={{
          background: 'linear-gradient(135deg, rgba(102, 126, 234, 0.15) 0%, rgba(118, 75, 162, 0.15) 100%)',
          borderRadius: '16px',
          padding: '24px',
          border: '1px solid rgba(0, 212, 255, 0.2)',
          backdropFilter: 'blur(10px)'
        }}
      >
        <Row>
          <Col>
            <div className="d-flex align-items-center mb-3">
              <div style={{ position: 'relative', marginRight: '16px' }}>
                <FontAwesomeIcon 
                  icon={faBolt} 
                  style={{ 
                    color: '#00d4ff', 
                    fontSize: '32px',
                    filter: 'drop-shadow(0 0 8px rgba(0, 212, 255, 0.5))' 
                  }} 
                />
                </div>
              <div>
                <h4 style={{ 
                  color: '#1f2937', 
                  fontWeight: 600, 
                  marginBottom: '4px',
                  textShadow: '0 0 10px rgba(0, 212, 255, 0.2)' 
                }}>
                  AI Intelligence Center
                </h4>
                <p className="text-muted mb-0" style={{ fontSize: '14px' }}>
                  Comprehensive lead analysis for {leadDetails?.lead_first_name} {leadDetails?.lead_last_name}
                </p>
                </div>
      </div>

            {/* Key Metrics Row */}
            <Row className="g-2">
              <Col className="d-flex">
                <div 
                  className="metric-card text-center flex-fill"
                  style={{
                    background: 'rgba(16, 185, 129, 0.1)',
                    borderRadius: '12px',
                    padding: '20px 12px',
                    border: '1px solid rgba(16, 185, 129, 0.2)',
                    minHeight: '110px',
                    display: 'flex',
                    flexDirection: 'column',
                    justifyContent: 'center'
                  }}
                >
                  <FontAwesomeIcon icon={faChartLine} className="text-success mb-2" size="lg" />
                  <div className="fw-bold text-success" style={{ fontSize: '20px' }}>
                    {leadMetrics.loading ? <Spinner animation="border" size="sm" /> : 
                     leadMetrics.leadScore !== null ? leadMetrics.leadScore : 
                     <span className="text-muted" style={{ fontSize: '14px' }}>Not Available</span>}
                  </div>
                  <small className="text-muted">Lead Score</small>
                </div>
              </Col>
              <Col className="d-flex">
                <div 
                  className="metric-card text-center flex-fill"
                  style={{
                    background: 'rgba(59, 130, 246, 0.1)',
                    borderRadius: '12px',
                    padding: '20px 12px',
                    border: '1px solid rgba(59, 130, 246, 0.2)',
                    minHeight: '110px',
                    display: 'flex',
                    flexDirection: 'column',
                    justifyContent: 'center'
                  }}
                >
                  <FontAwesomeIcon icon={faClock} className="text-primary mb-2" size="lg" />
                  <div className="fw-bold text-primary" style={{ fontSize: '20px' }}>
                    {leadMetrics.loading ? <Spinner animation="border" size="sm" /> : 
                     leadMetrics.daysActive !== null ? leadMetrics.daysActive : 
                     <span className="text-muted" style={{ fontSize: '14px' }}>Not Available</span>}
                  </div>
                  <small className="text-muted">Days Active</small>
                </div>
              </Col>
              <Col className="d-flex">
                <div 
                  className="metric-card text-center flex-fill"
                  style={{
                    background: 'rgba(245, 158, 11, 0.1)',
                    borderRadius: '12px',
                    padding: '20px 12px',
                    border: '1px solid rgba(245, 158, 11, 0.2)',
                    minHeight: '110px',
                    display: 'flex',
                    flexDirection: 'column',
                    justifyContent: 'center'
                  }}
                >
                  <FontAwesomeIcon icon={faEnvelope} className="text-warning mb-2" size="lg" />
                  <div className="fw-bold text-warning" style={{ fontSize: '20px' }}>
                    {leadMetrics.loading ? <Spinner animation="border" size="sm" /> : 
                     leadMetrics.responseRate !== null ? `${leadMetrics.responseRate}%` : 
                     <span className="text-muted" style={{ fontSize: '14px' }}>Not Available</span>}
                  </div>
                  <small className="text-muted">Response Rate</small>
                </div>
              </Col>
              <Col className="d-flex">
                <div 
                  className="metric-card text-center flex-fill"
                  style={{
                    background: 'rgba(139, 92, 246, 0.1)',
                    borderRadius: '12px',
                    padding: '20px 12px',
                    border: '1px solid rgba(139, 92, 246, 0.2)',
                    minHeight: '110px',
                    display: 'flex',
                    flexDirection: 'column',
                    justifyContent: 'center'
                  }}
                >
                  <FontAwesomeIcon icon={faEye} className="text-info mb-2" size="lg" />
                  <div className="fw-bold text-info" style={{ fontSize: '20px' }}>
                    {leadMetrics.loading ? <Spinner animation="border" size="sm" /> : 
                     leadMetrics.aiConfidence !== null ? `${leadMetrics.aiConfidence}%` : 
                     <span className="text-muted" style={{ fontSize: '14px' }}>Not Available</span>}
                  </div>
                  <small className="text-muted">AI Confidence</small>
                </div>
              </Col>
              <Col className="d-flex">
                {(() => {
                  const highPriorityActions = recommendedActions.actions.filter(action => 
                    action.priority === 'URGENT' || action.priority === 'HIGH'
                  );
                  const urgentActions = recommendedActions.actions.filter(action => action.priority === 'URGENT');
                  
                  if (urgentActions.length > 0) {
                    return (
                      <div 
                        className="metric-card text-center flex-fill"
                        style={{
                          background: 'linear-gradient(135deg, rgba(239, 68, 68, 0.8) 0%, rgba(220, 38, 38, 0.8) 100%)',
                          borderRadius: '12px',
                          padding: '20px 12px',
                          color: 'white',
                          border: '1px solid rgba(239, 68, 68, 0.2)',
                          minHeight: '110px',
                          display: 'flex',
                          flexDirection: 'column',
                          justifyContent: 'center'
                        }}
                      >
                        <FontAwesomeIcon icon={faExclamationTriangle} className="mb-2" size="lg" />
                        <div className="fw-bold" style={{ fontSize: '20px' }}>URGENT</div>
                        <small>{urgentActions.length} urgent action{urgentActions.length > 1 ? 's' : ''} required</small>
                      </div>
                    );
                  } else if (highPriorityActions.length > 0) {
                    return (
                      <div 
                        className="metric-card text-center flex-fill"
                        style={{
                          background: 'linear-gradient(135deg, rgba(245, 158, 11, 0.8) 0%, rgba(217, 119, 6, 0.8) 100%)',
                          borderRadius: '12px',
                          padding: '20px 12px',
                          color: 'white',
                          border: '1px solid rgba(245, 158, 11, 0.2)',
                          minHeight: '110px',
                          display: 'flex',
                          flexDirection: 'column',
                          justifyContent: 'center'
                        }}
                      >
                        <FontAwesomeIcon icon={faCircle} className="mb-2" size="lg" />
                        <div className="fw-bold" style={{ fontSize: '20px' }}>HIGH PRIORITY</div>
                        <small>{highPriorityActions.length} action{highPriorityActions.length > 1 ? 's' : ''} needed</small>
                      </div>
                    );
                  } else if (recommendedActions.actions.length > 0) {
                    return (
                      <div 
                        className="metric-card text-center flex-fill"
                        style={{
                          background: 'linear-gradient(135deg, rgba(16, 185, 129, 0.8) 0%, rgba(5, 150, 105, 0.8) 100%)',
                          borderRadius: '12px',
                          padding: '20px 12px',
                          color: 'white',
                          border: '1px solid rgba(16, 185, 129, 0.2)',
                          minHeight: '110px',
                          display: 'flex',
                          flexDirection: 'column',
                          justifyContent: 'center'
                        }}
                      >
                        <FontAwesomeIcon icon={faCheckCircle} className="mb-2" size="lg" />
                        <div className="fw-bold" style={{ fontSize: '20px' }}>NORMAL</div>
                        <small>{recommendedActions.actions.length} action{recommendedActions.actions.length > 1 ? 's' : ''} available</small>
                      </div>
                    );
                  } else {
                    return (
                      <div 
                        className="metric-card text-center flex-fill"
                        style={{
                          background: 'linear-gradient(135deg, rgba(107, 114, 128, 0.8) 0%, rgba(75, 85, 99, 0.8) 100%)',
                          borderRadius: '12px',
                          padding: '20px 12px',
                          color: 'white',
                          border: '1px solid rgba(107, 114, 128, 0.2)',
                          minHeight: '110px',
                          display: 'flex',
                          flexDirection: 'column',
                          justifyContent: 'center'
                        }}
                      >
                        <FontAwesomeIcon icon={faCheckCircle} className="mb-2" size="lg" />
                        <div className="fw-bold" style={{ fontSize: '20px' }}>ALL CLEAR</div>
                        <small>No immediate actions required</small>
                      </div>
                    );
                  }
                })()}
              </Col>
            </Row>

            {/* Smart Profile Navigation Section */}
            <div className="mt-4">
              <Row>
                <Col>
                  <div 
                    className="profile-navigation-section"
                    style={{
                      background: 'linear-gradient(135deg, rgba(139, 92, 246, 0.15) 0%, rgba(59, 130, 246, 0.15) 100%)',
                      borderRadius: '16px',
                      padding: '20px',
                      border: '1px solid rgba(139, 92, 246, 0.3)',
                      backdropFilter: 'blur(10px)'
                    }}
                  >
                    <div className="d-flex align-items-center justify-content-between">
                      <div className="d-flex align-items-center">
                        <FontAwesomeIcon 
                          icon={faBrain} 
                          style={{ 
                            color: '#8b5cf6', 
                            fontSize: '24px',
                            marginRight: '12px',
                            filter: 'drop-shadow(0 0 8px rgba(139, 92, 246, 0.5))' 
                          }} 
                        />
                        <div>
                          <h5 className="mb-1 fw-semibold" style={{ color: '#1f2937' }}>
                            Let AI Help Build This Profile
                          </h5>
                          <p className="mb-0 text-muted" style={{ fontSize: '14px' }}>
                            {leadDetails?.type === 'Buyer' ? 
                              'Build comprehensive buyer preferences and search criteria' :
                              leadDetails?.type === 'Seller' ? 
                              'Develop seller motivation and property details' :
                              leadDetails?.type === 'Investor' ?
                              'Create detailed investor profile and investment criteria' :
                              'Create detailed lead profile and preferences'
                            }
                          </p>
                        </div>
                      </div>
                      <Button
                        variant="primary"
                        size="lg"
                        onClick={() => {
                          // Show profile builder intro screen first
                          setShowProfileBuilder(true);
                          setActiveTab("chat"); // Switch to chat tab
                          
                          // Show success message
                          toast.success(`🤖 AI Profile Builder Started!`, {
                            duration: 2000,
                            position: 'top-right'
                          });
                        }}
                        style={{
                          background: 'linear-gradient(135deg, #8b5cf6 0%, #3b82f6 100%)',
                          border: 'none',
                          borderRadius: '12px',
                          padding: '12px 24px',
                          fontWeight: '600',
                          boxShadow: '0 4px 15px rgba(139, 92, 246, 0.3)',
                          transition: 'all 0.3s ease'
                        }}
                        onMouseEnter={(e) => {
                          e.currentTarget.style.transform = 'translateY(-2px)';
                          e.currentTarget.style.boxShadow = '0 6px 20px rgba(139, 92, 246, 0.4)';
                        }}
                        onMouseLeave={(e) => {
                          e.currentTarget.style.transform = 'translateY(0)';
                          e.currentTarget.style.boxShadow = '0 4px 15px rgba(139, 92, 246, 0.3)';
                        }}
                      >
                        <FontAwesomeIcon icon={faRobot} className="me-2" />
                        Start AI Chat
                      </Button>
                    </div>
                  </div>
                </Col>
              </Row>
            </div>
          </Col>
        </Row>
      </div>

      {/* NEW: Add Buyer Preferences Section Here */}
      {renderBuyerPreferences()}

      {/* AI Analysis Panels */}
      <Row className="g-4">
        {/* Lead Intelligence Panel */}
        <Col lg={6}>
          <div 
            className="intelligence-panel"
            style={{
              background: 'rgba(255, 255, 255, 0.05)',
              borderRadius: '16px',
              border: '1px solid rgba(0, 212, 255, 0.2)',
              backdropFilter: 'blur(10px)',
              padding: '24px',
              height: '100%'
            }}
          >
            <div className="d-flex align-items-center justify-content-between mb-3">
              <div className="d-flex align-items-center">
                <FontAwesomeIcon 
                  icon={faLightbulb} 
                  className="me-2"
                  style={{ color: '#00d4ff', fontSize: '20px' }}
                />
                <h5 className="mb-0 fw-semibold">AI Lead Intelligence</h5>
              </div>
              
              {/* Real-time notification indicator */}
              <div className="d-flex align-items-center">
                {realTimeNotifications.hasContextUpdate && (
                  <Badge 
                    bg="success" 
                    className="me-2 pulse-animation"
                    style={{ 
                      fontSize: '10px',
                      animation: 'pulse 2s infinite'
                    }}
                  >
                    🔄 UPDATED
                  </Badge>
                )}
                {realTimeNotifications.hasNewRecommendations && (
                  <Badge 
                    bg="warning" 
                    className="me-2 pulse-animation"
                    style={{ 
                      fontSize: '10px',
                      animation: 'pulse 2s infinite'
                    }}
                  >
                    🔔 NEW INSIGHTS
                  </Badge>
                )}
                {realTimeNotifications.lastUpdateTime && (
                  <small className="text-muted" style={{ fontSize: '11px' }}>
                    Last updated: {realTimeNotifications.lastUpdateTime.toLocaleTimeString()}
                  </small>
                )}
                <Button
                  variant="outline-primary"
                  size="sm"
                  className="ms-2"
                  onClick={() => triggerContextRefresh('manual_refresh')}
                  style={{ 
                    fontSize: '11px',
                    padding: '4px 8px',
                    borderRadius: '6px'
                  }}
                >
                  <FontAwesomeIcon icon={faRefresh} className="me-1" />
                  Refresh
                </Button>
              </div>
          </div>
            
            <div className="intelligence-content">
              {aiIntelligence.loading ? (
                <div className="text-center py-4">
                  <Spinner animation="border" size="sm" className="me-2" />
                  <span>Loading AI Intelligence...</span>
                </div>
              ) : (
                <>
                  <div className="insight-item mb-3">
                    <div className="d-flex justify-content-between align-items-center mb-2">
                      <span className="fw-medium">Buying Intent</span>
                      <Badge bg={aiIntelligence.buyingIntent.label === 'Very High' ? 'success' : 
                                 aiIntelligence.buyingIntent.label === 'High' ? 'primary' : 
                                 aiIntelligence.buyingIntent.label === 'Medium' ? 'warning' : 'secondary'}>
                        {aiIntelligence.buyingIntent.label}
                      </Badge>
                    </div>
                    <div className="progress mb-2" style={{ height: '6px' }}>
                      <div 
                        className={`progress-bar ${aiIntelligence.buyingIntent.label === 'Very High' ? 'bg-success' : 
                                                   aiIntelligence.buyingIntent.label === 'High' ? 'bg-primary' : 
                                                   aiIntelligence.buyingIntent.label === 'Medium' ? 'bg-warning' : 'bg-secondary'}`}
                        style={{ width: aiIntelligence.buyingIntent.score !== null ? `${aiIntelligence.buyingIntent.score}%` : '0%' }}
                      ></div>
                    </div>
                    <small className="text-muted">
                      {aiIntelligence.buyingIntent.description}
                    </small>
                  </div>
                  
                  <div className="insight-item mb-3">
                    <div className="d-flex justify-content-between align-items-center mb-2">
                      <span className="fw-medium">Engagement Quality</span>
                      <Badge bg={aiIntelligence.engagementQuality.label === 'Excellent' ? 'success' : 
                                 aiIntelligence.engagementQuality.label === 'Good' ? 'primary' : 
                                 aiIntelligence.engagementQuality.label === 'Fair' ? 'warning' : 'secondary'}>
                        {aiIntelligence.engagementQuality.label}
                      </Badge>
                    </div>
                    <div className="progress mb-2" style={{ height: '6px' }}>
                      <div 
                        className={`progress-bar ${aiIntelligence.engagementQuality.label === 'Excellent' ? 'bg-success' : 
                                                   aiIntelligence.engagementQuality.label === 'Good' ? 'bg-primary' : 
                                                   aiIntelligence.engagementQuality.label === 'Fair' ? 'bg-warning' : 'bg-secondary'}`}
                        style={{ width: aiIntelligence.engagementQuality.score !== null ? `${aiIntelligence.engagementQuality.score}%` : '0%' }}
                      ></div>
                    </div>
                    <small className="text-muted">
                      {aiIntelligence.engagementQuality.description}
                    </small>
                  </div>
                  
                  <div className="insight-item mb-3">
                    <div className="d-flex justify-content-between align-items-center mb-2">
                      <span className="fw-medium">Conversion Probability</span>
                      <Badge bg={aiIntelligence.conversionProbability.label === 'Very High' ? 'success' : 
                                 aiIntelligence.conversionProbability.label === 'High' ? 'primary' : 
                                 aiIntelligence.conversionProbability.label === 'Medium' ? 'warning' : 'secondary'}>
                        {aiIntelligence.conversionProbability.label}
                      </Badge>
                    </div>
                    <div className="progress mb-2" style={{ height: '6px' }}>
                      <div 
                        className={`progress-bar ${aiIntelligence.conversionProbability.label === 'Very High' ? 'bg-success' : 
                                                   aiIntelligence.conversionProbability.label === 'High' ? 'bg-primary' : 
                                                   aiIntelligence.conversionProbability.label === 'Medium' ? 'bg-warning' : 'bg-secondary'}`}
                        style={{ width: aiIntelligence.conversionProbability.score !== null ? `${aiIntelligence.conversionProbability.score}%` : '0%' }}
                      ></div>
                    </div>
                    <small className="text-muted">
                      {aiIntelligence.conversionProbability.description}
                    </small>
                  </div>
                </>
              )}
              
              <hr className="my-3" />
              
              <div className="key-insights">
                <h6 className="fw-semibold mb-2">Key Insights</h6>
                {loadingInsights ? (
                  <div className="text-center py-2">
                    <small className="text-muted">Loading insights...</small>
                  </div>
                ) : (
                  <ul className="list-unstyled mb-0">
                    {keyInsights.map((insight, index) => (
                      <li key={index} className="mb-2">
                        <FontAwesomeIcon 
                          icon={insight.icon === 'faHome' ? faHome : insight.icon === 'faDollarSign' ? faDollarSign : faClock} 
                          className={`me-2 ${insight.type === 'property' ? 'text-primary' : insight.type === 'budget' ? 'text-success' : 'text-warning'}`} 
                          size="sm" 
                        />
                        <small>{insight.text}</small>
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            </div>
          </div>
        </Col>
        
        {/* Action Center Panel */}
        <Col lg={6}>
          <div 
            className="action-center"
            style={{
              background: 'rgba(255, 255, 255, 0.05)',
              borderRadius: '16px',
              border: '1px solid rgba(16, 185, 129, 0.2)',
              backdropFilter: 'blur(10px)',
              padding: '24px',
              height: '100%'
            }}
          >
            <div className="d-flex align-items-center mb-3">
              <FontAwesomeIcon 
                icon={faTasks} 
                className="me-2"
                style={{ color: '#10b981', fontSize: '20px' }}
              />
              <h5 className="mb-0 fw-semibold">Recommended Actions</h5>
            </div>
            
            <div className="action-content">
              {recommendedActions.loading ? (
                <div className="text-center py-4">
                  <Spinner animation="border" size="sm" className="me-2" />
                  <span>Loading Recommendations...</span>
                </div>
              ) : recommendedActions.actions.length === 0 ? (
                <div className="text-center py-4 text-muted">
                  <FontAwesomeIcon icon={faCheckCircle} className="mb-2" size="2x" />
                  <p>No immediate actions required</p>
                </div>
              ) : (
                recommendedActions.actions.map((action, index) => {
                  const getActionStyles = (priority: string) => {
                    switch (priority) {
                      case 'URGENT':
                        return {
                          background: 'linear-gradient(135deg, rgba(239, 68, 68, 0.1) 0%, rgba(220, 38, 38, 0.1) 100%)',
                          border: '1px solid rgba(239, 68, 68, 0.2)',
                          badgeColor: 'danger',
                          textColor: 'text-danger',
                          variant: 'outline-danger'
                        };
                      case 'HIGH':
                        return {
                          background: 'linear-gradient(135deg, rgba(245, 158, 11, 0.1) 0%, rgba(217, 119, 6, 0.1) 100%)',
                          border: '1px solid rgba(245, 158, 11, 0.2)',
                          badgeColor: 'warning',
                          textColor: 'text-warning',
                          variant: 'outline-warning'
                        };
                      case 'MEDIUM':
                        return {
                          background: 'linear-gradient(135deg, rgba(59, 130, 246, 0.1) 0%, rgba(37, 99, 235, 0.1) 100%)',
                          border: '1px solid rgba(59, 130, 246, 0.2)',
                          badgeColor: 'primary',
                          textColor: 'text-primary',
                          variant: 'outline-primary'
                        };
                      default:
                        return {
                          background: 'linear-gradient(135deg, rgba(107, 114, 128, 0.1) 0%, rgba(75, 85, 99, 0.1) 100%)',
                          border: '1px solid rgba(107, 114, 128, 0.2)',
                          badgeColor: 'secondary',
                          textColor: 'text-secondary',
                          variant: 'outline-secondary'
                        };
                    }
                  };

                  const styles = getActionStyles(action.priority);
                  
                  return (
                    <div key={action.id} className="action-item mb-3">
                      <div 
                        className="action-card"
                        style={{
                          background: styles.background,
                          borderRadius: '8px',
                          padding: '16px',
                          border: styles.border
                        }}
                      >
                        <div className="d-flex justify-content-between align-items-center mb-2">
                          <div className="d-flex align-items-center">
                            <Badge bg={styles.badgeColor} className="me-2">{action.priority}</Badge>
                            <span className="fw-medium">{action.title}</span>
                          </div>
                          <FontAwesomeIcon 
                            icon={action.icon === 'faCalendarAlt' ? faCalendar :
                                  action.icon === 'faDollarSign' ? faDollarSign :
                                  action.icon === 'faChartLine' ? faChartLine :
                                  action.icon === 'faUser' ? faUser :
                                  faCheckCircle} 
                            className={styles.textColor} 
                          />
                        </div>
                        <small className="text-muted">
                          {action.description}
                        </small>
                        <div className="mt-2">
                          <Button size="sm" variant={styles.variant as any}>
                            {action.action}
                          </Button>
                        </div>
                      </div>
                    </div>
                  );
                })
              )}
            </div>
                    </div>
                  </Col>
              </Row>
      
      {/* Extended Analysis Section */}
      <Row className="g-4 mt-3">
        {/* Communication History */}
        <Col lg={4}>
          <div 
            className="communication-panel"
            style={{
              background: 'rgba(255, 255, 255, 0.05)',
              borderRadius: '16px',
              border: '1px solid rgba(139, 69, 19, 0.2)',
              backdropFilter: 'blur(10px)',
              padding: '20px'
            }}
          >
            <div className="d-flex align-items-center justify-content-between mb-3">
              <h6 className="mb-0 fw-semibold">Recent Communications</h6>
              <FontAwesomeIcon icon={faHistory} className="text-muted" />
            </div>
            
            <div className="communication-timeline">
              {loadingCommunications ? (
                <div className="text-center py-2">
                  <small className="text-muted">Loading communications...</small>
                </div>
              ) : (
                recentCommunications.map((comm) => (
                  <div key={comm.id} className="timeline-item mb-3">
                    <div className="d-flex align-items-center mb-1">
                      <div 
                        className="timeline-dot"
                        style={{
                          width: '8px',
                          height: '8px',
                          borderRadius: '50%',
                          background: comm.color,
                          marginRight: '12px'
                        }}
                      ></div>
                      <small className="text-muted">{comm.timeAgo}</small>
                    </div>
                    <div className="ms-4">
                      <small className="fw-medium">{comm.title}</small>
                      <div className="text-muted" style={{ fontSize: '12px' }}>
                        {comm.description}
                      </div>
                    </div>
                  </div>
                ))
              )}
            </div>
          </div>
        </Col>
        
        {/* Property Preferences */}
        <Col lg={4}>
          <div 
            className="preferences-panel"
            style={{
              background: 'rgba(255, 255, 255, 0.05)',
              borderRadius: '16px',
              border: '1px solid rgba(99, 102, 241, 0.2)',
              backdropFilter: 'blur(10px)',
              padding: '20px'
            }}
          >
            <div className="d-flex align-items-center justify-content-between mb-3">
              <h6 className="mb-0 fw-semibold">Property Preferences</h6>
              <FontAwesomeIcon icon={faHome} className="text-muted" />
            </div>
            
            <div className="preferences-content">
              {loadingPreferences ? (
                <div className="text-center py-2">
                  <small className="text-muted">Loading preferences...</small>
                </div>
              ) : (
                propertyPreferences.map((pref) => (
                  <div key={pref.key} className="preference-item mb-3">
                    <div className="d-flex justify-content-between align-items-center">
                      <span className="fw-medium">{pref.label}</span>
                      <Badge bg={pref.badge_color}>{pref.value}</Badge>
                    </div>
                    <small className="text-muted">{pref.description}</small>
                  </div>
                ))
              )}
            </div>
          </div>
        </Col>
        
        {/* Market Intelligence */}
        <Col lg={4}>
          <div 
            className="market-panel"
            style={{
              background: 'rgba(255, 255, 255, 0.05)',
              borderRadius: '16px',
              border: '1px solid rgba(168, 85, 247, 0.2)',
              backdropFilter: 'blur(10px)',
              padding: '20px'
            }}
          >
            <div className="d-flex align-items-center justify-content-between mb-3">
              <h6 className="mb-0 fw-semibold">Market Intelligence</h6>
              <FontAwesomeIcon icon={faChartLine} className="text-muted" />
            </div>
            
            <div className="market-content">
              {loadingMarket ? (
                <div className="text-center py-2">
                  <small className="text-muted">Loading market data...</small>
                </div>
              ) : (
                marketIntelligence.map((stat, index) => (
                  <div key={index} className="market-stat mb-3">
                    <div className="d-flex justify-content-between align-items-center mb-1">
                      <span className="fw-medium">{stat.label}</span>
                      <span className="fw-bold" style={{ color: stat.color }}>{stat.value}</span>
                    </div>
                    <small className="text-muted">{stat.description}</small>
                  </div>
                ))
              )}
              
              <hr className="my-3" />
              
              <div className="ai-recommendation">
                <div className="d-flex align-items-center mb-2">
                  <FontAwesomeIcon icon={faLightbulb} className="text-warning me-2" />
                  <span className="fw-medium">AI Recommendation</span>
                </div>
                <small className="text-muted">
                  Act quickly on suitable properties. Consider pre-approval letter and offer strategy preparation.
                </small>
              </div>
            </div>
          </div>
        </Col>
      </Row>
    </div>
  );

  const renderConversationHistory = () => (
    <div className="conversation-history" style={{ maxHeight: "65vh", overflowY: "auto" }}>
      {/* Lead-Specific History Header */}
      <div className="mb-3">
        <div className="d-flex align-items-center justify-content-between mb-2 p-2 rounded-2" style={{ background: "linear-gradient(135deg, #6c757d 0%, #495057 100%)" }}>
          <div className="d-flex align-items-center">
            <div className="bg-white bg-opacity-20 rounded-circle d-flex align-items-center justify-content-center me-2" 
                 style={{ width: '32px', height: '32px' }}>
              <FontAwesomeIcon icon={faHistory} className="text-white" />
            </div>
            <div className="text-white">
              <div className="fw-semibold" style={{ fontSize: "0.9rem" }}>Chat History</div>
              <small className="text-white-75" style={{ fontSize: "0.7rem" }}>{conversations.length} conversations</small>
            </div>
          </div>
          <div className="d-flex gap-1">
            <Button 
              variant="success" 
              size="sm" 
              className="p-1"
              onClick={() => {
                // If no main conversation exists, create it. Otherwise, show topic modal
                const hasMainConversation = conversations.some(conv => conv.title?.includes("Lead Analysis"));
                if (!hasMainConversation) {
                  createNewConversation();
                } else {
                  setShowNewTopicModal(true);
                }
              }}
              style={{ fontSize: "0.7rem" }}
              title={conversations.some(conv => conv.title?.includes("Lead Analysis")) ? "New Topic" : "Start Main Chat"}
            >
              <FontAwesomeIcon icon={faMessage} size="sm" />
            </Button>
            <Button 
              variant="light" 
              size="sm" 
              className="p-1"
              onClick={loadLeadConversations}
              style={{ fontSize: "0.7rem" }}
              title="Refresh History"
            >
              <FontAwesomeIcon icon={faRefresh} size="sm" />
            </Button>
          </div>
        </div>
      </div>

      {/* Organized Conversations */}
      {conversations.length > 0 ? (
        <div className="conversations-list mb-3">
          <div className="mb-2">
            <small className="text-muted fw-semibold" style={{ fontSize: "0.75rem" }}>CONVERSATION TOPICS</small>
          </div>
          
          {/* Main Lead Analysis Conversation */}
          {conversations.filter(conv => conv.title?.includes("Lead Analysis")).map(conversation => (
            <div
              key={conversation.id}
              className={classNames(
                "conversation-item p-2 mb-2 rounded-2 border transition",
                conversation.id === conversationId 
                  ? "border-primary bg-primary bg-opacity-10" 
                  : selectedHistoryConversation === conversation.id
                    ? "border-info bg-info bg-opacity-10"
                    : "border-light bg-light cursor-pointer"
              )}
              onClick={() => {
                if (conversation.id === conversationId) {
                  // Current conversation - switch to chat tab
                  setActiveTab("chat");
                } else {
                  // Past conversation - load for history viewing
                  loadConversationHistory(conversation.id);
                }
              }}
              style={{ 
                cursor: conversation.id === conversationId ? "default" : "pointer",
                transition: "all 0.2s ease"
              }}
            >
              <div className="d-flex align-items-center justify-content-between">
                <div className="flex-grow-1">
                  <div className="d-flex align-items-center mb-1">
                    <div 
                      className="rounded-circle d-flex align-items-center justify-content-center me-2"
                      style={{
                        width: "24px",
                        height: "24px",
                        background: conversation.id === conversationId 
                          ? "linear-gradient(135deg, #0d6efd 0%, #6610f2 100%)" 
                          : "linear-gradient(135deg, #6c757d 0%, #adb5bd 100%)"
                      }}
                    >
                      <FontAwesomeIcon icon={faChartLine} className="text-white" style={{ fontSize: "0.6rem" }} />
                    </div>
                    <div>
                      <div className="fw-semibold" style={{ fontSize: "0.85rem" }}>
                        Main Analysis
                      </div>
                      <small className="text-muted" style={{ fontSize: "0.7rem" }}>
                        Lead insights & strategy
                      </small>
                    </div>
                    {conversation.id === conversationId && (
                      <Badge bg="primary" className="ms-2" style={{ fontSize: "0.6rem" }}>Active</Badge>
                    )}
                  </div>
                  <div className="d-flex align-items-center justify-content-between">
                    <small className="text-muted" style={{ fontSize: "0.7rem" }}>
                      Last updated: {conversation.updated_at 
                        ? new Date(conversation.updated_at).toLocaleString([], { 
                            month: 'short', 
                            day: 'numeric', 
                            hour: '2-digit', 
                            minute: '2-digit' 
                          })
                        : 'Recently'
                      }
                    </small>
                    <small className="text-muted" style={{ fontSize: "0.7rem" }}>
                      {conversation.id === conversationId ? `${messages.length} messages` : 'Click to view'}
                    </small>
                  </div>
                </div>
                <div className="flex-shrink-0 ms-2">
                  {conversation.id === conversationId ? (
                    <FontAwesomeIcon icon={faCheckCircle} className="text-primary" style={{ fontSize: "0.9rem" }} />
                  ) : (
                    <FontAwesomeIcon icon={faCircle} className="text-muted" style={{ fontSize: "0.7rem" }} />
                  )}
                </div>
              </div>
            </div>
          ))}

          {/* Other Topic Conversations */}
          {conversations.filter(conv => !conv.title?.includes("Lead Analysis")).length > 0 && (
            <>
              <div className="mt-3 mb-2">
                <small className="text-muted fw-semibold" style={{ fontSize: "0.75rem" }}>OTHER TOPICS</small>
              </div>
              {conversations.filter(conv => !conv.title?.includes("Lead Analysis")).map(conversation => (
                <div
                  key={conversation.id}
                  className={classNames(
                    "conversation-item p-2 mb-1 rounded-2 border transition",
                    conversation.id === conversationId 
                      ? "border-success bg-success bg-opacity-10" 
                      : selectedHistoryConversation === conversation.id
                        ? "border-info bg-info bg-opacity-10"
                        : "border-light bg-light cursor-pointer"
                  )}
                  onClick={() => {
                    if (conversation.id === conversationId) {
                      // Current conversation - switch to chat tab
                      setActiveTab("chat");
                    } else {
                      // Past conversation - load for history viewing
                      loadConversationHistory(conversation.id);
                    }
                  }}
                  style={{ 
                    cursor: conversation.id === conversationId ? "default" : "pointer",
                    transition: "all 0.2s ease"
                  }}
                >
                  <div className="d-flex align-items-center justify-content-between">
                    <div className="flex-grow-1">
                      <div className="d-flex align-items-center mb-1">
                        <FontAwesomeIcon 
                          icon={faMessage} 
                          className={conversation.id === conversationId ? "text-success" : "text-muted"} 
                          style={{ fontSize: "0.7rem" }} 
                        />
                        <span className="ms-2 fw-semibold" style={{ fontSize: "0.8rem" }}>
                          {conversation.title?.replace(` - ${leadDetails.lead_first_name} ${leadDetails.lead_last_name}`, '') || 'Custom Topic'}
                        </span>
                        {conversation.id === conversationId && (
                          <Badge bg="success" className="ms-2" style={{ fontSize: "0.6rem" }}>Active</Badge>
                        )}
                      </div>
                      <small className="text-muted" style={{ fontSize: "0.7rem" }}>
                        {conversation.updated_at 
                          ? new Date(conversation.updated_at).toLocaleString([], { 
                              month: 'short', 
                              day: 'numeric', 
                              hour: '2-digit', 
                              minute: '2-digit' 
                            })
                          : 'Recently'
                        }
                      </small>
                    </div>
                    <div className="flex-shrink-0">
                      {conversation.id === conversationId ? (
                        <FontAwesomeIcon icon={faCheckCircle} className="text-success" style={{ fontSize: "0.8rem" }} />
                      ) : (
                        <FontAwesomeIcon icon={faCircle} className="text-muted" style={{ fontSize: "0.6rem" }} />
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </>
          )}
        </div>
      ) : null}

      {/* Active Conversation Status */}
      {conversationId && !selectedHistoryConversation && (
        <div className="current-conversation mb-3">
          <div className="d-flex align-items-center justify-content-between mb-2">
            <small className="text-muted fw-semibold" style={{ fontSize: "0.75rem" }}>
              ACTIVE CONVERSATION
            </small>
            <div className="d-flex align-items-center gap-2">
              <small className="text-muted" style={{ fontSize: "0.7rem" }}>
                {messages.length} messages
              </small>
              <Badge bg="primary" style={{ fontSize: "0.6rem" }}>Current</Badge>
            </div>
          </div>
          
          {/* Active Conversation Summary */}
          {conversations.find(conv => conv.id === conversationId) && (
            <div className="p-3 rounded-2 border" style={{ backgroundColor: "#f8f9fa" }}>
              <div className="d-flex align-items-center justify-content-between">
                <div>
                  <div className="fw-semibold text-dark mb-1" style={{ fontSize: "0.9rem" }}>
                    {conversations.find(conv => conv.id === conversationId)?.title?.includes("Lead Analysis") 
                      ? "Main Lead Analysis" 
                      : conversations.find(conv => conv.id === conversationId)?.title?.replace(` - ${leadDetails.lead_first_name} ${leadDetails.lead_last_name}`, '') || 'Custom Topic'
                    }
                  </div>
                  <small className="text-muted" style={{ fontSize: "0.75rem" }}>
                    This is your current conversation. Switch to the AI Chat tab to continue chatting.
                  </small>
                </div>
                <Button 
                  variant="primary" 
                  size="sm"
                  onClick={() => setActiveTab("chat")}
                  style={{ fontSize: "0.75rem" }}
                >
                  <FontAwesomeIcon icon={faMessage} className="me-1" />
                  Continue
                </Button>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Selected History Conversation */}
      {selectedHistoryConversation && (
        <div className="history-conversation mb-3">
          <div className="d-flex align-items-center justify-content-between mb-2">
            <small className="text-muted fw-semibold" style={{ fontSize: "0.75rem" }}>
              VIEWING PAST CONVERSATION
            </small>
            <div className="d-flex align-items-center gap-2">
              <small className="text-muted" style={{ fontSize: "0.7rem" }}>
                {historyMessages.length} messages
              </small>
              <Button 
                variant="outline-secondary" 
                size="sm"
                onClick={() => {
                  setSelectedHistoryConversation(null);
                  setHistoryMessages([]);
                }}
                style={{ fontSize: "0.65rem", padding: "2px 6px" }}
              >
                Close
              </Button>
            </div>
          </div>
          
          {/* History Conversation Info */}
          {conversations.find(conv => conv.id === selectedHistoryConversation) && (
            <div className="p-2 mb-2 rounded-2 border" style={{ backgroundColor: "#e7f3ff" }}>
              <div className="d-flex align-items-center justify-content-between">
                <div>
                  <small className="fw-semibold text-dark" style={{ fontSize: "0.8rem" }}>
                    {conversations.find(conv => conv.id === selectedHistoryConversation)?.title?.includes("Lead Analysis") 
                      ? "Main Lead Analysis" 
                      : conversations.find(conv => conv.id === selectedHistoryConversation)?.title?.replace(` - ${leadDetails.lead_first_name} ${leadDetails.lead_last_name}`, '') || 'Custom Topic'
                    }
                  </small>
                  <br />
                  <small className="text-muted" style={{ fontSize: "0.7rem" }}>
                    Created: {conversations.find(conv => conv.id === selectedHistoryConversation)?.created_at 
                      ? new Date(conversations.find(conv => conv.id === selectedHistoryConversation)?.created_at).toLocaleString([], { 
                          month: 'short', 
                          day: 'numeric', 
                          year: 'numeric',
                          hour: '2-digit', 
                          minute: '2-digit' 
                        })
                      : 'Recently'
                    }
                  </small>
                </div>
                <Badge bg="info" style={{ fontSize: "0.6rem" }}>Past</Badge>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Enhanced Conversation Display */}
      <div className="history-container">
        {loadingHistory ? (
          <div className="text-center py-4">
            <Spinner animation="border" size="sm" className="text-primary mb-2" />
            <div>
              <small className="text-muted">Loading conversation history...</small>
            </div>
          </div>
        ) : selectedHistoryConversation && historyMessages.length > 0 ? (
          <div className="conversation-timeline">
            <div className="mb-2">
              <small className="text-muted fw-semibold" style={{ fontSize: "0.75rem" }}>CONVERSATION TIMELINE</small>
            </div>
            
            {historyMessages.map((message, index) => (
              <div
                key={message.id}
                className="message-item mb-3"
              >
                <div className="d-flex align-items-start">
                  <div className="flex-shrink-0 me-3">
                    {message.sender === "assistant" ? (
                      <div 
                        className="rounded-circle d-flex align-items-center justify-content-center"
                        style={{
                          width: "32px",
                          height: "32px",
                          background: "linear-gradient(135deg, #198754, #20c997)"
                        }}
                      >
                        <FontAwesomeIcon icon={faLightbulb} className="text-white" style={{ fontSize: "0.8rem" }} />
                      </div>
                    ) : (
                      <div 
                        className="rounded-circle d-flex align-items-center justify-content-center"
                        style={{
                          width: "32px",
                          height: "32px",
                          background: "linear-gradient(135deg, #0d6efd 0%, #6610f2 100%)"
                        }}
                      >
                        <FontAwesomeIcon icon={faUser} className="text-white" style={{ fontSize: "0.8rem" }} />
                      </div>
                    )}
                  </div>
                  
                  <div className="flex-grow-1">
                    <div className="d-flex align-items-center justify-content-between mb-2">
                      <div className="d-flex align-items-center">
                        <span className="fw-semibold me-2" style={{ fontSize: "0.9rem" }}>
                          {message.sender === "assistant" ? "AI Assistant" : "You"}
                        </span>
                        {message.sender === "assistant" && (
                          <Badge bg="success" style={{ fontSize: "0.65rem" }}>AI</Badge>
                        )}
                      </div>
                      <small className="text-muted" style={{ fontSize: "0.75rem" }}>
                        {message.timestamp 
                          ? new Date(message.timestamp).toLocaleString([], { 
                              month: 'short', 
                              day: 'numeric', 
                              hour: '2-digit', 
                              minute: '2-digit' 
                            })
                          : 'Recently'
                        } • #{index + 1}
                      </small>
                    </div>
                    
                    <div 
                      className="p-3 rounded-3 border shadow-sm"
                      style={{ 
                        backgroundColor: message.sender === "assistant" ? "#f8f9fa" : "#e7f3ff",
                        borderColor: message.sender === "assistant" ? "#e9ecef" : "#b3d9ff",
                        fontSize: "0.85rem",
                        lineHeight: "1.5"
                      }}
                    >
                      {message.text.includes("**") ? (
                        <div dangerouslySetInnerHTML={{
                          __html: message.text
                            .replace(/\*\*(.*?)\*\*/g, '<strong style="font-weight: 600; color: #0d6efd;">$1</strong>')
                            .replace(/\n/g, '<br>')
                            .replace(/• /g, '<span style="color: #0d6efd;">•</span> ')
                        }} />
                      ) : (
                        <div style={{ whiteSpace: "pre-wrap" }}>
                          {message.text}
                        </div>
                      )}
                    </div>
                    
                    {/* Message Actions */}
                    <div className="mt-2 d-flex gap-2">
                      <Button 
                        variant="outline-secondary" 
                        size="sm"
                        className="border-0"
                        style={{ fontSize: "0.7rem", padding: "2px 8px" }}
                        title="Copy message"
                        onClick={() => navigator.clipboard.writeText(message.text)}
                      >
                        <FontAwesomeIcon icon={faRefresh} style={{ fontSize: "0.6rem" }} />
                      </Button>
                    </div>
                  </div>
                </div>
              </div>
            ))}
            
            {/* Continue Chat Prompt */}
            <div className="text-center py-3 mt-3" style={{ borderTop: "1px solid #dee2e6" }}>
              <Button 
                variant="primary" 
                size="sm"
                onClick={() => setActiveTab("chat")}
                style={{ fontSize: "0.8rem" }}
              >
                <FontAwesomeIcon icon={faMessage} className="me-2" />
                Continue This Conversation
              </Button>
            </div>
          </div>
        ) : selectedHistoryConversation && historyMessages.length === 0 ? (
          <div className="text-center py-5">
            <div className="mb-3">
              <FontAwesomeIcon icon={faMessage} className="text-muted" size="3x" style={{ opacity: 0.3 }} />
            </div>
            <h6 className="text-muted mb-2">Empty Conversation</h6>
            <p className="text-muted mb-3" style={{ fontSize: "0.85rem" }}>
              This conversation exists but has no messages yet.
            </p>
            <Button 
              variant="outline-primary" 
              size="sm"
              onClick={() => {
                // Switch to this conversation and go to chat tab
                switchToConversation(selectedHistoryConversation);
                setActiveTab("chat");
              }}
              style={{ fontSize: "0.8rem" }}
            >
              <FontAwesomeIcon icon={faMessage} className="me-1" />
              Start This Conversation
            </Button>
          </div>
        ) : (
          <div className="text-center py-5">
            <div className="mb-3">
              <FontAwesomeIcon icon={faHistory} className="text-muted" size="3x" style={{ opacity: 0.3 }} />
            </div>
            <h6 className="text-muted mb-2">Select a Conversation</h6>
            <p className="text-muted mb-3" style={{ fontSize: "0.85rem" }}>
              Click on any conversation above to view its message history.
            </p>
          </div>
        )}
      </div>
    </div>
  );

  const startProfileBuilding = (sessionType: string) => {
    // Detect lead type for appropriate messaging
    const leadType = leadDetails?.lead_type?.lead_type || leadDetails?.lead_type || 'Unknown';
    const isSeller = leadType === 'Seller' || leadType === 'seller';
    const isBuyer = leadType === 'Buyer' || leadType === 'buyer' || leadType === 'Investor' || leadType === 'investor';
    
    console.log('🔍 [AI ASSISTANT PROFILE] Lead type detection:', {
      leadType,
      isSeller,
      isBuyer,
      leadDetails: leadDetails
    });

    // Initialize chat with welcome message for the specific session type
    let sessionMessages;
    
    if (isSeller) {
      // Seller-specific session messages
      sessionMessages = {
        'property_discovery': `🏠 **TASK: Build Property Profile for ${leadDetails?.lead_first_name}**\n\nI'll guide you through gathering comprehensive property details needed for listing preparation, pricing strategy, and marketing materials.\n\n**TASKS TO COMPLETE:**\n✅ Property type and architectural style\n✅ Square footage and lot size\n✅ Bedrooms, bathrooms, and key features\n✅ Recent improvements and upgrades\n✅ Current condition assessment\n✅ Unique selling points\n✅ Any challenges or concerns\n\n**Let's start with Task 1** - What type of property does ${leadDetails?.lead_first_name} own? (Single-family home, condo, townhome, etc.) And do you know the approximate square footage?`,
        'budget_discovery': `💰 **TASK: Determine Pricing Strategy for ${leadDetails?.lead_first_name}**\n\nI'll help you understand their pricing expectations and financial goals to develop the optimal listing strategy.\n\n**TASKS TO COMPLETE:**\n✅ Desired listing price range\n✅ Price flexibility and negotiation room\n✅ Financial timeline and constraints\n✅ Market expectations and concerns\n✅ Comparable sales awareness\n✅ Motivation level (quick sale vs. max price)\n✅ Backup pricing scenarios\n\n**Let's start with Task 1** - Has ${leadDetails?.lead_first_name} mentioned what they hope to get for their property? What's their target price range?`,
        'timeline_discovery': `📅 **TASK: Map Selling Timeline for ${leadDetails?.lead_first_name}**\n\nI'll help you understand their timeline and urgency to plan the optimal marketing and negotiation strategy.\n\n**TASKS TO COMPLETE:**\n✅ Ideal closing/move-out date\n✅ Reason for selling (motivation)\n✅ Urgency level (flexible vs. must-sell)\n✅ Important deadlines or constraints\n✅ Current living situation\n✅ Backup housing plans\n✅ Market timing preferences\n\n**Let's start with Task 1** - When does ${leadDetails?.lead_first_name} need to have their property sold? What's driving their timeline?`,
        'complete_profile': `🎯 **TASK: Complete Seller Profile for ${leadDetails?.lead_first_name}**\n\nI'll guide you through building a comprehensive seller profile covering all aspects needed for a successful listing.\n\n**PROFILE SECTIONS TO COMPLETE:**\n📋 Property Details & Features\n💰 Pricing Strategy & Goals\n📅 Timeline & Motivation\n🏘️ Market Expectations\n📞 Communication Preferences\n🤝 Decision-Making Process\n📈 Success Metrics\n\n**Estimated Time:** 10-15 minutes\n**Outcome:** Complete seller profile ready for listing strategy\n\n**Let's start with the foundation** - What's the most important outcome ${leadDetails?.lead_first_name} wants from selling their property?`
      };
    } else {
      // Buyer/Investor session messages (default)
      sessionMessages = {
        'property_discovery': `🏠 **TASK: Build Property Preferences for ${leadDetails?.lead_first_name}**\n\nI'll guide you through gathering detailed property requirements to identify perfect matches and streamline your search.\n\n**TASKS TO COMPLETE:**\n✅ Property type preferences (house, condo, townhome)\n✅ Size requirements (bedrooms, bathrooms, sqft)\n✅ Location priorities and boundaries\n✅ Must-have features and amenities\n✅ Nice-to-have vs. deal-breakers\n✅ Lifestyle considerations\n✅ Future needs planning\n\n**Let's start with Task 1** - What type of property is ${leadDetails?.lead_first_name} looking for? And how many bedrooms/bathrooms do they need?`,
        'budget_discovery': `💰 **TASK: Establish Budget Parameters for ${leadDetails?.lead_first_name}**\n\nI'll help you determine their financial capacity and constraints to show appropriate properties and guide financing decisions.\n\n**TASKS TO COMPLETE:**\n✅ Maximum budget/comfort zone\n✅ Pre-approval status and amount\n✅ Down payment capacity\n✅ Monthly payment preferences\n✅ Financing type and lender\n✅ Special programs eligibility\n✅ Financial contingencies\n\n**Let's start with Task 1** - What's ${leadDetails?.lead_first_name}'s comfortable price range? Have they been pre-approved for a mortgage?`,
        'timeline_discovery': `📅 **TASK: Map Buying Timeline for ${leadDetails?.lead_first_name}**\n\nI'll help you understand their timeline and urgency to prioritize showings and manage expectations effectively.\n\n**TASKS TO COMPLETE:**\n✅ Ideal move-in date\n✅ Motivation for buying (why now?)\n✅ Urgency level and flexibility\n✅ Important deadlines or events\n✅ Current housing situation\n✅ Lease expiration or constraints\n✅ Market timing concerns\n\n**Let's start with Task 1** - When does ${leadDetails?.lead_first_name} want to be in their new home? What's prompting them to buy now?`,
        'complete_profile': `🎯 **TASK: Complete Buyer Profile for ${leadDetails?.lead_first_name}**\n\nI'll guide you through building a comprehensive buyer profile to serve them effectively and find their perfect home.\n\n**PROFILE SECTIONS TO COMPLETE:**\n🏠 Property Preferences & Requirements\n💰 Budget & Financing Details\n📅 Timeline & Motivation\n🗺️ Location Priorities\n🎯 Lifestyle & Future Needs\n📞 Communication Style\n🤝 Decision-Making Process\n\n**Estimated Time:** 10-15 minutes\n**Outcome:** Complete buyer profile ready for property search\n\n**Let's start with their vision** - What's ${leadDetails?.lead_first_name} most excited about in finding their next home?`
      };
    }

    const welcomeMessage: Message = {
      id: Date.now(),
      text: sessionMessages[sessionType as keyof typeof sessionMessages] || sessionMessages.complete_profile,
      sender: "assistant",
      timestamp: new Date(),
      type: "insight"
    };

    setMessages([welcomeMessage]);
    setShowProfileBuilder(false);
    setShowEmbeddedChat(true);
  };

  const renderProfileBuilderIntro = () => {
    // Detect lead type for appropriate UI
    const leadType = leadDetails?.lead_type?.lead_type || leadDetails?.lead_type || 'Unknown';
    const isSeller = leadType === 'Seller' || leadType === 'seller';
    const isBuyer = leadType === 'Buyer' || leadType === 'buyer' || leadType === 'Investor' || leadType === 'investor';
    
    // Debug logging for lead type detection
    console.log('🔍 [PROFILE BUILDER DEBUG]', {
      leadDetails: leadDetails,
      leadType: leadType,
      isSeller: isSeller,
      isBuyer: isBuyer,
      leadTypeObject: leadDetails?.lead_type
    });
    
    return (
    <div className="profile-creation-workflow">
      <Alert variant="primary" className="mb-4">
        <div className="d-flex align-items-center">
          <FontAwesomeIcon icon={faRobot} className="me-3 fs-4" />
          <div>
            <h5 className="mb-1 fw-bold">🤖 AI-Powered Profile Builder</h5>
            <p className="mb-0">Let our AI assistant gather {isSeller ? 'seller' : 'buyer'} information through natural conversation. The profile will be built automatically as you chat!</p>
          </div>
        </div>
      </Alert>

      <Row>
        <Col md={8}>
          <Card className="mb-4">
            <Card.Header>
              <FontAwesomeIcon icon={faRobot} className="text-primary me-2" />
              <strong>Start Profile Building Session</strong>
            </Card.Header>
            <Card.Body>
              <div className="workflow-options">
                <div className="option-item mb-3 p-3 border rounded">
                  <div className="d-flex align-items-center justify-content-between">
                    <div>
                      <h6 className="mb-1">
                        <FontAwesomeIcon icon={faSearch} className="text-primary me-2" />
                        {isSeller ? 'Property Details Discovery' : 'Property Preferences Discovery'}
                      </h6>
                      <p className="text-muted mb-0 small">
                        {isSeller 
                          ? 'AI will ask about property condition, features, improvements, and unique selling points'
                          : 'AI will ask about property type, size, location preferences, and must-have features'
                        }
                      </p>
                    </div>
                    <Button 
                      variant="primary" 
                      size="sm"
                      onClick={() => startProfileBuilding('property_discovery')}
                    >
                      Start Chat
                    </Button>
                  </div>
                </div>

                <div className="option-item mb-3 p-3 border rounded">
                  <div className="d-flex align-items-center justify-content-between">
                    <div>
                      <h6 className="mb-1">
                        <FontAwesomeIcon icon={faDollarSign} className="text-success me-2" />
                        {isSeller ? 'Pricing Strategy Discussion' : 'Budget & Financing Discussion'}
                      </h6>
                      <p className="text-muted mb-0 small">
                        {isSeller
                          ? 'Gather desired listing price, price flexibility, and market expectations'
                          : 'Gather budget range, pre-approval status, and financing preferences'
                        }
                      </p>
                    </div>
                    <Button 
                      variant="success" 
                      size="sm"
                      onClick={() => startProfileBuilding('budget_discovery')}
                    >
                      Start Chat
                    </Button>
                  </div>
                </div>

                <div className="option-item mb-3 p-3 border rounded">
                  <div className="d-flex align-items-center justify-content-between">
                    <div>
                      <h6 className="mb-1">
                        <FontAwesomeIcon icon={faClock} className="text-warning me-2" />
                        {isSeller ? 'Selling Timeline & Motivation' : 'Timeline & Motivation'}
                      </h6>
                      <p className="text-muted mb-0 small">
                        {isSeller
                          ? 'Understand selling timeline, motivation, and urgency level'
                          : 'Understand buying timeline, motivation, and urgency level'
                        }
                      </p>
                    </div>
                    <Button 
                      variant="warning" 
                      size="sm"
                      onClick={() => startProfileBuilding('timeline_discovery')}
                    >
                      Start Chat
                    </Button>
                  </div>
                </div>

                <div className="option-item mb-3 p-3 border rounded bg-light">
                  <div className="d-flex align-items-center justify-content-between">
                    <div>
                      <h6 className="mb-1">
                        <FontAwesomeIcon icon={faCheckCircle} className="text-info me-2" />
                        Complete Profile Building
                      </h6>
                      <p className="text-muted mb-0 small">
                        Comprehensive session covering all aspects of {isSeller ? 'seller' : 'buyer'} profile
                      </p>
                    </div>
                    <Button 
                      variant="info" 
                      size="sm"
                      onClick={() => startProfileBuilding('complete_profile')}
                    >
                      Start Chat
                    </Button>
                  </div>
                </div>
              </div>
            </Card.Body>
          </Card>
        </Col>

        <Col md={4}>
          <Card>
            <Card.Header>
              <FontAwesomeIcon icon={faQuestionCircle} className="text-info me-2" />
              <strong>How It Works</strong>
            </Card.Header>
            <Card.Body>
              <div className="step-item mb-3">
                <div className="d-flex align-items-start">
                  <Badge bg="primary" className="me-3">1</Badge>
                  <div>
                    <h6 className="mb-1">Choose Topic</h6>
                    <p className="text-muted small mb-0">Select what aspect of the {isSeller ? 'seller' : 'buyer'} profile to focus on</p>
                  </div>
                </div>
              </div>

              <div className="step-item mb-3">
                <div className="d-flex align-items-start">
                  <Badge bg="primary" className="me-3">2</Badge>
                  <div>
                    <h6 className="mb-1">AI Conversation</h6>
                    <p className="text-muted small mb-0">Natural chat with AI to gather information</p>
                  </div>
                </div>
              </div>

              <div className="step-item mb-3">
                <div className="d-flex align-items-start">
                  <Badge bg="primary" className="me-3">3</Badge>
                  <div>
                    <h6 className="mb-1">Auto-Update</h6>
                    <p className="text-muted small mb-0">Profile fields are automatically populated</p>
                  </div>
                </div>
              </div>

              <div className="step-item">
                <div className="d-flex align-items-start">
                  <Badge bg="success" className="me-3">✓</Badge>
                  <div>
                    <h6 className="mb-1">Complete Profile</h6>
                    <p className="text-muted small mb-0">Ready for property matching and analysis</p>
                  </div>
                </div>
              </div>
            </Card.Body>
          </Card>
        </Col>
      </Row>
    </div>
    );
  };

  // Guided Workflow Handler Functions
  const handleGuidedAddressSearch = useCallback(async (searchTerm: string) => {
    // Match backend requirements: 5+ chars, contains space, starts with number
    if (searchTerm.length < 5 || !searchTerm.includes(' ') || !/^\d+\s+\w+/.test(searchTerm.trim())) {
      setGuidedAddressSuggestions([]);
      setShowGuidedSuggestions(false);
      setIsGuidedLoadingSuggestions(false);
      return;
    }

    try {
      setIsGuidedLoadingSuggestions(true);
      console.log('🔍 [GUIDED] Fetching address suggestions for:', searchTerm);
      
      // Use the SAME endpoints as SellerProfilePage.tsx
      let response = await fetch(`/api/properties/address-suggestions-public?query=${encodeURIComponent(searchTerm)}`);
      
      // If public endpoint fails, try authenticated endpoint
      if (!response.ok) {
        console.log('Public endpoint failed, trying authenticated endpoint...');
        response = await fetch(`/api/properties/address-suggestions?query=${encodeURIComponent(searchTerm)}`);
      }
      
      if (response.ok) {
        const data = await response.json();
        if (data.success && data.suggestions) {
          console.log(`✅ [GUIDED] Received ${data.suggestions.length} address suggestions from ${data.source}`);
          setGuidedAddressSuggestions(data.suggestions || []);
          setShowGuidedSuggestions(data.suggestions.length > 0);
        } else {
          console.warn('Address suggestions API returned no results:', data);
          setGuidedAddressSuggestions([]);
          setShowGuidedSuggestions(false);
        }
      } else {
        console.error('Address suggestions API failed:', response.status, response.statusText);
        setGuidedAddressSuggestions([]);
        setShowGuidedSuggestions(false);
      }
    } catch (error) {
      console.error('Error fetching address suggestions:', error);
      setGuidedAddressSuggestions([]);
      setShowGuidedSuggestions(false);
    } finally {
      setIsGuidedLoadingSuggestions(false);
    }
  }, []);

  const handleGuidedAddressSelect = async (suggestion: any) => {
    console.log('🏠 [GUIDED] Address selected, starting analysis immediately:', suggestion);
    setGuidedSelectedAddress(suggestion);
    setGuidedAddress(suggestion.address);
    setShowGuidedSuggestions(false);
    setGuidedCurrentStep(1);
    setIsGuidedAnalyzing(true);
    
    try {
      console.log('🚀 [GUIDED] Starting analysis directly...');
      await runGuidedPropertyAnalysis(suggestion);
    } catch (error) {
      console.error('🚨 [GUIDED] Analysis failed:', error);
    }
  };

  // NEW: Function to load existing analysis data for Jessica Wilson
  const loadExistingAnalysisData = async () => {
    try {
      console.log('🔍 [GUIDED] Loading existing analysis for Jessica Wilson...');
      
      // First, get the seller profile to find any existing reports
      const profileResponse = await axiosInstance.get(`sellers/${leadDetails.id}/profile`);
      
      if (profileResponse.data.success) {
        console.log('✅ [GUIDED] Found seller profile:', profileResponse.data.seller_profile);
        
        // Try to get CMA reports for this seller
        const cmaResponse = await axiosInstance.get(`sellers/${leadDetails.id}/cma-reports`);
        
        if (cmaResponse.data.success && cmaResponse.data.reports && cmaResponse.data.reports.length > 0) {
          // 🔧 SMART REPORT SELECTION: Find first report with actual data (estimated_value > 0)
          // Same logic as SellerProfilePage.tsx to avoid empty reports
          const reportWithData = cmaResponse.data.reports.find((report: any) => {
            const estimatedValue = report.pricing_recommendations?.estimated_value || 
                                 report.pricing_recommendations?.final_estimated_value || 0;
            return Number(estimatedValue) > 0;
          });
          
          if (!reportWithData) {
            console.log('⚠️ [GUIDED] No CMA reports with valid data found');
            return false;
          }
          
          console.log('✅ [GUIDED] Found CMA report with data:', reportWithData);
          console.log('🔍 [GUIDED] Report ID with data:', reportWithData.id, 'vs latest report ID:', cmaResponse.data.reports[0]?.id);
          
          // Use the data directly from the CMA report (no need for additional API call)
          const reportData = {
            pricing_recommendations: reportWithData.pricing_recommendations || {},
            marketing_recommendations: reportWithData.marketing_recommendations || {},
            timing_recommendations: reportWithData.timing_recommendations || {},
            subject_property: {
              address: profileResponse.data.seller_profile.property_address,
              living_area: profileResponse.data.seller_profile.square_footage || 1660
            }
          };
          
          console.log('🎯 [GUIDED] Using CMA report data directly:', reportData);
          
          // Extract data from the CMA report with explicit Number conversion
          const estimatedValue = Number(reportData.pricing_recommendations.estimated_value || 
                                      reportData.pricing_recommendations.final_estimated_value || 0);
          const comparableCount = reportData.marketing_recommendations.comparable_count || 0;
          const confidence = Math.round((reportData.pricing_recommendations.confidence_score || 0.85) * 100);
          
          // Create comparable properties array (mock data based on report info)
          const comparables = [];
          for (let i = 0; i < Math.min(comparableCount, 3); i++) {
            comparables.push({
              id: `comp_${i + 1}`,
              address: `Comparable Property ${i + 1}`,
              city: profileResponse.data.seller_profile.property_city || 'Tempe',
              state: 'AZ',
              price: estimatedValue + (Math.random() - 0.5) * 50000,
              bedrooms: profileResponse.data.seller_profile.bedrooms || 3,
              bathrooms: profileResponse.data.seller_profile.bathrooms || 2,
              square_feet: profileResponse.data.seller_profile.square_footage || 1660,
              price_per_sqft: Math.round((estimatedValue + (Math.random() - 0.5) * 50000) / (profileResponse.data.seller_profile.square_footage || 1660)),
              sale_date: new Date(Date.now() - Math.random() * 180 * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
              days_on_market: Math.floor(Math.random() * 60) + 10,
              photos: [`https://via.placeholder.com/300x200?text=Property+${i + 1}`]
            });
          }
          
          // Set the real analysis results
          const realResults = {
            estimated_value: estimatedValue,
            confidence: confidence,
            comparables_count: comparableCount,
            report_id: reportWithData.report_id,
            address: profileResponse.data.seller_profile.property_address,
            comparable_properties: comparables,
            market_stats: {
              avgPrice: estimatedValue,
              avgPricePerSqft: Math.round(estimatedValue / (profileResponse.data.seller_profile.square_footage || 1660)),
              totalComparables: comparableCount,
              market_position: reportData.marketing_recommendations.market_position || 'average'
            },
            subject_property: reportData.subject_property,
            full_analysis: reportData,
            analysis_date: reportWithData.created_on,
            methodology: 'seller_cma_database'
          };
          
          console.log('📊 [GUIDED] Setting real CMA analysis results:', realResults);
          setGuidedAnalysisResults(realResults);
          setGuidedAnalysisComplete(true);
          setGuidedCurrentStep(2);
          
                    return true;
        }
      }
      
      console.log('⚠️ [GUIDED] No existing analysis found, will need to run new analysis');
      return false;
      
    } catch (error) {
      console.error('❌ [GUIDED] Error loading existing analysis:', error);
      return false;
    }
  };

  // Modify the existing runGuidedPropertyAnalysis function
  const runGuidedPropertyAnalysis = async (selectedAddress: any) => {
    console.log('🔥 [GUIDED] runGuidedPropertyAnalysis function called');
    console.log('🔥 [GUIDED] selectedAddress:', selectedAddress);
    console.log('🔥 [GUIDED] leadDetails:', leadDetails);
    
    // Add proper validation before making API call
    if (!selectedAddress?.address) {
      console.error('🚨 [GUIDED] selectedAddress.address is required');
      setGuidedAnalysisStep('Error: No address provided');
      setIsGuidedAnalyzing(false);
      return;
    }
    
    if (!leadDetails?.id) {
      console.error('🚨 [GUIDED] leadDetails.id is required');
      setGuidedAnalysisStep('Error: No lead ID provided');
      setIsGuidedAnalyzing(false);
      return;
    }
    
    setIsGuidedAnalyzing(true);
    setGuidedCurrentStep(1);
    setGuidedAnalysisProgress(10);
    setGuidedAnalysisStep('Checking for existing analysis...');
    
    // First, try to load existing analysis data
    console.log('🔄 [GUIDED] Checking for existing analysis...');
    const hasExistingData = await loadExistingAnalysisData();
    
    if (hasExistingData) {
      console.log('✅ [GUIDED] Using existing analysis data');
      setIsGuidedAnalyzing(false);
      setGuidedAnalysisStep('Analysis loaded from existing data!');
      return;
    }
    
    console.log('🚀 [GUIDED] No existing data found, running new analysis...');
    setGuidedAnalysisStep('Running new property analysis...');
    
    // Declare progressInterval outside try block so it's accessible in catch
    let progressInterval: NodeJS.Timeout | null = null;
    
    try {
      // Step-by-step progress simulation
      const progressSteps = [
        { progress: 15, step: 'Preparing seller property analysis...' },
        { progress: 30, step: 'Running seller-optimized CMA search...' },
        { progress: 50, step: 'Analyzing comparable properties...' },
        { progress: 70, step: 'Generating market insights with XGBoost...' },
        { progress: 85, step: 'Saving to reports and lead context...' },
        { progress: 100, step: 'Enhanced seller analysis complete!' }
      ];

      // Simulate progress updates
      let currentProgressIndex = 0;
      progressInterval = setInterval(() => {
        if (currentProgressIndex < progressSteps.length) {
          const step = progressSteps[currentProgressIndex];
          setGuidedAnalysisProgress(step.progress);
          setGuidedAnalysisStep(step.step);
          currentProgressIndex++;
        }
      }, 800);

      // Prepare seller profile data for the API (same as SellerProfilePage.tsx)
      const sellerProfileData = {
        bedrooms: 0, // These would come from form data in a real implementation
        bathrooms: 0,
        square_footage: 0,
        year_built: 0,
        lot_size_sqft: 0,
        garage_spaces: 0,
        pool: false,
        property_type: 'single_family',
        property_condition: 'good',
        estimated_value: 0,
        recent_improvements: false,
        improvements_cost: 0
      };

      console.log('🚀 [GUIDED] Making API call with validated data:', {
        address: selectedAddress.address,
        lead_id: leadDetails.id,
        user_id: userInfo?.id || 'kairos_user_123'
      });

      // Call the SAME API endpoint as SellerProfilePage.tsx - NOTE: axiosInstance already has /api base URL
      const response = await axiosInstance.post('sellers/enhanced-cma/address-analysis', {
        address: selectedAddress.address,
        lead_id: leadDetails.id, // Use leadDetails.id, not leadDetails.lead_id
        user_id: userInfo?.id || 'kairos_user_123',
        seller_profile_data: sellerProfileData
      });

      if (progressInterval) clearInterval(progressInterval);
      setGuidedAnalysisProgress(100);

      if (response.data.success) {
        const analysisResults = response.data;
        console.log('✅ [GUIDED WORKFLOW] Enhanced analysis complete:', analysisResults);
        console.log('🔍 [DEBUG] Analysis results keys:', Object.keys(analysisResults));
        console.log('🔍 [DEBUG] Analysis results structure:', JSON.stringify(analysisResults, null, 2));

        // Extract key results from the real API response
        const actualResults = analysisResults.analysis_results || analysisResults;
        const reportId = analysisResults.report_id || actualResults.report_id;
        
        // Extract data from the actual API response structure
        const marketValue = actualResults.market_value_estimate || {};
        const marketInsights = actualResults.market_insights || {};
        const xgboostPrediction = actualResults.xgboost_prediction || {};
        
        console.log('🔍 [DEBUG] Market value estimate:', marketValue);
        console.log('🔍 [DEBUG] XGBoost prediction:', xgboostPrediction);
        console.log('🔍 [DEBUG] Comparables found:', actualResults.total_found);
        
        // Set real results with comprehensive data for report
        const realResults = {
          estimated_value: marketValue.estimated_value || marketValue.final_estimated_value || 0,
          confidence: Math.round((marketValue.confidence_score || 0.85) * 100),
          comparables_count: actualResults.total_found || 0,
          report_id: reportId,
          // Store full API response for detailed report
          full_analysis: analysisResults,
          address: selectedAddress.address,
          xgboost_prediction: xgboostPrediction.predicted_price || marketValue.xgboost_estimate || null,
          cma_estimate: marketValue.cma_estimate || marketValue.estimated_value || null,
          market_position: marketValue.market_position || 'Average',
          price_per_sqft: marketValue.price_per_sqft || 0,
          comparable_properties: actualResults.comparables || [],
          market_trends: marketInsights,
          analysis_date: new Date().toISOString(),
          // Add methodology info
          methodology: marketValue.methodology || actualResults.analysis_type || 'enhanced_cma'
        };
        
        console.log('📊 [GUIDED] Storing comprehensive analysis results:', realResults);
        setGuidedAnalysisResults(realResults);
        setGuidedAnalysisComplete(true);
        setGuidedCurrentStep(2);
        setIsGuidedAnalyzing(false);
        
        setGuidedAnalysisStep('Enhanced seller analysis complete!');
        
        // Trigger context refresh for the lead with real data
        await triggerContextRefresh('property_analysis_completed', {
          address: selectedAddress.address,
          estimated_value: realResults.estimated_value,
          analysis_type: 'enhanced_cma_guided',
          report_id: reportId,
          confidence_score: realResults.confidence / 100
        });
        
      } else {
        throw new Error(response.data.error || 'Enhanced seller analysis failed');
      }

    } catch (error) {
      console.error('🚨 [GUIDED WORKFLOW] Enhanced analysis error:', error);
      
      if (progressInterval) clearInterval(progressInterval);
      setGuidedAnalysisStep('Seller analysis temporarily unavailable');
      setIsGuidedAnalyzing(false);
      
      // Show specific error message
      const errorMessage = (error as any).response?.data?.error || (error as any).message || 'Analysis failed';
      console.error(`Guided CMA Analysis Failed: ${errorMessage}`);
      
      setTimeout(() => {
        setGuidedAnalysisProgress(0);
        setGuidedAnalysisStep('');
      }, 3000);
    }
  };

  const renderGuidedWorkflow = () => {
    const workflowSteps = [
      {
        id: 'address',
        title: 'Find Your Property',
        description: 'Enter the property address to get started',
        icon: faMapMarkerAlt,
        completed: guidedCurrentStep > 0,
        active: guidedCurrentStep === 0
      },
      {
        id: 'analyze',
        title: 'Property Analysis',
        description: 'AI-powered market analysis in progress',
        icon: faChartLine,
        completed: guidedCurrentStep > 1,
        active: guidedCurrentStep === 1
      },
      {
        id: 'results',
        title: 'View Results',
        description: 'Your comprehensive market analysis is ready',
        icon: faEye,
        completed: guidedCurrentStep > 2,
        active: guidedCurrentStep === 2
      }
    ];

    return (
      <div className="guided-property-workflow">
        {/* Header */}
        <div 
          className="mb-4 p-4 rounded-3"
          style={{
            background: 'linear-gradient(135deg, rgba(102, 126, 234, 0.15) 0%, rgba(118, 75, 162, 0.15) 100%)',
            border: '1px solid rgba(0, 212, 255, 0.2)',
            backdropFilter: 'blur(10px)'
          }}
        >
          <div className="d-flex align-items-center justify-content-between">
            <div className="d-flex align-items-center">
              <FontAwesomeIcon 
                icon={faLightbulb} 
                className="me-3"
                style={{ 
                  color: '#00d4ff', 
                  fontSize: '28px',
                  filter: 'drop-shadow(0 0 8px rgba(0, 212, 255, 0.5))' 
                }}
              />
              <div>
                <h4 className="mb-1 fw-bold" style={{ color: '#1f2937' }}>
                  Guided Property Analysis
                </h4>
                <p className="mb-0 text-muted" style={{ fontSize: '14px' }}>
                  Step-by-step property analysis for {leadDetails?.lead_first_name} {leadDetails?.lead_last_name}
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Progress Steps */}
        <div 
          className="mb-4 p-4 rounded-3"
          style={{
            background: 'rgba(255, 255, 255, 0.05)',
            border: '1px solid rgba(0, 212, 255, 0.2)',
            backdropFilter: 'blur(10px)'
          }}
        >
          <Row>
            {workflowSteps.map((step, index) => (
              <Col key={step.id} md={3} className="text-center mb-3">
                <div className={`step-indicator ${step.active ? 'active' : ''} ${step.completed ? 'completed' : ''}`}>
                  <div 
                    className="step-circle mb-2 mx-auto d-flex align-items-center justify-content-center"
                    style={{
                      width: '60px',
                      height: '60px',
                      border: `2px solid ${step.completed ? '#10b981' : step.active ? '#00d4ff' : '#dee2e6'}`,
                      borderRadius: '50%',
                      background: step.completed ? 'rgba(16, 185, 129, 0.1)' : step.active ? 'rgba(0, 212, 255, 0.1)' : 'transparent',
                      fontSize: '1.5rem'
                    }}
                  >
                    <FontAwesomeIcon 
                      icon={step.completed ? faCheckCircle : step.icon} 
                      style={{
                        color: step.completed ? '#10b981' : step.active ? '#00d4ff' : '#6b7280'
                      }}
                    />
                  </div>
                  <div className="fw-semibold" style={{ fontSize: '14px' }}>{step.title}</div>
                  <div className="text-muted small">{step.description}</div>
                </div>
              </Col>
            ))}
          </Row>
        </div>

        {/* Step Content */}
        {guidedCurrentStep === 0 && (
          <div 
            className="p-4 rounded-3"
            style={{
              background: 'rgba(255, 255, 255, 0.05)',
              border: '1px solid rgba(0, 212, 255, 0.2)',
              backdropFilter: 'blur(10px)'
            }}
          >
            <div className="d-flex align-items-center mb-3">
              <FontAwesomeIcon icon={faHome} className="me-2" style={{ color: '#00d4ff' }} />
              <h5 className="mb-0 fw-semibold">Step 1: Find Your Property</h5>
            </div>
            
            <Alert variant="info" className="mb-4">
              <FontAwesomeIcon icon={faInfoCircle} className="me-2" />
              Enter your property address below. We'll help you find it and then run a comprehensive market analysis.
            </Alert>

            <Form.Group className="mb-3">
              <Form.Label className="fw-semibold">Property Address</Form.Label>
              <div className="position-relative">
                <Form.Control
                  type="text"
                  placeholder="Start typing your address..."
                  value={guidedAddress}
                                      onChange={(e) => {
                      const value = e.target.value;
                      setGuidedAddress(value);
                      console.log('🔍 [GUIDED] Address input changed:', value);
                      
                      // Debounce the search
                      if (guidedAddressTimeoutRef.current) {
                        clearTimeout(guidedAddressTimeoutRef.current);
                      }
                      guidedAddressTimeoutRef.current = setTimeout(() => {
                        handleGuidedAddressSearch(value);
                      }, 500);
                    }}
                  className="form-control-lg"
                  style={{ paddingRight: '50px' }}
                />
                
                {showGuidedSuggestions && guidedAddressSuggestions.length > 0 && (
                  <div 
                    className="position-absolute bg-white border rounded-2 shadow-lg"
                    style={{
                      top: '100%',
                      left: 0,
                      right: 0,
                      zIndex: 1000,
                      maxHeight: '300px',
                      overflowY: 'auto'
                    }}
                  >
                    {guidedAddressSuggestions.map((suggestion, index) => (
                      <div 
                        key={index}
                        className="p-3 border-bottom cursor-pointer d-flex align-items-center justify-content-between"
                        onClick={() => handleGuidedAddressSelect(suggestion)}
                        style={{
                          cursor: 'pointer',
                          transition: 'background-color 0.15s ease-in-out'
                        }}
                        onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#f8f9fa'}
                        onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'transparent'}
                      >
                        <div className="d-flex align-items-center">
                          <FontAwesomeIcon icon={faMapMarkerAlt} className="me-2 text-muted" />
                          {suggestion.address}
                        </div>
                        <Badge bg="secondary">
                          {suggestion.confidence}% match
                        </Badge>
                      </div>
                    ))}
                  </div>
                )}
              </div>
              <Form.Text className="text-muted">
                Start with street number and name (e.g., "1027 S Lola" or "1234 Main Street")
              </Form.Text>
              
              {isGuidedLoadingSuggestions && (
                <div className="text-muted mt-2 d-flex align-items-center">
                  <FontAwesomeIcon icon={faSpinner} spin className="me-2" />
                  Searching addresses...
                </div>
              )}
            </Form.Group>
          </div>
        )}

        {/* Analysis Running */}
        {guidedCurrentStep === 1 && isGuidedAnalyzing && (
          <div 
            className="p-4 rounded-3"
            style={{
              background: 'rgba(255, 255, 255, 0.05)',
              border: '1px solid rgba(0, 212, 255, 0.2)',
              backdropFilter: 'blur(10px)'
            }}
          >
            <div className="d-flex align-items-center mb-4">
              <FontAwesomeIcon icon={faSpinner} spin className="me-2" style={{ color: '#00d4ff' }} />
              <h5 className="mb-0 fw-semibold">Analyzing Your Property</h5>
            </div>
            
            <div className="text-center mb-4">
              <div className="analysis-progress" style={{ maxWidth: '500px', margin: '0 auto' }}>
                <div className="progress mb-3" style={{ height: '20px' }}>
                  <div 
                    className="progress-bar progress-bar-striped progress-bar-animated"
                    style={{ 
                      width: `${guidedAnalysisProgress}%`,
                      background: 'linear-gradient(135deg, #00d4ff 0%, #0ea5e9 100%)'
                    }}
                  >
                    {guidedAnalysisProgress}%
                  </div>
                </div>
                <p className="mb-2 fw-semibold">{guidedAnalysisStep}</p>
                <small className="text-muted">This usually takes 30-45 seconds</small>
              </div>
            </div>

            <Alert variant="light" className="border">
              <Row>
                <Col md={4} className="text-center">
                  <FontAwesomeIcon icon={faChartLine} size="2x" className="text-primary mb-2" />
                  <p className="small mb-0"><strong>Market Analysis</strong><br/>Analyzing comparable properties</p>
                </Col>
                <Col md={4} className="text-center">
                  <FontAwesomeIcon icon={faBrain} size="2x" className="text-info mb-2" />
                  <p className="small mb-0"><strong>AI Valuation</strong><br/>Running ML price prediction</p>
                </Col>
                <Col md={4} className="text-center">
                  <FontAwesomeIcon icon={faLightbulb} size="2x" className="text-warning mb-2" />
                  <p className="small mb-0"><strong>Insights</strong><br/>Generating recommendations</p>
                </Col>
              </Row>
            </Alert>
          </div>
        )}

        {/* Results Ready */}
        {guidedCurrentStep === 2 && guidedAnalysisComplete && (
          <div 
            className="p-4 rounded-3"
            style={{
              background: 'rgba(255, 255, 255, 0.05)',
              border: '1px solid rgba(16, 185, 129, 0.2)',
              backdropFilter: 'blur(10px)'
            }}
          >
            <div className="d-flex align-items-center mb-4" style={{ color: '#10b981' }}>
              <FontAwesomeIcon icon={faCheckCircle} className="me-2" />
              <h5 className="mb-0 fw-semibold">Analysis Complete!</h5>
            </div>
            
            <Alert variant="success" className="mb-4">
              <strong>Great news!</strong> Your property analysis is ready. Here's what we found:
            </Alert>

            <Row className="mb-4">
              <Col md={6}>
                <div 
                  className="p-4 rounded-3 text-center"
                  style={{
                    background: 'linear-gradient(135deg, rgba(16, 185, 129, 0.1) 0%, rgba(5, 150, 105, 0.1) 100%)',
                    border: '1px solid rgba(16, 185, 129, 0.2)'
                  }}
                >
                  <h5 className="text-success mb-2">Estimated Value</h5>
                  <h3 className="fw-bold">${guidedAnalysisResults?.estimated_value?.toLocaleString() || 'Processing...'}</h3>
                  <small className="text-muted">
                    Confidence: {guidedAnalysisResults?.confidence || 0}%
                  </small>
                </div>
              </Col>
              <Col md={6}>
                <div 
                  className="p-4 rounded-3 text-center"
                  style={{
                    background: 'linear-gradient(135deg, rgba(59, 130, 246, 0.1) 0%, rgba(37, 99, 235, 0.1) 100%)',
                    border: '1px solid rgba(59, 130, 246, 0.2)'
                  }}
                >
                  <h5 className="text-info mb-2">Comparable Properties</h5>
                  <h3 className="fw-bold">{guidedAnalysisResults?.comparables_count || 0}</h3>
                  <small className="text-muted">
                    Similar properties analyzed
                  </small>
                </div>
              </Col>
            </Row>

            <div className="text-center">
              <Button 
                variant="primary" 
                size="lg" 
                className="me-3"
                onClick={() => setShowReportModal(true)}
                style={{
                  background: 'linear-gradient(135deg, #00d4ff 0%, #0ea5e9 100%)',
                  border: 'none',
                  padding: '12px 24px'
                }}
              >
                <FontAwesomeIcon icon={faEye} className="me-2" />
                View Comparable Properties ({guidedAnalysisResults?.comparables_count || 0})
              </Button>
              <Button variant="outline-secondary">
                <FontAwesomeIcon icon={faArrowRight} className="me-2" />
                Continue to Profile
              </Button>
            </div>
            
            {/* Enhanced Comparable Properties Preview */}
            {guidedAnalysisResults?.comparable_properties && guidedAnalysisResults.comparable_properties.length > 0 && (
              <div className="mt-4">
                <h6 className="mb-3">📊 Top Comparable Properties</h6>
                <Row className="g-3">
                  {guidedAnalysisResults.comparable_properties.slice(0, 3).map((comp: any, index: number) => (
                    <Col md={4} key={index}>
                      <div 
                        className="p-3 rounded-3 border"
                        style={{
                          background: 'rgba(255, 255, 255, 0.8)',
                          borderColor: 'rgba(0, 212, 255, 0.2)',
                          cursor: 'pointer',
                          transition: 'all 0.2s ease'
                        }}
                        onClick={() => setShowReportModal(true)}
                        onMouseEnter={(e) => {
                          e.currentTarget.style.background = 'rgba(0, 212, 255, 0.05)';
                          e.currentTarget.style.transform = 'translateY(-2px)';
                          e.currentTarget.style.boxShadow = '0 4px 12px rgba(0, 212, 255, 0.15)';
                        }}
                        onMouseLeave={(e) => {
                          e.currentTarget.style.background = 'rgba(255, 255, 255, 0.8)';
                          e.currentTarget.style.transform = 'translateY(0)';
                          e.currentTarget.style.boxShadow = 'none';
                        }}
                      >
                        {/* Property Photo */}
                        {comp.photos && comp.photos.length > 0 ? (
                          <img
                            src={comp.photos[0].medium || comp.photos[0].small || comp.photos[0].large}
                            alt={`${comp.address} photo`}
                            style={{
                              width: '100%',
                              height: '120px',
                              objectFit: 'cover',
                              borderRadius: '6px',
                              marginBottom: '8px'
                            }}
                          />
                        ) : (
                          <div
                            style={{
                              width: '100%',
                              height: '120px',
                              backgroundColor: '#f8f9fa',
                              border: '1px dashed #dee2e6',
                              borderRadius: '6px',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              marginBottom: '8px',
                              color: '#6c757d'
                            }}
                          >
                            <FontAwesomeIcon icon={faHome} size="2x" />
                          </div>
                        )}
                        
                        {/* Property Details */}
                        <div style={{ fontSize: '0.9em' }}>
                          <strong className="text-primary">{comp.address?.substring(0, 25)}...</strong>
                          <div className="d-flex justify-content-between align-items-center mt-1">
                            <span className="text-success fw-bold">
                              ${(comp.close_price || comp.list_price || 0).toLocaleString()}
                            </span>
                            <small className="text-muted">
                              {comp.bedrooms}br / {comp.bathrooms}ba
                            </small>
                          </div>
                          <div className="d-flex justify-content-between align-items-center mt-1">
                            <small className="text-muted">
                              {comp.living_area?.toLocaleString()} sq ft
                            </small>
                            <small className="text-muted">
                              ${Math.round(comp.price_per_sqft || 0)}/sq ft
                            </small>
                          </div>
                        </div>
                      </div>
                    </Col>
                  ))}
                </Row>
                <div className="text-center mt-3">
                  <small className="text-muted">
                    Click on any property or "View Comparable Properties" to see all {guidedAnalysisResults.comparables_count} properties
                  </small>
                </div>
              </div>
            )}
          </div>
        )}


      </div>
    );
  };

  const renderChatInterface = () => (
    <div className="chat-interface">
      {/* Compact Chat Header */}
      <div className="d-flex align-items-center justify-content-between mb-2 p-2 rounded-2" style={{ background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)" }}>
        <div className="d-flex align-items-center">
          <div className="bg-white bg-opacity-20 rounded-circle d-flex align-items-center justify-content-center me-2" 
               style={{ width: '32px', height: '32px' }}>
            <FontAwesomeIcon icon={faChartLine} className="text-white" />
          </div>
          <div className="text-white">
            <div className="fw-semibold" style={{ fontSize: "0.9rem" }}>Lead Analysis AI</div>
            <small className="text-white-75" style={{ fontSize: "0.7rem" }}>for {leadDetails.lead_first_name}</small>
          </div>
        </div>
        <Badge bg="light" text="dark" className="fw-semibold" style={{ fontSize: "0.7rem" }}>Online</Badge>
      </div>

      {/* Compact Messages Container */}
      <div
        ref={chatContainerRef}
        className="chat-messages-container mb-2"
        style={{ 
          height: "45vh", 
          overflowY: "auto",
          background: "linear-gradient(135deg, #f8f9ff 0%, #fff 100%)",
          borderRadius: "12px",
          padding: "0.75rem",
          border: "1px solid rgba(0,0,0,0.08)"
        }}
      >
        {messages.map((message) => (
          <div
            key={`${message.id}-${message.lastUpdated || message.timestamp?.getTime() || 0}`}
            className={classNames(
              "d-flex mb-2",
              message.sender === "user" ? "justify-content-end" : "justify-content-start"
            )}
          >
            <div className="d-flex align-items-start" style={{ maxWidth: "90%" }}>
              <div className="flex-grow-1">
                <div
                  className={classNames(
                    "p-2 position-relative",
                    message.sender === "user" 
                      ? "bg-primary text-white" 
                      : message.type === "insight"
                        ? "bg-white border border-success"
                        : "bg-white border"
                  )}
                  style={{
                    borderRadius: message.sender === "user" ? "12px 12px 4px 12px" : "12px 12px 12px 4px",
                    boxShadow: message.sender === "user" 
                      ? "0 2px 8px rgba(13, 110, 253, 0.15)" 
                      : message.type === "insight"
                        ? "0 2px 8px rgba(25, 135, 84, 0.1)"
                        : "0 2px 8px rgba(0, 0, 0, 0.08)",
                    fontSize: "0.85rem"
                  }}
                >
                  {message.type === "insight" && message.sender === "assistant" && (
                    <div className="d-flex align-items-center mb-1 pb-1 border-bottom border-success-subtle">
                      <div 
                        className="rounded-circle me-1 d-flex align-items-center justify-content-center"
                        style={{ width: "16px", height: "16px", background: "linear-gradient(135deg, #198754, #20c997)" }}
                      >
                        <FontAwesomeIcon icon={faLightbulb} className="text-white" style={{ fontSize: "0.6rem" }} />
                      </div>
                      <small className="text-success fw-bold" style={{ fontSize: "0.65rem", letterSpacing: "0.3px" }}>
                        AI INSIGHT
                      </small>
                    </div>
                  )}
                  
                  <div className="lh-sm">
                    {message.text.includes("**") ? (
                      <div dangerouslySetInnerHTML={{
                        __html: message.text
                          .replace(/\*\*(.*?)\*\*/g, '<strong style="font-weight: 600;">$1</strong>')
                          .replace(/\n/g, '<br>')
                      }} />
                    ) : (
                      message.text
                    )}
                  </div>
                  
                  {message.timestamp && (
                    <div className={classNames(
                      "d-flex align-items-center mt-1 pt-1",
                      message.sender === "user" ? "border-top border-white-25" : "border-top border-light"
                    )}>
                      <small 
                        className={classNames(
                          message.sender === "user" ? "text-white-75" : "text-muted"
                        )}
                        style={{ fontSize: "0.6rem" }}
                      >
                        {formatTime(message.timestamp)}
                      </small>
                    </div>
                  )}
                </div>
              </div>
              
              {message.sender === "user" && (
                <div 
                  className="rounded-circle d-flex align-items-center justify-content-center ms-2 flex-shrink-0"
                  style={{
                    width: "24px",
                    height: "24px",
                    background: "linear-gradient(135deg, #0d6efd 0%, #6610f2 100%)"
                  }}
                >
                  <FontAwesomeIcon icon={faUser} className="text-white" style={{ fontSize: "0.7rem" }} />
                </div>
              )}
            </div>
          </div>
        ))}
        
        {loading && (
          <div className="d-flex justify-content-start mb-2">
            <div className="d-flex align-items-start" style={{ maxWidth: "85%" }}>
              <div 
                className="bg-white p-2 border rounded-2" 
                style={{ boxShadow: "0 2px 8px rgba(0, 0, 0, 0.08)" }}
              >
                <div className="d-flex align-items-center">
                  <div className="spinner-border spinner-border-sm text-primary me-2" style={{ width: "1rem", height: "1rem" }} />
                  <span className="text-muted" style={{ fontSize: "0.8rem" }}>Analyzing...</span>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Compact Input Area */}
      <div className="position-relative">
        <Form.Control
          as="textarea"
          rows={1}
          placeholder="Ask about lead potential, next steps, strategy..."
          value={inputMessage}
          onChange={(e) => {
            setInputMessage(e.target.value);
            e.target.style.height = "auto";
            e.target.style.height = Math.min(e.target.scrollHeight, 80) + "px";
          }}
          onKeyPress={(e) => {
            if (e.key === "Enter" && !e.shiftKey && !loading && inputMessage.trim()) {
              e.preventDefault();
              handleSendMessage();
            }
          }}
          className="border-0 shadow-sm"
          style={{ 
            minHeight: "40px",
            borderRadius: "20px",
            paddingLeft: "16px",
            paddingRight: "60px",
            paddingTop: "10px",
            paddingBottom: "10px",
            fontSize: "0.85rem",
            backgroundColor: "#f8f9fa",
            boxShadow: "inset 0 1px 4px rgba(0, 0, 0, 0.06)",
            resize: "none"
          }}
          disabled={loading}
        />
        <Button
          onClick={handleSendMessage}
          disabled={loading || !inputMessage.trim()}
          className="rounded-circle position-absolute end-0 top-50 translate-middle-y me-1 shadow-sm d-inline-flex align-items-center justify-content-center p-0"
          style={{ 
            width: "36px", 
            height: "36px", 
            zIndex: 10,
            background: inputMessage.trim() && !loading
              ? "linear-gradient(135deg, #0d6efd 0%, #6610f2 100%)"
              : undefined,
            border: "none",
            boxShadow: "0 2px 8px rgba(13, 110, 253, 0.3)",
            lineHeight: 1
          }}
        >
          <div className="d-flex align-items-center justify-content-center" style={{ width: "100%", height: "100%" }}>
            {loading ? (
              <Spinner animation="border" size="sm" style={{ width: "0.8rem", height: "0.8rem" }} />
            ) : (
              <FontAwesomeIcon icon={faPaperPlane} style={{ fontSize: "0.8rem" }} />
            )}
          </div>
        </Button>
        
        {error && (
          <div className="text-danger mt-1 d-flex align-items-center" style={{ fontSize: "0.75rem" }}>
            <FontAwesomeIcon icon={faLightbulb} className="me-1" />
            {formatErrorForReact(error)}
          </div>
        )}
      </div>
    </div>
  );

  // Add buyer preferences render function
  const renderBuyerPreferences = () => {
    if (loadingBuyerProfile) {
      return (
        <Card className="mb-4">
          <Card.Body className="text-center py-4">
            <Spinner animation="border" variant="primary" />
            <div className="mt-2">Loading buyer preferences...</div>
          </Card.Body>
        </Card>
      );
    }

    // If no buyer profile exists, show "Add Preferences" interface
    if (!buyerProfile) {
      return (
        <Card className="mb-4" style={{
          background: 'linear-gradient(135deg, rgba(0, 212, 255, 0.1) 0%, rgba(14, 165, 233, 0.05) 100%)',
          border: '1px solid rgba(0, 212, 255, 0.2)'
        }}>
          <Card.Header className="d-flex justify-content-between align-items-center">
            <div>
                             <FontAwesomeIcon icon={faHome} className="text-primary me-2" />
               <strong>What {leadDetails?.lead_first_name} is Looking For</strong>
             </div>
             <Button 
               size="sm" 
               variant="primary"
               onClick={() => {
                 console.log('🔍 [DEBUG] Add Preferences clicked - current buyerProfile:', buyerProfile);
                 
                 if (buyerProfile && (buyerProfile as SimpleBuyerProfile).id) {
                   // Profile already exists - just enable editing mode
                   console.log('✅ [DEBUG] Profile exists with ID:', (buyerProfile as SimpleBuyerProfile).id, '- enabling edit mode');
                   setEditingBuyerProfile(true);
                 } else {
                   // No profile exists - initialize empty one
                   console.log('📝 [DEBUG] No profile exists - initializing new one');
                   setBuyerProfile({
                     lead_id: leadDetails?.id || '',
                     buying_timeframe: '3-6 months',
                     pre_approved: false,
                     first_time_buyer: false,
                     buyer_stage: 'researching',
                     property_preferences: {
                       property_type: 'single_family',
                       bedrooms: undefined,
                       bathrooms: undefined,
                       square_feet_min: undefined,
                       square_feet_max: undefined,
                       budget_min: undefined,
                       budget_max: undefined,
                       must_have_features: []
                     },
                     location_preferences: {
                       preferred_cities: [],
                       preferred_areas: [],
                       deal_breaker_areas: []
                     },
                     lifestyle_preferences: {
                       amenities: [],
                       deal_breakers: []
                     }
                   });
                   setEditingBuyerProfile(true);
                 }
               }}
             >
               <FontAwesomeIcon icon={faPlus} className="me-1" />
               Add Preferences
             </Button>
           </Card.Header>
           <Card.Body className="text-center py-5">
             <FontAwesomeIcon icon={faHome} className="text-primary mb-3" size="3x" />
             <h5 className="text-muted mb-2">Ready to build your complete buyer profile?</h5>
             <p className="text-muted mb-4">
               Create a comprehensive profile to unlock AI-powered home search and personalized recommendations.
             </p>
             
             <div className="d-flex justify-content-center gap-2 flex-wrap">
               <Button 
                 variant="outline-primary"
                 size="lg"
                 onClick={async () => {
                   // Initialize empty profile if none exists
                   if (!buyerProfile) {
                     setBuyerProfile({
                       lead_id: leadDetails?.id || '',
                       buying_timeframe: '3-6 months',
                       pre_approved: false,
                       first_time_buyer: false,
                       buyer_stage: 'researching',
                       property_preferences: {
                         property_type: '',
                         bedrooms: undefined,
                         bathrooms: undefined,
                         square_feet_min: undefined,
                         square_feet_max: undefined,
                         budget_min: undefined,
                         budget_max: undefined,
                         must_have_features: []
                       },
                       location_preferences: {
                         preferred_cities: [],
                         preferred_areas: [],
                         deal_breaker_areas: []
                       },
                       lifestyle_preferences: {
                         amenities: [],
                         deal_breakers: []
                       }
                     });
                   }
                   // Transition workflow to manual profile collection mode
                   await transitionToProfileCollectionWorkflow('manual');
                   
                   setProfileModalTab('manual');
                   setShowProfileModal(true);
                 }}
                 className="px-4"
               >
                 <FontAwesomeIcon icon={faEdit} className="me-2" />
                 Manual Entry
               </Button>
               <Button 
                 variant="primary"
                 size="lg"
                 onClick={async () => {
                   // Transition workflow to AI-assisted profile collection mode
                   await transitionToProfileCollectionWorkflow('ai_assisted');
                   
                   setProfileModalTab('ai');
                   setShowProfileModal(true);
                 }}
                 className="px-4"
               >
                 <FontAwesomeIcon icon={faRobot} className="me-2" />
                 AI Assistant
               </Button>
             </div>
             
             <div className="mt-3">
               <small className="text-muted">
                 <FontAwesomeIcon icon={faLightbulb} className="me-1" />
                 AI Assistant uses conversational workflow to build your profile naturally
               </small>
             </div>
          </Card.Body>
        </Card>
      );
    }

    // Profile exists - show clean summary with modal buttons
    return (
      <Card className="mb-4" style={{
        background: 'linear-gradient(135deg, rgba(0, 212, 255, 0.1) 0%, rgba(14, 165, 233, 0.05) 100%)',
        border: '1px solid rgba(0, 212, 255, 0.2)'
      }}>
        <Card.Header className="d-flex justify-content-between align-items-center">
          <div>
            <FontAwesomeIcon icon={faHome} className="text-primary me-2" />
            <strong>What {leadDetails?.lead_first_name} is Looking For</strong>
          </div>
          <div className="d-flex gap-2">
            <Button 
              size="sm" 
              variant="outline-primary"
              onClick={async () => {
                // Transition workflow to manual profile collection mode
                await transitionToProfileCollectionWorkflow('manual');
                
                setProfileModalTab('manual');
                setShowProfileModal(true);
              }}
            >
              <FontAwesomeIcon icon={faEdit} className="me-1" />
              Edit Profile
            </Button>
            <Button 
              size="sm" 
              variant="primary"
              onClick={async () => {
                // Transition workflow to AI-assisted profile collection mode
                await transitionToProfileCollectionWorkflow('ai_assisted');
                
                setProfileModalTab('ai');
                setShowProfileModal(true);
              }}
            >
              <FontAwesomeIcon icon={faRobot} className="me-1" />
              AI Assistant
            </Button>
          </div>
        </Card.Header>
        <Card.Body>
          {/* Clean Summary Grid */}
          <Row className="g-3">
            {/* Property Type */}
            <Col sm={6} md={3}>
              <div className="text-center p-3 rounded" style={{background: 'rgba(13, 110, 253, 0.1)'}}>
                <FontAwesomeIcon icon={faHome} className="text-primary mb-2" size="lg" />
                <div className="fw-semibold text-primary small">PROPERTY TYPE</div>
                <div className="text-dark">
                  {buyerProfile.property_preferences?.property_type?.replace('_', ' ').replace(/\b\w/g, l => l.toUpperCase()) || 'Not specified'}
                </div>
              </div>
                         </Col>
            
            {/* Budget */}
            <Col sm={6} md={3}>
              <div className="text-center p-3 rounded" style={{background: 'rgba(25, 135, 84, 0.1)'}}>
                <FontAwesomeIcon icon={faDollarSign} className="text-success mb-2" size="lg" />
                <div className="fw-semibold text-success small">BUDGET RANGE</div>
                <div className="text-dark">
                  {buyerProfile.property_preferences?.budget_min && buyerProfile.property_preferences?.budget_max 
                    ? `$${buyerProfile.property_preferences?.budget_min?.toLocaleString()} - $${buyerProfile.property_preferences?.budget_max?.toLocaleString()}`
                    : 'Not specified'
                  }
                </div>
              </div>
            </Col>
            
            {/* Bedrooms & Bathrooms */}
            <Col sm={6} md={3}>
              <div className="text-center p-3 rounded" style={{background: 'rgba(13, 202, 240, 0.1)'}}>
                <FontAwesomeIcon icon={faBed} className="text-info mb-2" size="lg" />
                <div className="fw-semibold text-info small">BEDROOMS</div>
                <div className="text-dark">{buyerProfile.property_preferences?.bedrooms || 'Any'}</div>
                <div className="small text-muted mt-1">
                  <FontAwesomeIcon icon={faBath} className="me-1" />
                  {buyerProfile.property_preferences?.bathrooms || 'Any'} bath
                </div>
              </div>
            </Col>
            
            {/* Square Footage */}
            <Col sm={6} md={3}>
              <div className="text-center p-3 rounded" style={{background: 'rgba(255, 193, 7, 0.1)'}}>
                <FontAwesomeIcon icon={faRulerCombined} className="text-warning mb-2" size="lg" />
                <div className="fw-semibold text-warning small">SQUARE FEET</div>
                <div className="text-dark">
                  {buyerProfile.property_preferences?.square_feet_min && buyerProfile.property_preferences?.square_feet_max 
                    ? `${buyerProfile.property_preferences?.square_feet_min?.toLocaleString()} - ${buyerProfile.property_preferences?.square_feet_max?.toLocaleString()} sq ft`
                    : 'Not specified'
                  }
                </div>
              </div>
            </Col>
          </Row>
          
          {/* Timeline & Features Row */}
          <Row className="g-3 mt-2">
            <Col md={6}>
              <div className="d-flex align-items-center p-3 rounded" style={{background: 'rgba(108, 117, 125, 0.1)'}}>
                <FontAwesomeIcon icon={faClock} className="text-secondary me-3" size="lg" />
                <div>
                  <div className="fw-semibold text-secondary small">TIMELINE</div>
                  <div className="text-dark">{buyerProfile.buying_timeframe || 'Not specified'}</div>
                </div>
              </div>
            </Col>
            <Col md={6}>
              <div className="d-flex align-items-start p-3 rounded" style={{background: 'rgba(108, 117, 125, 0.1)'}}>
                <FontAwesomeIcon icon={faStar} className="text-secondary me-3 mt-1" size="lg" />
                <div className="flex-grow-1">
                  <div className="fw-semibold text-secondary small">MUST-HAVE FEATURES</div>
                  <div className="text-dark">
                                         {(buyerProfile?.property_preferences?.must_have_features || []).length > 0 ? (
                       <div className="d-flex flex-wrap gap-1 mt-1">
                         {(buyerProfile?.property_preferences?.must_have_features || []).slice(0, 3).map((feature, index) => (
                           <Badge key={index} bg="secondary" className="small">{feature}</Badge>
                         ))}
                         {(buyerProfile?.property_preferences?.must_have_features || []).length > 3 && (
                           <Badge bg="light" text="dark" className="small">
                             +{(buyerProfile?.property_preferences?.must_have_features || []).length - 3} more
                           </Badge>
                         )}
                       </div>
                    ) : (
                      <span className="text-muted">No must-have features specified</span>
                    )}
                  </div>
                </div>
              </div>
            </Col>
          </Row>
          
          {/* Action Buttons */}
          <div className="text-center mt-4 pt-3 border-top">
            <div className="d-flex justify-content-center gap-3 flex-wrap">
              <Button 
                variant="outline-primary"
                onClick={async () => {
                  // Transition workflow to manual profile collection mode
                  await transitionToProfileCollectionWorkflow('manual');
                  
                  setProfileModalTab('manual');
                  setShowProfileModal(true);
                }}
                className="px-4"
              >
                <FontAwesomeIcon icon={faEdit} className="me-2" />
                Complete Profile
              </Button>
              <Button 
                variant="primary"
                onClick={async () => {
                  // Transition workflow to AI-assisted profile collection mode
                  await transitionToProfileCollectionWorkflow('ai_assisted');
                  
                  setProfileModalTab('ai');
                  setShowProfileModal(true);
                }}
                className="px-4"
              >
                <FontAwesomeIcon icon={faRobot} className="me-2" />
                AI Enhancement
              </Button>
              <Button 
                variant="success"
                onClick={() => {
                  // TODO: Navigate to buyer portal or launch property search
                  console.log('🏠 Launching property search with profile:', buyerProfile);
                }}
                className="px-4"
              >
                <FontAwesomeIcon icon={faSearch} className="me-2" />
                Find Properties
              </Button>
            </div>
            
            <div className="mt-3">
              <small className="text-muted">
                <FontAwesomeIcon icon={faLightbulb} className="me-1" />
                Use AI Assistant to enhance your profile with conversational workflow, or complete manually
              </small>
            </div>
          </div>
        </Card.Body>
      </Card>
    );
  };

  // Add new state for modal
  const [showProfileModal, setShowProfileModal] = useState(false);
  const [profileModalTab, setProfileModalTab] = useState<'manual' | 'ai'>('manual');
  const [aiWorkflowSession, setAiWorkflowSession] = useState<any>(null);
  const [workflowLoading, setWorkflowLoading] = useState(false);

  // Add new function to start AI workflow session
  const startAIWorkflowSession = async () => {
    if (!leadDetails?.id) return;
    
    setWorkflowLoading(true);
    try {
      console.log('🚀 [AI WORKFLOW] Starting buyer profile workflow for lead:', leadDetails.id);
      
      const response = await axiosInstance.post(`/buyer-profiles/lead/${leadDetails.id}/start-workflow-session`, {
        workflowType: 'BUYER_PROFILE_COLLECTION',
        stageId: 'bf7c8e9d-stage1-basic-qualification'
      });
      
      const sessionData = response.data.data;
      console.log('✅ [AI WORKFLOW] Session started:', sessionData);
      
      setAiWorkflowSession(sessionData);
      
      // Initialize with welcome message
      const welcomeMessage: Message = {
        id: Date.now(),
        text: `👋 **Hi! I'm your Buyer Profile Collection Assistant.**\n\nI'll help you systematically gather ${leadDetails.lead_first_name}'s buyer preferences and requirements. This creates structured data that will power your property search and personalized recommendations.\n\n**Let's start with the basics:** What has ${leadDetails.lead_first_name} told you about their timeline? Are they looking to purchase within the next few months, or is this more of a longer-term search?`,
        sender: "assistant",
        timestamp: new Date(),
        type: "insight"
      };
      
      setMessages([welcomeMessage]);
      
      toast.success('�� AI Profile Builder Started!');
    } catch (error: any) {
      console.error('❌ [AI WORKFLOW] Failed to start session:', error);
      toast.error('Failed to start AI workflow');
    } finally {
      setWorkflowLoading(false);
    }
  };

  if (!isAuthorized) {
    return (
      <Card className={classNames(className)}>
        <Card.Body className="text-center py-5">
          <FontAwesomeIcon icon={faLightbulb} className="text-muted mb-3" size="3x" />
          <h5 className="text-muted mb-3">AI Assistant Unavailable</h5>
          <p className="text-muted mb-3">
            Please upgrade your subscription to access the Lead AI Assistant.
          </p>
          <Button variant="primary" href="/settings/subscription-settings">
            Upgrade Now
          </Button>
        </Card.Body>
      </Card>
    );
  }



  return (
    <>
      <style>
        {`
          @keyframes pulse {
            0% { opacity: 1; transform: scale(1); }
            50% { opacity: 0.7; transform: scale(1.05); }
            100% { opacity: 1; transform: scale(1); }
          }
          
          .pulse-animation {
            animation: pulse 2s infinite;
          }
          
          .real-time-indicator {
            position: relative;
          }
          
          .real-time-indicator::before {
            content: '';
            position: absolute;
            top: -2px;
            right: -2px;
            width: 8px;
            height: 8px;
            background: #00d4ff;
            border-radius: 50%;
            animation: pulse 1.5s infinite;
            box-shadow: 0 0 6px rgba(0, 212, 255, 0.6);
          }
          
          .ai-intelligence-center {
            background: linear-gradient(135deg, rgba(255, 255, 255, 0.1) 0%, rgba(255, 255, 255, 0.05) 100%);
            border-radius: 20px;
            padding: 0;
            border: 1px solid rgba(0, 212, 255, 0.1);
            backdrop-filter: blur(20px);
            box-shadow: 0 8px 32px rgba(0, 0, 0, 0.1);
          }
          
          /* Responsive styles for small screens */
          @media (max-width: 500px) {
            .ai-assistant-header {
              flex-direction: column;
              align-items: flex-start;
            }
            
            .ai-assistant-title {
              white-space: nowrap;
              overflow: hidden;
              text-overflow: ellipsis;
              width: 100%;
              max-width: 100%;
              font-size: 1rem !important;
            }
            
            .ai-assistant-badge {
              margin-top: 8px;
              align-self: flex-start;
              margin-left: 0;
              margin-right: auto;
              padding: 6px 12px !important;
            }
          }
        `}
      </style>
      <Card 
        className={classNames(className, "h-100 border-0")}
        style={{
          background: 'linear-gradient(135deg, rgba(255, 255, 255, 0.1) 0%, rgba(255, 255, 255, 0.05) 100%)',
          backdropFilter: 'blur(15px)',
          border: '1px solid rgba(0, 212, 255, 0.2) !important'
        }}
      >
      <Card.Header 
        className="border-bottom-0 py-4"
        style={{ 
          background: 'linear-gradient(135deg, rgba(102, 126, 234, 0.15) 0%, rgba(118, 75, 162, 0.15) 100%)',
          backdropFilter: 'blur(10px)',
          borderRadius: '12px 12px 0 0'
        }}
      >
        <div className="d-flex align-items-center justify-content-between">
            <div className="d-flex align-items-center">
            <div 
              className="rounded-circle d-flex align-items-center justify-content-center me-3"
              style={{ 
                width: "48px", 
                height: "48px",
                background: 'linear-gradient(135deg, #00d4ff 0%, #0ea5e9 100%)',
                boxShadow: '0 0 20px rgba(0, 212, 255, 0.4)'
              }}
            >
              <FontAwesomeIcon 
                icon={faBolt} 
                className="text-white" 
                size="lg"
                style={{ filter: 'drop-shadow(0 0 4px rgba(255, 255, 255, 0.5))' }}
              />
            </div>
            <div>
              <h4 
                className="mb-1 fw-bold ai-assistant-title"
                style={{ 
                  color: '#1f2937',
                  textShadow: '0 0 10px rgba(0, 212, 255, 0.3)',
                  fontSize: '1.1rem'
                }}
              >
                Lead AI Assistant
              </h4>
              <p 
                className="text-muted mb-0"
                style={{ fontSize: '14px' }}
              >
                Neural network-powered insights for {leadDetails?.lead_first_name} {leadDetails?.lead_last_name}
              </p>
          </div>
          </div>
          <div 
            className="d-flex align-items-center ai-assistant-badge"
            style={{
              background: 'linear-gradient(135deg, rgba(16, 185, 129, 0.1) 0%, rgba(5, 150, 105, 0.1) 100%)',
              padding: '8px 16px',
              borderRadius: '20px',
              border: '1px solid rgba(16, 185, 129, 0.3)'
            }}
          >
            <div 
              className="rounded-circle me-2"
              style={{
                width: '8px',
                height: '8px',
                background: '#10b981',
                animation: 'pulse 2s infinite'
              }}
            ></div>
            <span 
              className="fw-semibold text-success"
              style={{ 
                fontSize: '12px',
                textTransform: 'uppercase',
                letterSpacing: '0.5px'
              }}
            >
              AI ACTIVE
            </span>
          </div>
        </div>
      </Card.Header>
      
      <Card.Body className="p-0" style={{ background: 'transparent' }}>
        <Tabs
          activeKey={activeTab}
          onSelect={(k) => setActiveTab(k || "insights")}
          className="ai-assistant-tabs"
          fill
        >
          <Tab eventKey="insights" title={
            <div className="d-flex align-items-center" style={{ fontSize: "14px", fontWeight: 500 }}>
              <div 
                className="me-2 rounded-circle d-flex align-items-center justify-content-center"
                style={{
                  width: '24px',
                  height: '24px',
                  background: activeTab === 'insights' 
                    ? 'linear-gradient(135deg, #00d4ff 0%, #0ea5e9 100%)' 
                    : 'rgba(156, 163, 175, 0.3)',
                  transition: 'all 0.3s ease'
                }}
              >
                <FontAwesomeIcon 
                  icon={faLightbulb} 
                  className="text-white" 
                  size="sm"
                  style={{ fontSize: '12px' }}
                />
              </div>
              <span style={{ 
                color: activeTab === 'insights' ? '#00d4ff' : '#6b7280',
                textShadow: activeTab === 'insights' ? '0 0 5px rgba(0, 212, 255, 0.3)' : 'none'
              }}>
                AI INSIGHTS
              </span>
            </div>
          }>
            <div className="p-4" style={{ background: 'rgba(255, 255, 255, 0.02)' }}>
              {renderInsightsOverview()}
            </div>
          </Tab>
          <Tab eventKey="chat" title={
            <div className="d-flex align-items-center" style={{ fontSize: "14px", fontWeight: 500 }}>
              <div 
                className="me-2 rounded-circle d-flex align-items-center justify-content-center"
                style={{
                  width: '24px',
                  height: '24px',
                  background: activeTab === 'chat' 
                    ? 'linear-gradient(135deg, #00d4ff 0%, #0ea5e9 100%)' 
                    : 'rgba(156, 163, 175, 0.3)',
                  transition: 'all 0.3s ease'
                }}
              >
                <FontAwesomeIcon 
                  icon={faMessage} 
                  className="text-white" 
                  size="sm"
                  style={{ fontSize: '12px' }}
                />
              </div>
              <span style={{ 
                color: activeTab === 'chat' ? '#00d4ff' : '#6b7280',
                textShadow: activeTab === 'chat' ? '0 0 5px rgba(0, 212, 255, 0.3)' : 'none'
              }}>
                AI CHAT
              </span>
            </div>
          }>
            <div className="p-4" style={{ background: 'rgba(255, 255, 255, 0.02)' }}>
              {showProfileBuilder ? renderProfileBuilderIntro() : renderChatInterface()}
            </div>
          </Tab>
          <Tab eventKey="guided" title={
            <div className="d-flex align-items-center" style={{ fontSize: "14px", fontWeight: 500 }}>
              <div 
                className="me-2 rounded-circle d-flex align-items-center justify-content-center"
                style={{
                  width: '24px',
                  height: '24px',
                  background: activeTab === 'guided' 
                    ? 'linear-gradient(135deg, #00d4ff 0%, #0ea5e9 100%)' 
                    : 'rgba(156, 163, 175, 0.3)',
                  transition: 'all 0.3s ease'
                }}
              >
                <FontAwesomeIcon 
                  icon={faTasks} 
                  className="text-white" 
                  size="sm"
                  style={{ fontSize: '12px' }}
                />
              </div>
              <span style={{ 
                color: activeTab === 'guided' ? '#00d4ff' : '#6b7280',
                textShadow: activeTab === 'guided' ? '0 0 5px rgba(0, 212, 255, 0.3)' : 'none'
              }}>
                GUIDED
              </span>
            </div>
          }>
            <div className="p-4" style={{ background: 'rgba(255, 255, 255, 0.02)' }}>
              {renderGuidedWorkflow()}
            </div>
          </Tab>
          <Tab eventKey="history" title={
            <div className="d-flex align-items-center" style={{ fontSize: "14px", fontWeight: 500 }}>
              <div 
                className="me-2 rounded-circle d-flex align-items-center justify-content-center"
                style={{
                  width: '24px',
                  height: '24px',
                  background: activeTab === 'history' 
                    ? 'linear-gradient(135deg, #00d4ff 0%, #0ea5e9 100%)' 
                    : 'rgba(156, 163, 175, 0.3)',
                  transition: 'all 0.3s ease'
                }}
              >
                <FontAwesomeIcon 
                  icon={faHistory} 
                  className="text-white" 
                  size="sm"
                  style={{ fontSize: '12px' }}
                />
              </div>
              <span style={{ 
                color: activeTab === 'history' ? '#00d4ff' : '#6b7280',
                textShadow: activeTab === 'history' ? '0 0 5px rgba(0, 212, 255, 0.3)' : 'none'
              }}>
                HISTORY
              </span>
            </div>
          }>
            <div className="p-4" style={{ background: 'rgba(255, 255, 255, 0.02)' }}>
              {renderConversationHistory()}
            </div>
          </Tab>
        </Tabs>
        
        <style dangerouslySetInnerHTML={{
          __html: `
            .ai-assistant-tabs .nav-link {
              border: none !important;
              background: transparent !important;
              border-radius: 0 !important;
              border-bottom: 3px solid transparent !important;
              transition: all 0.3s ease !important;
              padding: 16px 20px !important;
            }
            
            .ai-assistant-tabs .nav-link:hover {
              border-bottom-color: rgba(0, 212, 255, 0.3) !important;
              background: rgba(0, 212, 255, 0.05) !important;
            }
            
            .ai-assistant-tabs .nav-link.active {
              border-bottom-color: #00d4ff !important;
              background: linear-gradient(135deg, rgba(0, 212, 255, 0.1) 0%, rgba(14, 165, 233, 0.1) 100%) !important;
              box-shadow: inset 0 -3px 0 #00d4ff !important;
            }
            
            .ai-assistant-tabs .nav-tabs {
              border-bottom: 1px solid rgba(0, 212, 255, 0.1) !important;
            }
          `
        }} />
      </Card.Body>

      {/* New Topic Modal */}
      <Modal 
        show={showNewTopicModal} 
        onHide={() => {
          setShowNewTopicModal(false);
          setNewTopicTitle("");
        }}
        centered
        size="sm"
      >
        <Modal.Header closeButton className="border-0 pb-2">
          <Modal.Title style={{ fontSize: "1.1rem" }}>
            <FontAwesomeIcon icon={faMessage} className="text-success me-2" />
            New Conversation Topic
          </Modal.Title>
        </Modal.Header>
        <Modal.Body className="pt-0">
          <p className="text-muted mb-3" style={{ fontSize: "0.9rem" }}>
            Create a focused conversation for a specific topic or discussion area.
          </p>
          <Form.Group>
            <Form.Label className="fw-semibold" style={{ fontSize: "0.9rem" }}>Topic Name</Form.Label>
            <Form.Control
              type="text"
              placeholder="e.g., Property Search, Financing Questions, Market Analysis"
              value={newTopicTitle}
              onChange={(e) => setNewTopicTitle(e.target.value)}
              style={{ fontSize: "0.9rem" }}
              className="border-2"
            />
            <Form.Text className="text-muted" style={{ fontSize: "0.8rem" }}>
              Choose a descriptive name for easy identification
            </Form.Text>
          </Form.Group>
        </Modal.Body>
        <Modal.Footer className="border-0 pt-1">
          <Button 
            variant="outline-secondary" 
            size="sm"
            onClick={() => {
              setShowNewTopicModal(false);
              setNewTopicTitle("");
            }}
            style={{ fontSize: "0.8rem" }}
          >
            Cancel
          </Button>
          <Button 
            variant="success" 
            size="sm"
            onClick={async () => {
              if (newTopicTitle.trim()) {
                await createNewConversation(newTopicTitle.trim());
                setShowNewTopicModal(false);
                setNewTopicTitle("");
                setActiveTab("chat");
              }
            }}
            disabled={!newTopicTitle.trim()}
            style={{ fontSize: "0.8rem" }}
          >
            <FontAwesomeIcon icon={faMessage} className="me-1" />
            Create Topic
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Buyer Profile Builder Modal */}
      <Modal 
        show={showProfileModal} 
        onHide={() => setShowProfileModal(false)}
        size="xl"
        centered
        backdrop="static"
        keyboard={false}
      >
        <Modal.Header 
          closeButton 
          className="border-0" 
          style={{ 
            background: 'linear-gradient(135deg, #00d4ff 0%, #0ea5e9 100%)',
            color: 'white'
          }}
        >
          <Modal.Title className="d-flex align-items-center">
            <FontAwesomeIcon icon={faHome} className="me-2" />
            Complete Buyer Profile - {leadDetails?.lead_first_name} {leadDetails?.lead_last_name}
          </Modal.Title>
        </Modal.Header>
        
        <Modal.Body className="p-0">
          {/* Tab Navigation */}
          <div className="border-bottom">
            <div className="d-flex">
              <button
                className={`btn btn-link border-0 px-4 py-3 fw-semibold ${
                  profileModalTab === 'manual' 
                    ? 'text-primary border-bottom border-primary border-3' 
                    : 'text-muted'
                }`}
                onClick={() => setProfileModalTab('manual')}
                style={{ 
                  borderRadius: 0,
                  background: profileModalTab === 'manual' ? 'rgba(0, 212, 255, 0.1)' : 'transparent'
                }}
              >
                <FontAwesomeIcon icon={faEdit} className="me-2" />
                Manual Entry
              </button>
              <button
                className={`btn btn-link border-0 px-4 py-3 fw-semibold ${
                  profileModalTab === 'ai' 
                    ? 'text-success border-bottom border-success border-3' 
                    : 'text-muted'
                }`}
                onClick={() => {
                  setProfileModalTab('ai');
                  if (!aiWorkflowSession) {
                    startAIWorkflowSession();
                  }
                }}
                style={{ 
                  borderRadius: 0,
                  background: profileModalTab === 'ai' ? 'rgba(40, 167, 69, 0.1)' : 'transparent'
                }}
              >
                <FontAwesomeIcon icon={faRobot} className="me-2" />
                AI Assistant
                {workflowLoading && <Spinner size="sm" className="ms-2" />}
              </button>
            </div>
          </div>

          {/* Tab Content */}
          <div className="p-4">
            {profileModalTab === 'manual' && (
              <div>
                <div className="mb-4">
                  <h5 className="text-primary mb-2">
                    <FontAwesomeIcon icon={faHome} className="me-2" />
                    Property Preferences
                  </h5>
                  <p className="text-muted mb-4">
                    Define what {leadDetails?.lead_first_name} is looking for in their ideal home.
                  </p>
                  
                  <Row>
                    <Col md={6}>
                      <Form.Group className="mb-3">
                        <Form.Label className="fw-semibold">
                          <FontAwesomeIcon icon={faHome} className="me-1 text-primary" />
                          Property Type
                        </Form.Label>
                        <Form.Select
                          value={buyerProfile?.property_preferences?.property_type || ''}
                          onChange={(e) => updatePropertyPreference('property_type', e.target.value)}
                        >
                          <option value="">Select Type</option>
                          <option value="single_family">Single Family Home</option>
                          <option value="condo">Condominium</option>
                          <option value="townhome">Townhome</option>
                          <option value="apartment">Apartment</option>
                          <option value="duplex">Duplex</option>
                          <option value="any">Any Type</option>
                        </Form.Select>
                      </Form.Group>
                    </Col>
                    <Col md={3}>
                      <Form.Group className="mb-3">
                        <Form.Label className="fw-semibold">
                          <FontAwesomeIcon icon={faBed} className="me-1 text-primary" />
                          Bedrooms
                        </Form.Label>
                        <Form.Select
                          value={buyerProfile?.property_preferences?.bedrooms || ''}
                          onChange={(e) => updatePropertyPreference('bedrooms', parseInt(e.target.value))}
                        >
                          <option value="">Any</option>
                          <option value="1">1 Bedroom</option>
                          <option value="2">2 Bedrooms</option>
                          <option value="3">3 Bedrooms</option>
                          <option value="4">4 Bedrooms</option>
                          <option value="5">5+ Bedrooms</option>
                        </Form.Select>
                      </Form.Group>
                    </Col>
                    <Col md={3}>
                      <Form.Group className="mb-3">
                        <Form.Label className="fw-semibold">
                          <FontAwesomeIcon icon={faBath} className="me-1 text-primary" />
                          Bathrooms
                        </Form.Label>
                        <Form.Select
                          value={buyerProfile?.property_preferences?.bathrooms || ''}
                          onChange={(e) => updatePropertyPreference('bathrooms', parseFloat(e.target.value))}
                        >
                          <option value="">Any</option>
                          <option value="1">1 Bathroom</option>
                          <option value="1.5">1.5 Bathrooms</option>
                          <option value="2">2 Bathrooms</option>
                          <option value="2.5">2.5 Bathrooms</option>
                          <option value="3">3+ Bathrooms</option>
                        </Form.Select>
                      </Form.Group>
                    </Col>
                  </Row>

                  <Row>
                    <Col md={6}>
                      <Form.Group className="mb-3">
                        <Form.Label className="fw-semibold">
                          <FontAwesomeIcon icon={faDollarSign} className="me-1 text-success" />
                          Budget Range
                        </Form.Label>
                        <Row>
                          <Col>
                            <Form.Control
                              type="number"
                              placeholder="Min Budget"
                              value={buyerProfile?.property_preferences?.budget_min || ''}
                              onChange={(e) => updatePropertyPreference('budget_min', parseInt(e.target.value))}
                            />
                          </Col>
                          <Col>
                            <Form.Control
                              type="number"
                              placeholder="Max Budget"
                              value={buyerProfile?.property_preferences?.budget_max || ''}
                              onChange={(e) => updatePropertyPreference('budget_max', parseInt(e.target.value))}
                            />
                          </Col>
                        </Row>
                      </Form.Group>
                    </Col>
                    <Col md={6}>
                      <Form.Group className="mb-3">
                        <Form.Label className="fw-semibold">
                          <FontAwesomeIcon icon={faRuler} className="me-1 text-info" />
                          Square Footage
                        </Form.Label>
                        <Row>
                          <Col>
                            <Form.Control
                              type="number"
                              placeholder="Min Sq Ft"
                              value={buyerProfile?.property_preferences?.square_feet_min || ''}
                              onChange={(e) => updatePropertyPreference('square_feet_min', parseInt(e.target.value))}
                            />
                          </Col>
                          <Col>
                            <Form.Control
                              type="number"
                              placeholder="Max Sq Ft"
                              value={buyerProfile?.property_preferences?.square_feet_max || ''}
                              onChange={(e) => updatePropertyPreference('square_feet_max', parseInt(e.target.value))}
                            />
                          </Col>
                        </Row>
                      </Form.Group>
                    </Col>
                  </Row>

                  <Row>
                    <Col md={6}>
                      <Form.Group className="mb-3">
                        <Form.Label className="fw-semibold">
                          <FontAwesomeIcon icon={faClock} className="me-1 text-warning" />
                          Buying Timeline
                        </Form.Label>
                        <Form.Select
                          value={buyerProfile?.buying_timeframe || ''}
                          onChange={(e) => updateBuyerProfile('buying_timeframe', e.target.value)}
                        >
                          <option value="">Select Timeline</option>
                          <option value="0-3 months">0-3 months</option>
                          <option value="3-6 months">3-6 months</option>
                          <option value="6-12 months">6-12 months</option>
                          <option value="12+ months">12+ months</option>
                          <option value="Not specified">Not specified</option>
                        </Form.Select>
                      </Form.Group>
                    </Col>
                    <Col md={6}>
                      <Form.Group className="mb-3">
                        <Form.Label className="fw-semibold">
                          <FontAwesomeIcon icon={faCheckCircle} className="me-1 text-success" />
                          Pre-approved?
                        </Form.Label>
                        <div className="d-flex gap-3 mt-2">
                          <Form.Check
                            type="radio"
                            id="pre-approved-yes"
                            name="pre-approved"
                            label="Yes"
                            checked={buyerProfile?.pre_approved === true}
                            onChange={() => updateBuyerProfile('pre_approved', true)}
                          />
                          <Form.Check
                            type="radio"
                            id="pre-approved-no"
                            name="pre-approved"
                            label="No"
                            checked={buyerProfile?.pre_approved === false}
                            onChange={() => updateBuyerProfile('pre_approved', false)}
                          />
                        </div>
                      </Form.Group>
                    </Col>
                  </Row>
                </div>

                <hr className="my-4" />

                <div className="mb-4">
                  <h5 className="text-success mb-2">
                    <FontAwesomeIcon icon={faMapMarkerAlt} className="me-2" />
                    Location Preferences
                  </h5>
                  <p className="text-muted mb-4">
                    Specify preferred areas and locations.
                  </p>

                  <Form.Group className="mb-3">
                    <Form.Label className="fw-semibold">Preferred Cities/Areas</Form.Label>
                    <Form.Control
                      type="text"
                      placeholder="Enter preferred locations (e.g., Phoenix, Scottsdale, Tempe)"
                      value={buyerProfile?.location_preferences?.preferred_cities?.join(', ') || ''}
                      onChange={(e) => updateLocationPreference('preferred_cities', e.target.value.split(',').map(s => s.trim()).filter(s => s))}
                    />
                    <Form.Text className="text-muted">
                      Separate multiple locations with commas
                    </Form.Text>
                  </Form.Group>
                </div>

                <hr className="my-4" />

                <div className="mb-4">
                  <h5 className="text-info mb-2">
                    <FontAwesomeIcon icon={faHeart} className="me-2" />
                    Additional Information
                  </h5>
                  
                  <Form.Group className="mb-3">
                    <Form.Label className="fw-semibold">Motivation for Buying</Form.Label>
                    <Form.Control
                      as="textarea"
                      rows={3}
                      placeholder="What's driving their home purchase? (e.g., growing family, relocation, investment)"
                      value={buyerProfile?.motivation || ''}
                      onChange={(e) => updateBuyerProfile('motivation', e.target.value)}
                    />
                  </Form.Group>

                  <Row>
                    <Col md={6}>
                      <Form.Group className="mb-3">
                        <Form.Label className="fw-semibold">First-time buyer?</Form.Label>
                        <div className="d-flex gap-3 mt-2">
                          <Form.Check
                            type="radio"
                            id="first-time-yes"
                            name="first-time"
                            label="Yes"
                            checked={buyerProfile?.first_time_buyer === true}
                            onChange={() => updateBuyerProfile('first_time_buyer', true)}
                          />
                          <Form.Check
                            type="radio"
                            id="first-time-no"
                            name="first-time"
                            label="No"
                            checked={buyerProfile?.first_time_buyer === false}
                            onChange={() => updateBuyerProfile('first_time_buyer', false)}
                          />
                        </div>
                      </Form.Group>
                    </Col>
                    <Col md={6}>
                      <Form.Group className="mb-3">
                        <Form.Label className="fw-semibold">Buyer Stage</Form.Label>
                        <Form.Select
                          value={buyerProfile?.buyer_stage || ''}
                          onChange={(e) => updateBuyerProfile('buyer_stage', e.target.value)}
                        >
                          <option value="researching">Researching</option>
                          <option value="viewing">Viewing Properties</option>
                          <option value="offering">Making Offers</option>
                          <option value="under_contract">Under Contract</option>
                          <option value="closed">Closed</option>
                          <option value="paused">Paused</option>
                          <option value="lost">Lost</option>
                        </Form.Select>
                      </Form.Group>
                    </Col>
                  </Row>
                </div>
              </div>
            )}

            {profileModalTab === 'ai' && (
              <div style={{ height: '600px' }}>
                {workflowLoading ? (
                  <div className="text-center py-5">
                    <Spinner animation="border" variant="primary" />
                    <div className="mt-2">Starting AI Workflow...</div>
                  </div>
                ) : (
                  <div className="d-flex flex-column h-100">
                    <div className="mb-3 p-3 bg-light rounded">
                      <h6 className="text-success mb-2">
                        <FontAwesomeIcon icon={faRobot} className="me-2" />
                        AI Profile Builder
                      </h6>
                      <p className="mb-0 small text-muted">
                        I'll help you build and enhance your client's buyer profile through intelligent conversation. This creates structured data perfect for AI-powered property search and personalized recommendations.
                      </p>
                    </div>
                    
                    <div className="flex-grow-1 overflow-auto border rounded p-3" style={{ maxHeight: '450px' }}>
                      {messages.map((message) => (
                        <div key={message.id} className={`mb-3 ${message.sender === 'user' ? 'text-end' : ''}`}>
                          <div
                            className={`d-inline-block p-3 rounded ${
                              message.sender === 'user'
                                ? 'bg-primary text-white'
                                : 'bg-light border'
                            }`}
                            style={{ maxWidth: '85%' }}
                          >
                            {message.text.split('\n').map((line, i) => (
                              <div key={i} dangerouslySetInnerHTML={{ __html: line.replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>') }} />
                            ))}
                          </div>
                          <div className="small text-muted mt-1">
                            {message.timestamp?.toLocaleTimeString()}
                          </div>
                        </div>
                      ))}
                    </div>

                    <div className="mt-3">
                      <div className="input-group">
                        <Form.Control
                          type="text"
                          placeholder="Type your response..."
                          value={inputMessage}
                          onChange={(e) => setInputMessage(e.target.value)}
                          onKeyPress={(e) => {
                            if (e.key === 'Enter' && !e.shiftKey) {
                              e.preventDefault();
                              handleSendMessage();
                            }
                          }}
                        />
                        <Button 
                          variant="primary"
                          onClick={handleSendMessage}
                          disabled={!inputMessage.trim()}
                        >
                          <FontAwesomeIcon icon={faPaperPlane} />
                        </Button>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>
        </Modal.Body>

        <Modal.Footer className="border-0 bg-light">
          <div className="d-flex justify-content-between w-100">
            <div>
              {profileModalTab === 'manual' && (
                <small className="text-muted">
                  <FontAwesomeIcon icon={faLightbulb} className="me-1" />
                  This data will be used for AI-powered home search and MLS integration
                </small>
              )}
              {profileModalTab === 'ai' && (
                <small className="text-muted">
                  <FontAwesomeIcon icon={faRobot} className="me-1" />
                  AI will extract structured data and update the buyer profile automatically
                </small>
              )}
            </div>
            <div>
              <Button 
                variant="outline-secondary" 
                className="me-2"
                onClick={() => setShowProfileModal(false)}
              >
                <FontAwesomeIcon icon={faTimes} className="me-1" />
                Cancel
              </Button>
              {profileModalTab === 'manual' && (
                <Button 
                  variant="success"
                  onClick={async () => {
                    await saveBuyerProfile();
                    setShowProfileModal(false);
                  }}
                  disabled={savingBuyerProfile}
                >
                  <FontAwesomeIcon icon={faSave} className="me-1" />
                  {savingBuyerProfile ? 'Saving...' : 'Save Profile'}
                </Button>
              )}
              {profileModalTab === 'ai' && aiWorkflowSession && (
                <Button 
                  variant="success"
                  onClick={() => {
                    // AI workflow saves automatically
                    setShowProfileModal(false);
                    toast.success('AI Profile data will be processed automatically');
                  }}
                >
                  <FontAwesomeIcon icon={faCheck} className="me-1" />
                  Complete
                </Button>
              )}
            </div>
          </div>
        </Modal.Footer>
      </Modal>
    </Card>
    </>
  );
};

export default LeadAiAssistant;

